import { importIdentityCandidates, needsImportIdentityReview, type IdentityCandidate } from '../../lib/documents/importIdentityReview.js';
import { resolveSupplierUnitMemory } from '@trackeat/shared/import';
import { applyRefreshedMatches, mergeRefreshedLineMatches, type RefreshedLineMatch } from '../../lib/documents/refreshedDocumentMatches.js';
import { cleanFoodName } from '../../lib/documents/cleanFoodName.js';
import { importStorageDefault, remembersImportSkip } from '../../lib/documents/importReviewPolicy.js';
import { confirmDialog } from '../../lib/ui/dialogs.js';
import { applySupplierLotPreference } from '../../lib/documents/supplierLotPreference.js';
import { toUserMessage, UserFacingError } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonModal,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { IonIcon } from '@ionic/react';
import {
  calendarOutline,
  camera,
  checkmarkCircle,
  close,
  informationOutline,
  restaurantOutline,
  scaleOutline,
} from 'ionicons/icons';
import { palette } from '../../components/design.js';
import { getCaptureProvider } from '../../lib/adapters/camera/index.js';
// T-170 1d: cleanup draft dopo commit (review-from-job).
import { finishDraft } from '../../lib/documents/draftStore.js';
import { usePersistentWorkDraft } from '../../lib/drafts/usePersistentWorkDraft.js';
import { usePageActivity } from '../../lib/hooks/usePageActivity.js';
import { fetchDocumentJob, markJobCommitted } from '../../lib/documents/documentJobsClient.js';
import {
  fetchDocumentImportReviewPayload,
  type DocumentImportReviewRequestPayload,
} from '../../lib/documents/documentImportReviews.js';
import { reviewOriginalRecoveryRequirement } from '../../lib/documents/reviewOriginalRecovery.js';
import { reviewTenantMismatch } from '../../lib/documents/reviewTenantGuard.js';
// T-176: vassoio multi-documento (sostituisce il selettore a file singolo).
import { DocumentUploadTray } from '../../components/DocumentUploadTray.js';
import { ReviewOriginalRecoveryPanel } from '../../components/ReviewOriginalRecoveryPanel.js';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { canCreateFood } from '../../lib/auth/capabilities.js';
import { getActiveOperatorAssertion } from '../../lib/auth/activeOperatorSession.js';
import { useAccountAccessState, useMasters, useReceivingTempPerItemEnabled } from '../../lib/data/queries.js';
import { setReviewFullscreen } from '../../lib/ui/reviewFullscreen.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { confirmedEventLocalIdentity } from '../../lib/sync/eventsLocalKey.js';
import { getSupabase, SUPABASE_URL } from '../../lib/supabase.js';
import { uploadLotPhoto } from '../../lib/labelQueue/labelExtract.js';
import { openLocalDb, flushLocalDb, getDeviceId } from '../../lib/db/local.js';
import { suggestAllergens, ALLERGEN_LABELS_IT, type Allergen, CONFORMITY_DISCLAIMER_ITEMS, FOOD_CATEGORIES } from '@trackeat/shared';
import { aggregateExtractedLines } from '../../lib/documents/aggregateExtractedLines.js';
import {
  documentMatchDetailLabel,
  isTrustedDocumentMatch,
  requireExplicitDocumentMatch,
  type DocumentMatchSource,
} from '../../lib/documents/documentMatchTrust.js';
import { sourceLineForPlan } from '../../lib/documents/planLineSource.js';
import { isImportLineCommitReady } from '../../lib/documents/importCommitReadiness.js';
import { planIndexForAcceptedLine } from '../../lib/documents/importErrorContext.js';
import type { LineValidation } from '@trackeat/shared';
import { assessDocumentAutoImport } from '@trackeat/shared';
import { AiRobotIcon } from '../../components/AiRobotIcon.js';
import { lineOpenReasons } from './reviewSemaphore.js';
import {
  applyPackFactorDraft,
  changeSourceUomDraft,
  changeStockUomDraft,
  correctionDocumentTotalDraft,
  deriveStockUnitCost,
  editSourceQuantityDraft,
  editStockQuantityDraft,
  reconstructImportLines,
  patchImportLineDraft,
  sourceEconomicsFromLineTotal,
  sourceEconomicsFromUnitPrice,
  type LoadedLine,
  type FoodLite,
} from './reconstructImportPlan.js';
import { prepareDocumentFile } from '../../lib/documents/documentSource.js';
import { formatMoney, moneyInputValue } from '../../lib/money.js';
import { UomSelect } from '../../components/UomSelect.js';
import {
  ImportReviewQueue,
  type ImportQueueItem,
} from '../../components/import/ImportReviewQueue.js';
import { BASE_UOM_OPTIONS, convertQty, isBaseUom, isPackUnit, normalizeUom, suggestPackFactor } from '../../lib/uom.js';
import {
  findTrustedPackFactor,
  type SupplierAliasPackFactor,
} from '../../lib/documents/supplierAliasPackFactor.js';

interface BusinessRow {
  id: string;
  tenant_id: string;
  name: string;
  archived_at?: string | null;
}

type DocKind = 'fattura' | 'ddt';

interface ParsedSupplier {
  piva?: string | null;
  codice_fiscale?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  category?: string | null;
  country?: string | null;
}

interface ParsedLine {
  description?: string;
  // T-063: testo originale del DDT, prima della normalizzazione del modello.
  // Lo mostriamo come hint sotto la description nella UI di review per dare
  // contesto all'utente: "il modello ha riscritto X come Y, ti torna?"
  raw_description?: string | null;
  category?: string | null;
  storage_type?: string | null;
  qty?: number;
  uom?: string;
  pack_size?: number | null;
  pack_uom?: string | null;
  total_weight_kg?: number | null;
  unit_price?: number;
  line_total?: number;
  iva_rate?: number;
  batch_lot?: string;
  expiration_date?: string;
  allergens?: string[];
  is_gift?: boolean;
  gift_indicator?: string | null;
  // Server segnala righe simili (stessa famiglia prodotto). Niente fusione
  // automatica: l'utente decide. similar_to[] = indici delle altre righe simili.
  similar_to?: number[];
  uom_canonical?: string;
  uom_recognized?: boolean;
}

interface FoodMatch {
  id: string;
  name: string;
  default_uom: string;
  storage_type?: string;
  category?: string | null;
  current_batch_lot?: string | null;
}

// T-132: shape minimale di una ricetta per contare in quante ricette compare
// ogni alimento (food picker manuale del review DDT). Solo i campi necessari.
interface RecipeForCount {
  id: string;
  archived_at?: string | null;
  ingredients?: Array<{ food_id?: string | null } | null> | null;
  // T-441: le varianti dieta (ricette figlie) replicano gli ingredienti della base
  // → escluse dal conteggio precedenza food per non contarle due volte.
  variant_of_recipe_id?: string | null;
}

// T-131: formatta una data ISO (YYYY-MM-DD o timestamptz) come "GG/MM" per le
// note di memoria/lotto nella review. Stringa vuota se non parsabile.
function fmtDayMonth(s?: string | null): string {
  if (!s) return '';
  const p = s.slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}` : s;
}

// Estrae l'unità di misura "naturale" dalla descrizione: se il DDT scrive
// "Petto di Pollo (kg 18)", l'utente pensa al prodotto come "kg" anche se
// l'AI ha estratto qty=12 uom="pz". Questa funzione restituisce 'kg' o 'l'
// quando il pattern è chiaramente di peso/volume tra parentesi.
function extractUomHint(raw: string): 'kg' | 'l' | null {
  if (!raw) return null;
  const m = raw.toLowerCase().match(/\(\s*(kg|g|gr|grammi|l|lt|litri|ml|cl)\s+\d+(?:[.,]\d+)?\s*\)/);
  if (!m) return null;
  const u = m[1];
  if (['kg', 'g', 'gr', 'grammi'].includes(u)) return 'kg';
  if (['l', 'lt', 'litri', 'ml', 'cl'].includes(u)) return 'l';
  return null;
}

// Strippa il prefisso "DDT N°:" / "Fattura N°:" / "Doc:" dal numero documento
// quando l'AI lo ha incluso. "DDT N°: 2026/503" → "2026/503".
function cleanDocNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw
    .replace(/^\s*(DDT|FT|FATTURA|DOC|DOCUMENTO)\s*(N°|N\.|NR|NUM|N)?\s*[:.]?\s*/i, '')
    .trim();
  return cleaned || null;
}

// Detect del kind dal numero del documento o dall'header. L'AI a volte
// scrive il kind nel numero (es. "DDT N°: 2026/503") → possiamo dedurlo.
function detectKindFromNumber(raw: string | null | undefined): 'fattura' | 'ddt' | null {
  if (!raw) return null;
  if (/^\s*(DDT|D\.D\.T)/i.test(raw)) return 'ddt';
  if (/^\s*(FT|FATTURA)/i.test(raw)) return 'fattura';
  return null;
}

// ─── Fuzzy match articolo lato client ───
// Confronta nome AI vs anagrafica `foods` con token-overlap (Jaccard).
// Soglia 0.5: almeno metà dei token significativi devono coincidere.
const STOPWORDS = new Set([
  'di','da','del','della','dello','dei','degli','delle','il','la','lo','i','gli','le',
  'in','con','per','a','ai','al','allo','alla','agli','alle','e','o','un','una','uno','d',
]);
// T-334: sinonimi/abbreviazioni fornitore → forma canonica, così varianti di
// marca/formato dello stesso prodotto matchano al PRIMO carico (prima che esista
// l'alias per-codice). Mappa volutamente piccola e conservativa.
// NB: volutamente NIENTE hz/hnz→heinz: aggiungere "heinz" alla descrizione
// faceva matchare la "Kk Burger Sauce" (campionatura distinta) con la "Classic
// Burger" (falso positivo verificato in simulazione). I casi legittimi (dip,
// bottiglie) matchano comunque per token condivisi.
const SYNONYMS: Record<string, string> = {
  bbq: 'barbecue',
  maionese: 'mayo', mayonnaise: 'mayo',
};
function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .map((t) => SYNONYMS[t] ?? t)
    .filter((t) => t.length >= 3 && !STOPWORDS.has(t));
}
function jaccardSimilarity(a: string[], b: string[]): number {
  if (a.length === 0 || b.length === 0) return 0;
  const sa = new Set(a);
  const sb = new Set(b);
  let inter = 0;
  for (const t of sa) if (sb.has(t)) inter++;
  const union = sa.size + sb.size - inter;
  return union === 0 ? 0 : inter / union;
}
function formatItDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

// Fallback lotto leggibile sull'etichetta quando il documento non riporta il
// batch reale del fornitore. Es: "Vedi DDT n.123 del 04/05/2026".
function buildLotFallback(kind: 'fattura' | 'ddt', number: string | null, date: string | null): string {
  const num = number && number.trim() ? `n.${number.trim()}` : '';
  const dateIt = date ? `del ${formatItDate(date)}` : '';
  const tail = [num, dateIt].filter(Boolean).join(' ');
  if (!tail) return '';
  return `Vedi ${kind === 'ddt' ? 'DDT' : 'fattura'} ${tail}`;
}

function findFuzzyFoodMatch(
  rawName: string,
  foods: FoodMatch[],
): { food: FoodMatch; score: number } | null {
  const cleaned = cleanFoodName(rawName);
  const aTokens = tokenize(cleaned);
  if (aTokens.length === 0) return null;
  const aSet = new Set(aTokens);
  let best: { food: FoodMatch; score: number } | null = null;
  for (const f of foods) {
    const bTokens = tokenize(f.name);
    if (bTokens.length === 0) continue;
    let score = jaccardSimilarity(aTokens, bTokens);
    // T-334: token-containment — se TUTTI i token significativi del food (≥2,
    // per evitare che un food mono-parola tipo "Acqua"/"Sale" matchi ovunque)
    // sono presenti nella descrizione, è un match forte anche con jaccard < 0.5
    // (es. food "Acqua Naturale" ⊆ desc "Acqua Naturale San Benedetto 50cl").
    const bSet = new Set(bTokens);
    if (bSet.size >= 2 && score < 0.6) {
      let contained = true;
      for (const t of bSet) if (!aSet.has(t)) { contained = false; break; }
      if (contained) score = Math.max(score, 0.6);
    }
    if (score >= 0.5 && (!best || score > best.score)) {
      best = { food: f, score };
    }
  }
  return best;
}

interface SupplierMatch {
  id: string;
  name: string;
  piva: string | null;
  category: string | null;
}

// T-074: il backend popola `existing_document` se rileva che esiste già
// un documento con la stessa chiave logica (supplier+number+date) sul
// business attivo. La UI mostra un banner rosso "documento già caricato"
// e blocca il commit.
interface ExistingDocument {
  source: 'public' | 'fiscal';
  id: string;
  number: string;
  date: string;
  type?: string;
}

export interface DryRunResponse {
  mode: 'dry_run';
  provider_used: string;
  request_id?: string;
  confidence: number | null;
  document: {
    number: string | null;
    date: string | null;
    total_amount: number | null;
    // T-197: true se il totale è stato STIMATO dalle righe (non leggibile sul
    // documento) → il client avvisa l'utente di verificarlo.
    total_amount_estimated?: boolean;
    type: string | null;
    currency: string;
  };
  supplier: {
    parsed: ParsedSupplier | null;
    existing: SupplierMatch | null;
    // T-466: se sta per creare un NUOVO fornitore, i simili già in anagrafica
    // (double-check "forse esiste già?").
    suggestions?: Array<{ id: string; name: string | null; piva: string | null; sharedTokens: number }>;
  };
  lines: Array<DryRunLine>;
  existing_document?: ExistingDocument | null;
  // T-205: avviso NON bloccante. Il server pensa che il documento prosegua su
  // una pagina non inclusa, ma NON blocca più l'import (l'utente carica le
  // pagine come documenti separati). Il client mostra un banner informativo in
  // review e lascia comunque procedere al commit.
  possible_missing_page?: boolean;
}

// T-131: ogni riga porta la provenienza del match (memoria fornitore vs ilike)
// e l'esito della regola data-based sul lotto attivo.
interface DryRunLine {
  index: number;
  parsed: ParsedLine;
  food_match: FoodMatch | null;
  // Secondo controllo indipendente: candidato simile trovato nell'anagrafica
  // anche quando codice/alias/nome esatto non hanno risolto la riga.
  identity_candidates?: IdentityCandidate[];
  similar_foods?: Array<NonNullable<DryRunLine['similar_food']>>;
  similar_food?: {
    default_uom?: string;
    storage_type?: string;
    category?: string | null;
    id: string;
    name: string;
    score: number;
    levenshtein_distance: number;
    hamming_distance: number | null;
    token_similarity: number;
  } | null;
  // 'alias_manual' = l'utente l'ha abbinato a mano in passato (massima fiducia);
  // 'alias_ai' = match AI confermato; 'alias_first_setup' = da First Setup menu;
  // 'code' = codice articolo fornitore; 'ilike' = match per nome;
  // null = nessun match.
  match_source?: DocumentMatchSource;
  alias_meta?: { source: string; last_used_at: string | null; occurrences: number } | null;
  // T-634: scelta operativa persistita per codice/descrizione fornitore.
  import_preference?: 'skip' | null;
  // Lotto attivo dell'alimento matchato + data del suo documento (per la regola data).
  current_lot?: { batch_lot: string; document_date: string | null } | null;
  lot_date_comparison?: 'newer' | 'older_or_equal' | 'unknown';
  // true se la fattura è più vecchia/uguale del lotto attivo → di default NON
  // sovrascrivere il lotto attivo (registra solo il carico).
  default_keep_current_lot?: boolean;
  // T-375: classificazione deterministica a semaforo della riga (modulo puro
  // condiviso @trackeat/shared/import). Se assente (dry_run vecchio), la riga è
  // trattata come "verde" (nessun gate) dal semaforo client.
  validation?: LineValidation;
}

interface CommitResponse {
  alias_memory_persisted?: boolean;
  mode: 'commit';
  document_id: string;
  doc_kind: DocKind;
  supplier_id: string | null;
  lines_accepted: number;
  created_foods: Record<string, string>;
  status?: 'awaiting_review';
  review_request_id?: string;
  missing_foods?: string[];
  // Server ritorna i record appena creati: il client li applica direttamente
  // alla cache locale senza dipendere da sync-pull. Garanzia di visibilità
  // istantanea della giacenza in magazzino post-commit.
  writeback?: {
    foods: Array<Record<string, unknown> & { id: string; tenant_id?: string | null }>;
    events: Array<Record<string, unknown> & { id: string; tenant_id: string; kind: string; payload: Record<string, unknown> }>;
    supplier: (Record<string, unknown> & { id: string; tenant_id?: string | null }) | null;
  };
}

export interface PlanLineDraft {
  parsed_index: number;
  // T-063: testo originale grezzo del DDT (prima della normalizzazione del
  // modello). Mostrato come hint nella UI ma non modificabile dall'utente.
  raw_description: string | null;
  // T-198: codice articolo del fornitore (dal dry_run). Inviato al commit per
  // dedup intra-documento + memorizzazione alias per-codice.
  supplier_code: string | null;
  // Anagrafica alimento: scegliere esistente o crearne nuovo.
  food_id: string | null;
  match_confirmed?: boolean;
  new_food_confirmed?: boolean;
  identity_error?: boolean;
  // Suggerimento UI per nuovo alimento (compilato sempre, attivo solo se food_id null).
  food_create: {
    name: string;
    default_uom: string;
    storage_type: string;
    category: string;
    allergens: string[];
  };
  qty: string;
  uom: string;
  batch_lot: string;
  expiration_date: string;
  is_frozen: boolean;
  notify_expiration: boolean;
  storage_unit_id: string;
  supplier_sku: string;
  skip: boolean;
  /** Omissione di questa sola revisione, senza memoria fornitore. */
  skip_once?: boolean;
  replace_active_lot: boolean;
  // T-131: registra il carico (stock_in) ma NON sovrascrive il lotto attivo
  // dell'alimento. Default acceso dalla regola data-based (fattura più vecchia
  // del lotto attivo). Mutuamente esclusivo con replace_active_lot.
  keep_current_lot: boolean;
  // T-284 (Food Cost): prezzo/totale riga dal dry_run, propagati al commit per
  // registrare il costo del carico nell'evento stock_in.
  //
  // T-481: i valori `source_*` descrivono la riga del documento e non vengono
  // riscritti quando qty/uom sono convertiti nell'unità di magazzino. Gli alias
  // legacy unit_price/line_total restano sincronizzati per i client/server
  // precedenti, ma contengono anch'essi sempre i valori sorgente.
  source_qty: number;
  source_uom: string;
  source_unit_price: number | null;
  source_line_total: number | null;
  currency: string;
  unit_price: number | null;
  line_total: number | null;
  // T-294B (fattore di conversione collo→base):
  // `extracted_uom` = unità estratta dall'AI (può essere un collo: conf/cassa/ct…),
  // distinta da `uom` che può essere stata sovrascritta con l'UdM del food.
  extracted_uom: string | null;
  // serve il fattore? (unità estratta = collo e qty = n. colli, non convertita)
  pack_needs_factor: boolean;
  // fattore applicato/da salvare: unità base per 1 collo (es. 1 cassa = 6 kg → 6).
  pack_factor: number | null;
  pack_base_uom: string | null;
  // true se la conversione è già stata applicata (da memoria o a mano).
  pack_factor_applied: boolean;
  // Provenienza/fattore della trasformazione sorgente → magazzino. Separati
  // dai campi pack perché anche total_weight_kg può produrre una conversione.
  conversion_factor: number | null;
  conversion_source: string | null;
  // Necessaria solo per conversioni non metriche inserite a mano. Finché è
  // false il commit non può interpretare la quantità in una nuova UdM.
  conversion_confirmed: boolean;
  // T-294B (fix #2/#9): descrizione AI-normalizzata = STESSA chiave con cui
  // l'alias viene salvato (raw_description_norm). Usata per ri-trovare il
  // fattore memorizzato (distinta da raw_description, che è il testo grezzo).
  ai_description: string | null;
  // T-375: semaforo import. `validation` = classificazione a semaforo dal
  // dry_run (gate della riga); `gates_ack`/`lot_ack` = conferme esplicite
  // dell'operatore che chiudono i gate residui (propagate al commit).
  validation: LineValidation | null;
  gates_ack: boolean;
  lot_ack: boolean;
  lot_ack_from_supplier?: boolean;
  // T-407: storage path della foto etichetta scattata per questa riga (salvata
  // su lot-photos), propagata al commit → foods.current_lot_label_path.
  label_photo_path?: string | null;
}

export interface PlanDraft {
  supplier_create_ack?: boolean;
  // Esistente o crea
  supplier_lot_preference?: { supplier_id: string | null; no_lot: boolean };
  supplier_id: string | null;
  supplier_create: {
    name: string;
    piva: string;
    codice_fiscale: string;
    category: string;
    email: string;
    phone: string;
    address: string;
    country: string;
  };
  document: { number: string; date: string; total_amount: string; total_amount_estimated?: boolean; type: string; currency: string };
  lines: PlanLineDraft[];
}

function storedReviewText(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function storedReviewNumber(value: unknown, fallback: number | null = null): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : fallback;
}

/** Ricostruisce la bozza finale dello staff senza rilanciare l'OCR. */
function planFromStoredReview(
  payload: DocumentImportReviewRequestPayload,
  foods: Map<string, FoodLite>,
): PlanDraft {
  const storedPlan = payload.plan;
  const supplierCreate = storedPlan.supplier_create ?? {};
  const document = storedPlan.document;
  const loadedLines: LoadedLine[] = storedPlan.lines.map((line, index) => {
    const food = line.food_id ? foods.get(line.food_id) ?? null : null;
    const foodCreate = line.food_create ?? {};
    const fallbackName = storedReviewText(foodCreate.name, food?.name ?? line.raw_description ?? '');
    const stockQty = storedReviewNumber(line.qty, 0) ?? 0;
    const stockUom = storedReviewText(line.uom, food?.default_uom ?? 'pz');
    return {
      seq: line.parsed_index ?? index,
      food_id: line.food_id ?? null,
      description: fallbackName,
      qty: stockQty,
      uom: stockUom,
      batch_lot: line.batch_lot ?? null,
      expiration_date: line.expiration_date ?? null,
      source_qty: line.source_qty ?? stockQty,
      source_uom: line.source_uom ?? stockUom,
      source_unit_price: line.source_unit_price ?? null,
      source_line_total: line.source_line_total ?? null,
      unit_price: line.source_unit_price ?? null,
      line_total: line.source_line_total ?? null,
      currency: line.currency ?? document.currency ?? 'EUR',
      stock_qty: stockQty,
      stock_uom: stockUom,
      conversion_factor: line.conversion_factor ?? null,
      conversion_source: line.conversion_source ?? null,
      is_frozen: line.is_frozen,
      notify_expiration: line.notify_expiration,
      storage_unit_id: line.storage_unit_id,
      supplier_sku: line.supplier_sku,
      keep_current_lot: line.keep_current_lot,
    };
  });
  const reconstructed = reconstructImportLines(loadedLines, foods);
  return {
    supplier_id: storedPlan.supplier_id ?? null,
    supplier_create_ack: storedPlan.supplier_create_ack === true,
    supplier_create: {
      name: storedReviewText(supplierCreate.name),
      piva: storedReviewText(supplierCreate.piva),
      codice_fiscale: storedReviewText(supplierCreate.codice_fiscale),
      category: storedReviewText(supplierCreate.category, 'altro'),
      email: storedReviewText(supplierCreate.email),
      phone: storedReviewText(supplierCreate.phone),
      address: storedReviewText(supplierCreate.address),
      country: storedReviewText(supplierCreate.country, 'IT'),
    },
    document: {
      number: document.number,
      date: document.date,
      total_amount: document.total_amount == null ? '' : String(document.total_amount),
      type: storedReviewText(document.type, payload.doc_kind),
      currency: storedReviewText(document.currency, 'EUR').toUpperCase(),
    },
    lines: reconstructed.map((line, index) => {
      const stored = storedPlan.lines[index];
      const foodCreate = stored.food_create ?? {};
      return {
        ...line,
        parsed_index: stored.parsed_index ?? line.parsed_index,
        raw_description: stored.raw_description ?? line.raw_description,
        supplier_code: stored.supplier_code ?? null,
        match_confirmed: stored.match_confirmed === true,
        new_food_confirmed: stored.new_food_confirmed === true,
        identity_error: stored.identity_error === true,
        food_create: {
          ...line.food_create,
          name: storedReviewText(foodCreate.name, line.food_create.name),
          default_uom: storedReviewText(foodCreate.default_uom, line.uom),
          storage_type: storedReviewText(foodCreate.storage_type, line.food_create.storage_type),
          category: storedReviewText(foodCreate.category, line.food_create.category),
        },
        is_frozen: stored.is_frozen ?? line.is_frozen,
        notify_expiration: stored.notify_expiration ?? line.notify_expiration,
        storage_unit_id: stored.storage_unit_id ?? line.storage_unit_id,
        supplier_sku: stored.supplier_sku ?? line.supplier_sku,
        keep_current_lot: stored.keep_current_lot ?? line.keep_current_lot,
        replace_active_lot: stored.replace_active_lot ?? line.replace_active_lot,
        conversion_factor: stored.conversion_factor ?? line.conversion_factor,
        conversion_source: stored.conversion_source ?? line.conversion_source,
        conversion_confirmed: stored.conversion_confirmed ?? line.conversion_confirmed,
        ai_description: typeof stored.ai_description === 'string' ? stored.ai_description : line.ai_description,
        pack_factor: typeof stored.pack_factor === 'number' ? stored.pack_factor : line.pack_factor,
        extracted_uom: typeof stored.extracted_uom === 'string' ? stored.extracted_uom : line.extracted_uom,
        pack_factor_applied: stored.pack_factor_applied ?? line.pack_factor_applied,
        pack_base_uom: stored.pack_base_uom ?? line.pack_base_uom,
        gates_ack: stored.gates_ack ?? line.gates_ack,
        lot_ack: stored.lot_ack ?? line.lot_ack,
        label_photo_path: stored.label_photo_path ?? line.label_photo_path,
      };
    }),
  };
}

function dryRunFromStoredReview(
  reviewId: string,
  payload: DocumentImportReviewRequestPayload,
  plan: PlanDraft,
): DryRunResponse {
  return {
    mode: 'dry_run',
    provider_used: 'staff_review',
    request_id: `review:${reviewId}`,
    confidence: null,
    document: {
      number: plan.document.number || null,
      date: plan.document.date || null,
      total_amount: plan.document.total_amount ? Number(plan.document.total_amount) : null,
      type: plan.document.type,
      currency: plan.document.currency,
    },
    supplier: { parsed: plan.supplier_create, existing: null },
    lines: plan.lines.map((line) => ({
      index: line.parsed_index,
      parsed: {
        description: line.raw_description ?? line.food_create.name,
        qty: line.source_qty,
        uom: line.source_uom,
        unit_price: line.source_unit_price ?? undefined,
        line_total: line.source_line_total ?? undefined,
        batch_lot: line.batch_lot || undefined,
        expiration_date: line.expiration_date || undefined,
      },
      food_match: null,
      match_source: null,
      validation: line.validation ?? undefined,
    })),
  };
}

// T-375: etichette leggibili dei gate a semaforo (Reg. UE — testi it-first).
const GATE_REASON_LABELS: Record<string, string> = {
  nuovo_alimento: 'Alimento non riconosciuto',
  lotto_sospetto: 'Lotto da verificare',
  unita_incoerente: 'Unità di misura da verificare',
  bassa_confidenza: 'Dati poco affidabili',
};

const SUPPLIER_CATEGORIES = [
  'ortofrutta', 'carne', 'pesce', 'latticini', 'pane', 'bevande',
  'pulizia', 'alimentare', 'servizi', 'altro',
];

const STORAGE_TYPES = [
  { value: 'fresco', label: 'Fresco (0–4 °C)' },
  { value: 'surgelato', label: 'Surgelato (-25/-18 °C)' },
  { value: 'secco', label: 'Secco (10–25 °C)' },
  { value: 'bevanda', label: 'Bevanda (4–16 °C)' },
];

function InfoBadge(props: {
  open: boolean;
  label: string;
  onToggle: () => void;
  inline?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`te-info-badge${props.inline ? ' te-info-badge--inline' : ''}${props.open ? ' is-open' : ''}`}
      aria-label={props.label}
      aria-expanded={props.open}
      onClick={props.onToggle}
    >
      <span className="te-info-badge__circle" aria-hidden="true">
        <IonIcon icon={informationOutline} />
      </span>
    </button>
  );
}

function ImportReviewSection(props: {
  tone: 'food' | 'quantity' | 'lot';
  icon: string;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section
      className={`te-import-section te-import-section--${props.tone}`}
      aria-label={props.title}
    >
      <div className="te-import-section__heading">
        <span className="te-import-section__icon" aria-hidden="true">
          <IonIcon icon={props.icon} />
        </span>
        <h3>{props.title}</h3>
      </div>
      <div className="te-import-section__body">{props.children}</div>
    </section>
  );
}

function extractionWarnings(line: DryRunLine | undefined): string[] {
  if (!line) return [];
  const warnings: string[] = [];
  if (line.parsed.similar_to?.length) {
    warnings.push(
      `Possibile duplicato delle righe ${line.parsed.similar_to
        .map((index) => index + 1)
        .join(', ')}`,
    );
  }
  if (line.parsed.uom_recognized === false) {
    warnings.push(
      `Unità “${line.parsed.uom ?? '?'}” non riconosciuta: verifica il valore convertito`,
    );
  }
  if (line.similar_food) {
    warnings.push(
      `Possibile alimento già presente: “${line.similar_food.name}” (${Math.round(line.similar_food.score * 100)}% di similarità)`,
    );
  }
  return warnings;
}

class ExistingFoodUomMismatchError extends UserFacingError {
  constructor(
    readonly parsedLine: number,
    readonly foodId: string | null,
    readonly expectedUom: string,
    readonly receivedUom: string,
  ) {
    super(
      `La riga usa ${receivedUom}, ma l’alimento associato è gestito in `
      + `${expectedUom}. Scegli l’alimento corretto oppure usa “Nuovo” se si `
      + `tratta davvero di una fornitura con unità diversa.`,
    );
    this.name = 'ExistingFoodUomMismatchError';
  }
}

class ImportLineError extends UserFacingError {
  constructor(message: string, readonly planLineIndex: number | null) {
    super(message);
    this.name = 'ImportLineError';
  }
}

interface ImportFailureBannerState {
  message: string;
  lineIndex: number | null;
}

function ImportFailureBanner(props: {
  failure: ImportFailureBannerState;
  reportText: string;
  onClose: () => void;
  safeArea?: boolean;
}): JSX.Element {
  const whatsappUrl = `https://wa.me/393486382343?text=${encodeURIComponent(props.reportText)}`;
  return (
    <aside
      className={`te-import-error-banner${props.safeArea ? ' te-import-error-banner--safe-area' : ''}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="te-import-error-banner__copy">
        <strong>Importazione non completata</strong>
        <span>{props.failure.message}</span>
      </div>
      <div className="te-import-error-banner__actions">
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
          Invia l’errore al consulente
        </a>
        <button type="button" onClick={props.onClose} aria-label="Chiudi avviso errore">
          <IonIcon icon={close} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}

// T-324 E: lista categorie = unica fonte condivisa (@trackeat/shared FOOD_CATEGORIES),
// stessa di FoodEditorModal. Niente più copia locale che driftava.

export function CapturePhotoPage({ quickLabel = false }: { quickLabel?: boolean } = {}): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { profile } = useAuth();
  const { isActive, capture } = usePageActivity('/documents/capture', profile?.active_tenant_id ?? null);
  const commitInFlight = useRef(false);
  const [loadAfterCommit, setLoadAfterCommit] = useState(0);
  const extractionRequest = useRef(0);
  const accountAccess = useAccountAccessState();
  const canCreateFoods = canCreateFood(profile);
  const queryClient = useQueryClient();
  const { data: cachedBusinesses = [] } = useMasters<BusinessRow>('businesses');
  const { data: cachedTenants = [] } = useMasters<{ id: string; name: string }>('tenants');
  const { data: cachedFoods = [] } = useMasters<FoodMatch & { tenant_id?: string; archived_at?: string | null }>('foods');
  // T-478: anagrafica fornitori per il picker "scegli esistente" in revisione.
  const { data: cachedSuppliers = [] } = useMasters<{ id: string; name: string; piva: string | null; category: string | null; archived_at?: string | null }>('suppliers');
  // T-132: ricette per calcolare la precedenza degli alimenti usati in cucina
  // nel food picker manuale. Replica recipesPerFood di WarehousePage.
  const { data: cachedRecipes = [] } = useMasters<RecipeForCount>('recipes');
  const tenantFoods = useMemo(
    () => cachedFoods.filter((f) => !f.archived_at),
    [cachedFoods],
  );
  // T-570: accesso dal riepilogo Food Cost. Il parametro porta soltanto il
  // contesto al caricamento; non deve preassociare una riga letta dall'AI.
  const uploadShortcutFood = useMemo(() => {
    const id = new URLSearchParams(location.search).get('shortcutFoodId');
    return id ? tenantFoods.find((food) => food.id === id) ?? null : null;
  }, [location.search, tenantFoods]);
  // T-132: numero di ricette (non archiviate) in cui ogni alimento è
  // ingrediente. Stesso identico calcolo di WarehousePage.recipesPerFood:
  // walk degli ingredients[].food_id, una ricetta conta 1 volta per food.
  const recipesPerFood = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of cachedRecipes) {
      if (r.archived_at) continue;
      // T-441: non contare le varianti dieta (ricette figlie) — stessa base.
      if (r.variant_of_recipe_id != null) continue;
      const seen = new Set<string>();
      for (const ing of r.ingredients ?? []) {
        if (ing?.food_id && !seen.has(ing.food_id)) {
          seen.add(ing.food_id);
          m.set(ing.food_id, (m.get(ing.food_id) ?? 0) + 1);
        }
      }
    }
    return m;
  }, [cachedRecipes]);
  const localTenantBusinesses = useMemo(
    () => cachedBusinesses.filter((b) => b.tenant_id === profile?.active_tenant_id && !b.archived_at),
    [cachedBusinesses, profile?.active_tenant_id],
  );

  const [remoteBusinesses, setRemoteBusinesses] = useState<BusinessRow[]>([]);
  useEffect(() => {
    if (!profile?.active_tenant_id) return;
    if (localTenantBusinesses.length > 0) return;
    let cancelled = false;
    void (async () => {
      const { data } = await getSupabase()
        .from('businesses')
        .select('id,tenant_id,name,archived_at')
        .eq('tenant_id', profile.active_tenant_id)
        .is('archived_at', null);
      if (!cancelled && data) setRemoteBusinesses(data as BusinessRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.active_tenant_id, localTenantBusinesses.length]);

  const tenantBusinesses =
    localTenantBusinesses.length > 0 ? localTenantBusinesses : remoteBusinesses;

  // T-067 — Nome del tenant attivo (per il banner "Cliente: ...").
  // Default a useMasters('tenants') (popolato dal sync); fallback a fetch
  // diretto remoto se la cache è vuota (consulente che ha appena switchato
  // su un tenant non ancora sincronizzato in locale).
  const [remoteTenantName, setRemoteTenantName] = useState<string | null>(null);
  const activeTenantNameFromCache = useMemo(() => {
    const id = profile?.active_tenant_id;
    if (!id) return null;
    return cachedTenants.find((t) => t.id === id)?.name ?? null;
  }, [cachedTenants, profile?.active_tenant_id]);
  useEffect(() => {
    if (!profile?.active_tenant_id) { setRemoteTenantName(null); return; }
    if (activeTenantNameFromCache) { setRemoteTenantName(null); return; }
    let cancelled = false;
    void (async () => {
      const { data } = await getSupabase()
        .from('tenants')
        .select('id,name')
        .eq('id', profile.active_tenant_id)
        .maybeSingle();
      if (!cancelled && data?.name) setRemoteTenantName(data.name as string);
    })();
    return () => { cancelled = true; };
  }, [profile?.active_tenant_id, activeTenantNameFromCache]);
  const activeTenantName = activeTenantNameFromCache ?? remoteTenantName;

  const [imageB64, setImageB64] = useState<string | null>(null);
  const [imageMime, setImageMime] = useState<string>('image/jpeg');
  const [originalFilename, setOriginalFilename] = useState<string | null>(null);
  // T-016: supplierHint rimosso. L'AI estrae il fornitore autonomamente dal
  // documento; il campo "Suggerimento fornitore" generava aspettative di
  // un'interazione che in pratica non serviva mai.
  // Default 'fattura': il caso più comune in Italia è caricare la fattura/e-fattura ricevuta
  // dal fornitore. I DDT cartacei sono più rari e di solito vengono fotografati al ricevimento.
  const [docKind, setDocKind] = useState<DocKind>('fattura');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [importFailure, setImportFailure] = useState<ImportFailureBannerState | null>(null);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [creatingBusiness, setCreatingBusiness] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Wizard state.
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null);
  const [plan, setPlan] = useState<PlanDraft | null>(null);
  const latestImportState = useRef({ plan, tenantId: profile?.active_tenant_id });
  latestImportState.current = { plan, tenantId: profile?.active_tenant_id };
  const importFailureReport = useMemo(() => {
    if (!importFailure) return '';
    const line = importFailure.lineIndex == null ? null : plan?.lines[importFailure.lineIndex] ?? null;
    const product = line?.food_create.name?.trim()
      || line?.raw_description?.trim()
      || 'non identificato';
    const supplier = dryRun?.supplier.existing?.name
      || plan?.supplier_create.name?.trim()
      || 'non identificato';
    return [
      'Ciao, segnalo un errore nel carico documenti TrackEAT.',
      `Documento: ${docKind.toUpperCase()} ${plan?.document.number?.trim() || 'numero non disponibile'}`,
      `Fornitore: ${supplier}`,
      `Prodotto: ${product}`,
      `Codice articolo: ${line?.supplier_code?.trim() || 'non disponibile'}`,
      `Dettaglio: ${importFailure.message}`,
      `Riferimento tecnico: ${dryRun?.request_id || 'non disponibile'}`,
    ].join('\n');
  }, [docKind, dryRun, importFailure, plan]);
  // T-289: normalizzazione nomi articoli. Il nome pulito (senza rumore
  // commerciale / spec confezione, capitalizzato) è già il default alla
  // costruzione della bozza (food_create.name = cleanFoodName). Questo flag lo
  // rende VISIBILE e reversibile: OFF ⇒ ripristina il testo grezzo del DDT
  // (raw_description) come nome, per chi vuole la dicitura esatta del fornitore.
  // Persistito per-device (preferenza operativa, non dato).
  const [normalizeNames, setNormalizeNames] = useState<boolean>(() => {
    try { return localStorage.getItem('trackeat.normalize_article_names') !== 'off'; } catch { return true; }
  });
  // T-291: temperatura di ricevimento per articolo (gated sul flag tenant).
  // Mappa parsed_index → temperatura in °C (stringa editabile). Finisce nel
  // payload conformity.item_temps[{food_id,temp_c}] (solo righe con food_id).
  const receivingTempPerItem = useReceivingTempPerItemEnabled();
  const [itemTemps, setItemTemps] = useState<Record<number, string>>({});
  // T-170 1d: modalità review-from-job (?jobId=).
  const [jobReviewId, setJobReviewId] = useState<string | null>(null);
  const [jobReviewDraftId, setJobReviewDraftId] = useState<string | null>(null);
  // T-668: review staff da riaprire nello stesso piano, prima dell'approvazione.
  const [importReviewId, setImportReviewId] = useState<string | null>(null);
  const [reviewOriginalRecovery, setReviewOriginalRecovery] = useState<{
    status: 'not_needed' | 'checking' | 'required' | 'deferred';
    expectedPages: number | null;
  }>({ status: 'not_needed', expectedPages: null });
  // T-511: una revisione resta vincolata al tenant che ha prodotto
  // l'estrazione. Ionic può riusare la stessa pagina tra cambi cliente e
  // cambi di query string, quindi non possiamo dedurlo dal tenant corrente.
  const [reviewTenantId, setReviewTenantId] = useState<string | null>(null);
  // T-201: ri-estrazione AI di un documento esistente. Se valorizzato, al commit
  // forziamo replace_document = questo doc (il vecchio import viene annullato e
  // rimpiazzato dalla nuova estrazione), e sopprimiamo l'alert anti-duplicato.
  const isCorrectingImport = new URLSearchParams(location.search).get('correct_import') === '1';
  const [reextractTarget, setReextractTarget] = useState<{ id: string; source: 'public' | 'fiscal' } | null>(null);

  // Review-from-job: se arriviamo da "In elaborazione" con ?jobId=, idratiamo
  // la UI di review esistente dal result jsonb del job (P1/P6: il commit resta
  // il path foreground invariato; qui riusiamo dryRun/plan + buildPlanFromDryRun).
  useEffect(() => {
    if (!isActive || commitInFlight.current) return;
    const jid = new URLSearchParams(location.search).get('jobId');
    if (!jid) return;
    const activeTenantId = profile?.active_tenant_id;
    if (!activeTenantId) return;
    extractionRequest.current++;
    // T-201 review fix: entrare in una review-from-job azzera un eventuale
    // target di ri-estrazione stantio (non vogliamo che un commit del job
    // sostituisca per errore il documento di una ri-estrazione abbandonata).
    setReextractTarget(null);
    // Ionic conserva la pagina quando cambia soltanto ?jobId=. Svuotiamo il
    // job precedente prima di idratare quello nuovo.
    setDryRun(null);
    setPlan(null);
    setImageStoragePath(null);
    setJobReviewId(null);
    setJobReviewDraftId(null);
    setReviewTenantId(null);
    setReviewOriginalRecovery({ status: 'not_needed', expectedPages: null });
    let active = true;
    void (async () => {
      const job = await fetchDocumentJob(jid);
      if (!active || !job || !job.result) return;
      if (job.tenant_id !== activeTenantId) {
        setToast('Hai cambiato cliente: apri il documento dal cliente corretto.');
        history.replace('/documents');
        return;
      }
      // T-179: se il job è già stato importato (o scartato), non mostrare una
      // review "fantasma" re-committabile: lo staging è già stato pulito e un
      // nuovo commit darebbe errore. Avvisa e torna all'archivio documenti.
      if (job.status === 'committed' || job.status === 'dismissed') {
        setToast('Questo documento è già stato importato.');
        setTimeout(() => { if (active) history.replace('/documents'); }, 1200);
        return;
      }
      setBusinessId(job.business_id);
      setReviewTenantId(job.tenant_id);
      setJobReviewId(job.id);
      setJobReviewDraftId(job.client_draft_uuid ?? null);
      // Le pagine su staging non sono cancellate dal worker → valide al commit:
      // il server le rifetcha (image_storage_path) e archivia l'originale.
      if (job.page_paths && job.page_paths[0]) setImageStoragePath(job.page_paths[0]);
      if (job.doc_kind_guess === 'ddt' || job.doc_kind_guess === 'fattura') setDocKind(job.doc_kind_guess);
      const dr = await refreshDryRunMatches(job.result as unknown as DryRunResponse, activeTenantId, job.business_id);
      if (!active) return;
      setDryRun(dr);
      const refreshedPlan = await applyStoredPackFactors(buildPlanFromDryRun(dr));
      if (active) setPlan(refreshedPlan);
    })().catch(error => { if (active) setToast(toUserMessage(error)); });
    return () => { active = false; };
    // Le funzioni di costruzione del piano sono locali e ricreate a ogni render:
    // i trigger intenzionali sono soltanto job/query e tenant attivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isActive, loadAfterCommit, location.search, profile?.active_tenant_id]);

  // T-668: il consulente/OSA/ristoratore modifica il payload finale inviato
  // dallo staff. Non rilanciamo l'OCR e non ricreiamo una nuova review: il
  // commit successivo passa lo stesso review_request_id all'RPC atomica.
  useEffect(() => {
    if (!isActive || commitInFlight.current) return;
    const reviewId = new URLSearchParams(location.search).get('reviewId');
    if (!reviewId) return;
    const activeTenantId = profile?.active_tenant_id;
    if (!activeTenantId) return;
    let active = true;
    extractionRequest.current++;
    setReextractTarget(null);
    setDryRun(null);
    setPlan(null);
    setImageB64(null);
    setImageStoragePath(null);
    setJobReviewId(null);
    setJobReviewDraftId(null);
    setImportReviewId(null);
    setReviewTenantId(null);
    setReviewOriginalRecovery({ status: 'checking', expectedPages: null });
    void (async () => {
      try {
        const review = await fetchDocumentImportReviewPayload({
          tenantId: activeTenantId,
          reviewId,
        });
        if (!active) return;
        if (review.tenant_id !== activeTenantId || review.request_payload.tenant_id !== activeTenantId) {
          setToast('Cliente cambiato: apri la revisione dal cliente corretto.');
          history.replace('/documents');
          return;
        }
        if (review.status !== 'awaiting_review') {
          setToast('Questa revisione non è più disponibile.');
          history.replace('/documents');
          return;
        }
        const payload = review.request_payload;
        const historicalJob = payload.document_job_id
          ? await fetchDocumentJob(payload.document_job_id)
          : null;
        if (!active) return;
        const recovery = reviewOriginalRecoveryRequirement(
          payload,
          historicalJob?.page_paths?.length ?? null,
        );
        setReviewOriginalRecovery({
          status: recovery.required ? 'required' : recovery.deferred ? 'deferred' : 'not_needed',
          expectedPages: recovery.expectedPages,
        });
        const foods = new Map(
          tenantFoods.map((food) => [food.id, {
            id: food.id,
            name: food.name,
            default_uom: food.default_uom,
            category: food.category ?? null,
            storage_type: food.storage_type ?? null,
          }]),
        );
        const planFromReview = planFromStoredReview(payload, foods);
        setBusinessId(review.business_id);
        setReviewTenantId(review.tenant_id);
        setImportReviewId(review.id);
        setJobReviewId(payload.document_job_id ?? null);
        setDocKind(review.doc_kind);
        setImageMime(payload.image_mime ?? 'image/jpeg');
        setImageB64(payload.image_b64 ?? null);
        setImageStoragePath(payload.image_storage_path ?? null);
        const refreshed = await refreshDryRunMatches(dryRunFromStoredReview(review.id, payload, planFromReview), activeTenantId, review.business_id, planFromReview.supplier_id);
        if (!active) return;
        setDryRun(refreshed);
        setPlan(applyRefreshedMatches(planFromReview, refreshed));
        const storedConformity = payload.conformity;
        if (storedConformity?.conform === true) setConformity('yes');
        else if (storedConformity?.conform === false) setConformity('no');
        else setConformity('pending');
        setConformityNotes(storedReviewText(storedConformity?.notes));
        const storedTemps = Array.isArray(storedConformity?.item_temps)
          ? storedConformity.item_temps as unknown[]
          : [];
        const lineIndexByFoodId = new Map(
          planFromReview.lines
            .filter((line) => line.food_id)
            .map((line) => [line.food_id as string, line.parsed_index]),
        );
        setItemTemps(Object.fromEntries(storedTemps.flatMap((item) => {
          if (!item || typeof item !== 'object') return [];
          const record = item as Record<string, unknown>;
          const foodId = typeof record.food_id === 'string' ? record.food_id : null;
          const temp = typeof record.temp_c === 'number' ? record.temp_c : null;
          const lineIndex = foodId ? lineIndexByFoodId.get(foodId) : undefined;
          return lineIndex != null && temp != null ? [[lineIndex, String(temp)]] : [];
        })));
        setReviewStep('supplier');
      } catch (error) {
        if (!active) return;
        setReviewOriginalRecovery({ status: 'not_needed', expectedPages: null });
        setToast(error instanceof Error && error.message === 'document_import_review_not_found'
          ? 'Revisione non trovata.'
          : 'Impossibile aprire la bozza della revisione.');
        history.replace('/documents');
      }
    })();
    return () => { active = false; };
    // Le funzioni locali e la cache anagrafica cambiano a ogni render; i
    // trigger intenzionali sono query string, tenant e anagrafica alimenti.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isActive, loadAfterCommit, location.search, profile?.active_tenant_id, tenantFoods.length]);

  // T-201: ri-estrazione (?reextract=1). Carichiamo il file salvato del
  // documento, rilanciamo un NUOVO dry_run (altro round AI) e prepariamo il
  // commit in SOSTITUZIONE del doc di partenza (reextractTarget).
  useEffect(() => {
    if (!isActive || commitInFlight.current) return;
    const isRx = new URLSearchParams(location.search).get('reextract');
    if (!isRx) return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('trackeat_reextract'); } catch { /* no-op */ }
    if (!raw) {
      if (!reextractTarget) setToast('Dati ri-estrazione non disponibili. Riprova dalla scheda documento.');
      return;
    }
    let info: { docId: string; source: 'public' | 'fiscal'; businessId: string | null; fileUrl: string; mime: string; docKind: 'ddt' | 'fattura' };
    try { info = JSON.parse(raw); } catch { return; }
    let active = true;
    let ingestionStarted = false;
    void (async () => {
      if (info.businessId) setBusinessId(info.businessId);
      setDocKind(info.docKind);
      setReextractTarget({ id: info.docId, source: info.source });
      setBusy(true);
      setToast('Scarico il documento…');
      try {
        const resp = await fetch(info.fileUrl);
        if (!resp.ok) throw new Error(`download_${resp.status}`);
        const blob = await resp.blob();
        const b64 = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve((r.result as string).split(',')[1] ?? '');
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
        if (!active) return;
        // ingestFile chiama resetWizard (che NON azzera reextractTarget/docKind/
        // businessId) + prepara la sorgente + lancia il dry_run.
        const mime = blob.type.split(';', 1)[0] || info.mime;
        ingestionStarted = true;
        try {
          if (sessionStorage.getItem('trackeat_reextract') === raw) sessionStorage.removeItem('trackeat_reextract');
        } catch { /* no-op */ }
        await ingestFile(b64, mime, `riestrazione.${mime.split('/')[1] || 'jpg'}`, {
          businessId: info.businessId, docKind: info.docKind,
        });
      } catch {
        if (active) { setBusy(false); setToast('Impossibile rileggere il documento. Riprova.'); }
      }
    })();
    return () => {
      active = false;
      if (!ingestionStarted && !commitInFlight.current) setBusy(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, loadAfterCommit, location.key, location.search, profile?.active_tenant_id]);
  // T-375 P2: correzione import (?correct_import=1). NIENTE AI: ricostruiamo il
  // piano di review dai carichi già registrati del documento (righe passate via
  // sessionStorage da DocumentDetailPage), ri-validati col semaforo P1, e
  // iniettiamo direttamente plan + dryRun (senza dry_run AI) così il wizard apre
  // la review pre-compilata. Il file viene scaricato e messo su staging perché il
  // commit-replace ne ha bisogno; reextractTarget → replace_document al commit.
  useEffect(() => {
    if (!isActive || commitInFlight.current) return;
    if (new URLSearchParams(location.search).get('correct_import') !== '1') return;
    const correctionTenantId = profile?.active_tenant_id;
    if (!correctionTenantId) {
      return;
    }
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('trackeat_correct_import'); } catch { /* no-op */ }
    if (!raw) { setToast('Dati correzione non disponibili. Riprova dalla scheda documento.'); return; }
    let info: {
      docId: string; source: 'public' | 'fiscal'; businessId: string | null;
      fileUrl: string | null; mime: string; docKind: DocKind;
      document: {
        number: string | null;
        date: string | null;
        type: string;
        currency?: string | null;
        total_amount?: number | null;
      };
      supplier_id: string | null; lines: LoadedLine[];
    };
    try { info = JSON.parse(raw); } catch { return; }
    let active = true;
    void (async () => {
      const foodsMap = new Map<string, FoodLite>(
        tenantFoods.map((f) => [f.id, {
          id: f.id, name: f.name, default_uom: f.default_uom,
          category: f.category ?? null, storage_type: f.storage_type ?? null,
        }]),
      );
      const planLines = reconstructImportLines(info.lines, foodsMap);
      const documentTotal = correctionDocumentTotalDraft(
        info.document.total_amount,
      );
      // DryRun sintetico allineato 1:1 alle righe ricostruite. La UI risolve
      // comunque la sorgente tramite parsed_index, come nei dry-run aggregati.
      const syntheticDryRun: DryRunResponse = {
        mode: 'dry_run',
        provider_used: 'correct_import',
        confidence: null,
        document: {
          number: info.document.number,
          date: info.document.date,
          total_amount: documentTotal.parsed,
          type: info.document.type,
          currency: (info.document.currency ?? 'EUR').trim().toUpperCase() || 'EUR',
        },
        supplier: { parsed: null, existing: null },
        lines: info.lines.map((l) => ({
          index: l.seq,
          parsed: {
            description: l.description,
            qty: l.source_qty ?? l.qty,
            uom: l.source_uom ?? l.uom,
            batch_lot: l.batch_lot ?? undefined,
            expiration_date: l.expiration_date ?? undefined,
            iva_rate: l.iva_rate ?? undefined,
            unit_price: l.source_unit_price ?? l.unit_price ?? undefined,
            line_total: l.source_line_total ?? l.line_total ?? undefined,
          },
          food_match: null,
          match_source: null,
          validation: planLines.find((pl) => pl.parsed_index === l.seq)?.validation ?? undefined,
        })),
      };
      if (!active) return;
      resetWizard();
      setImageB64(null);
      setImageStoragePath(null);
      setReviewStep('supplier');
      setPackBaseDraft({});
      setPackFactorDraft({});
      setSourceQtyDraft({});
      if (info.businessId) setBusinessId(info.businessId);
      setReviewTenantId(correctionTenantId);
      setDocKind(info.docKind);
      setImageMime(info.mime);
      setReextractTarget({ id: info.docId, source: info.source });
      setDryRun(syntheticDryRun);
      setPlan({
        supplier_id: info.supplier_id,
        supplier_create: { name: '', piva: '', codice_fiscale: '', category: 'altro', email: '', phone: '', address: '', country: 'IT' },
        document: {
          number: info.document.number ?? '',
          date: info.document.date ?? '',
          total_amount: documentTotal.input,
          total_amount_estimated: false,
          type: info.document.type,
          currency: (info.document.currency ?? 'EUR').trim().toUpperCase() || 'EUR',
        },
        lines: planLines,
      });
      // Il file serve al commit-replace (il commit richiede imageB64 || imageStoragePath).
      if (info.fileUrl) {
        setBusy(true);
        setToast('Scarico il documento…');
        try {
          const resp = await fetch(info.fileUrl);
          if (!resp.ok) throw new Error(`download_${resp.status}`);
          const blob = await resp.blob();
          const b64 = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve((r.result as string).split(',')[1] ?? '');
            r.onerror = reject;
            r.readAsDataURL(blob);
          });
          if (!active) return;
          const mime = blob.type.split(';', 1)[0] || info.mime;
          const path = await uploadToStaging(b64, mime);
          if (!active) return;
          setImageMime(mime);
          setImageStoragePath(path);
          setBusy(false);
          setToast(null);
        } catch {
          if (active) { setBusy(false); setToast('Impossibile scaricare il documento per la correzione. Riprova.'); }
        }
      }
      if (active) {
        try { sessionStorage.removeItem('trackeat_correct_import'); } catch { /* no-op */ }
      }
    })();
    return () => { active = false; if (!commitInFlight.current) setBusy(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, loadAfterCommit, location.key, location.search, profile?.active_tenant_id]);
  // T-151: tab bar nascosta SOLO quando l'esito IA è pronto (review), non
  // durante capture/upload. Spenta all'uscita dalla pagina.
  useEffect(() => {
    if (quickLabel) return;
    setReviewFullscreen(isActive && !!dryRun);
    return () => setReviewFullscreen(false);
  }, [dryRun, isActive, quickLabel]);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  // Review post-AI in due step "sovrapposti": prima fornitore + documento,
  // poi righe alimenti. Niente schermate
  // separate (ne abbiamo solo una "review" con segmented).
  const [reviewStep, setReviewStep] = useState<'supplier' | 'lines' | 'summary'>('supplier');
  // T-478: picker "scegli un fornitore esistente" (utile quando l'AI non matcha o
  // in correzione, dove existing è null → altrimenti lo step resta muto).
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  // T-186: OCR fast-capture lotto+scadenza per riga (multi-foto combinata): idx → busy?
  const [linePhotoBusy, setLinePhotoBusy] = useState<Record<number, boolean>>({});
  // T-407: cattura multi-foto etichetta con popup pulito (niente window.confirm).
  const [labelCapture, setLabelCapture] = useState<{ idx: number; photos: string[] } | null>(null);
  // T-294B: bozze dell'input fattore di conversione (collo→base) per riga.
  const [packFactorDraft, setPackFactorDraft] = useState<Record<number, string>>({});
  const [packBaseDraft, setPackBaseDraft] = useState<Record<number, string>>({});
  // T-482: la quantità documentale è distinta dalla quantità fisica. Una
  // bozza locale evita che digitare "2" passi per stati intermedi invalidi e
  // consente una conferma esplicita prima di ricalcolare stock e prezzo.
  const [sourceQtyDraft, setSourceQtyDraft] = useState<Record<number, string>>({});
  // T-079: paginazione 1-card-per-volta per evitare tap accidentali su scroll.
  // Con tutte le righe in colonna l'utente scrollava la lista e ogni tanto
  // il dito atterrava sul segmento Aggiorna/Nuovo/Salta cambiando la
  // selezione per sbaglio. Mostrando 1 card alla volta con bottoni di
  // navigazione "← Precedente / Successivo →" il problema sparisce.
  const [currentLineIdx, setCurrentLineIdx] = useState(0);
  const reviewCardRef = useRef<HTMLDivElement | null>(null);
  const [openAccessoryInfo, setOpenAccessoryInfo] = useState<string | null>(null);
  const autoRoutedReviewKeyRef = useRef<string | null>(null);
  const automaticCommitStartedRef = useRef<string | null>(null);
  // T-151: gestione lotto attivo (Aggiungi/Sostituisci/registra-senza-cambiare)
  // collassata di default → la card articolo entra senza scroll; si espande al tap.
  const [lotMgmtOpen, setLotMgmtOpen] = useState(false);
  // T-157: impostazioni alimento (nome/categoria/conservazione/allergeni) dietro
  // accordion "Dettagli alimento" CHIUSO di default. È la massa principale che
  // causava lo scroll (~14 chip allergeni). Aperto solo per correggere.
  // T-315: per un NUOVO alimento i dettagli partono APERTI (vedi effetto sotto).
  const [detailsOpen, setDetailsOpen] = useState(false);
  const currentLineStartsAsNewFood = Boolean(
    plan?.lines[currentLineIdx]
    && !plan.lines[currentLineIdx]?.skip
    && !plan.lines[currentLineIdx]?.food_id,
  );
  // Quando arriva un nuovo dryRun (estrazione AI), torno alla prima riga.
  useEffect(() => {
    setCurrentLineIdx(0);
    setSourceQtyDraft({});
  }, [dryRun?.lines?.length]);
  // Cambiando riga, richiudo la gestione lotto (riparte compatta).
  useEffect(() => {
    setLotMgmtOpen(false);
    setOpenAccessoryInfo(null);
    // Progressive disclosure sugli articoli già noti; un nuovo alimento resta
    // invece aperto perché categoria, conservazione e allergeni vanno verificati.
    setDetailsOpen(currentLineStartsAsNewFood);
  }, [currentLineIdx, currentLineStartsAsNewFood]);

  // T-634: dopo "Avanti / prossima verifica" la card nuova deve tornare nel
  // viewport. Ionic mantiene lo scroll della card precedente anche se il
  // contenuto cambia nello stesso nodo.
  useEffect(() => {
    if (reviewStep !== 'lines') return;
    const frame = window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      reviewCardRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentLineIdx, reviewStep]);
  // T-068: picker "Scegli alimento da anagrafica" per match manuale quando
  // l'AI non riconosce l'articolo. Apriamo un IonModal con search bar che
  // filtra `tenantFoods` (anagrafica del business attivo).
  const [foodPickerForLine, setFoodPickerForLine] = useState<number | null>(null);
  const [foodPickerSearch, setFoodPickerSearch] = useState('');
  // Set delle righe per cui l'utente ha *esplicitamente* scelto un food
  // dall'anagrafica via picker. Serve a marcare l'alias con source='manual'
  // (vince su 'ai_match' nelle future chiamate alla stessa coppia
  // supplier+raw_desc).
  const [manuallyMatchedLines, setManuallyMatchedLines] = useState<Set<number>>(() => new Set());
  // Redesign: action sheet inline per cambiare locale senza uscire dalla
  // pagina (prima si navigava a /settings, perdendo il file caricato).
  const [localePickerOpen, setLocalePickerOpen] = useState(false);
  // Action sheet "Aggiungi documento" che apre i 3 input (camera/galleria/
  // file). Centralizza i 3 tile multi-colore precedenti in una sola dropzone.
  // T-077: state rimosso — i 3 bottoni di import sono in-page, niente più
  // action sheet intermedio. Lasciamo il commento qui per audit.

  // T-061: Controllo conformità al ricevimento merci (HACCP scheda di controllo).
  // 'pending' = nessuna scelta (bottone import disabilitato); 'yes' = tutto
  // conforme, import procede; 'no' = ci sono non conformità, l'utente deve
  // compilare una nota e l'import procede comunque con riserva (no blocco hard).
  const [conformity, setConformity] = useState<'pending' | 'yes' | 'no'>('pending');
  const [conformityNotes, setConformityNotes] = useState<string>('');
  const conformityNotesValid = conformityNotes.trim().length >= 5;
  // T-074: se il dry-run ha rilevato un documento già presente con stessa
  // chiave (supplier+number+date), blocchiamo il commit. Il server farebbe
  // comunque 409 ma vogliamo evitare quella round-trip e dare un blocco UI
  // immediato.
  //
  // T-074 follow-up: se l'utente sceglie "Sostituisci esistente con questo"
  // dal banner, settiamo `replaceMode = true`. Il blocco UI viene tolto e
  // il commit invierà `replace_document` al backend che fa rollback del
  // vecchio + inserisce il nuovo. Caso d'uso: cliente ha sbagliato a
  // digitare qty/lotto/scadenza nel primo import e vuole rifare.
  const [replaceMode, setReplaceMode] = useState(false);
  // T-201: in ri-estrazione il "duplicato" è atteso (stiamo rileggendo lo stesso
  // doc per sostituirlo) → non è un blocco.
  const isDuplicate = !!dryRun?.existing_document && !replaceMode && !reextractTarget;
  // T-144: popup conformità finale. Il bottone "Conferma e carica" è abilitato
  // appena il documento non è un duplicato bloccante; la conformità si conferma
  // nel popup (non più inline a schermo).
  const [confirmOpen, setConfirmOpen] = useState(false);
  // T-144: "documento già caricato" come POPUP (non più banner rosso fisso in
  // cima). Si apre quando il dry_run rileva il duplicato; una volta che il
  // cliente sceglie (sostituisci / apri / annulla) non resta a schermo.
  const [dupAlertOpen, setDupAlertOpen] = useState(false);
  useEffect(() => {
    if (isActive) return;
    setPreviewFullscreen(false);
    setSupplierPickerOpen(false);
    setLabelCapture(null);
    setFoodPickerForLine(null);
    setLocalePickerOpen(false);
    setConfirmOpen(false);
    setDupAlertOpen(false);
  }, [isActive]);
  useEffect(() => {
    if (isActive && dryRun?.existing_document && !replaceMode && !reextractTarget) setDupAlertOpen(true);
  }, [isActive, dryRun?.existing_document, replaceMode, reextractTarget]);
  const lotPreferenceRequest = useRef(0);
  const lotPreferenceSupplierId = plan?.supplier_id ?? null;
  useEffect(() => {
    const request = ++lotPreferenceRequest.current;
    let active = true;
    const tenantId = profile?.active_tenant_id;
    const supplierId = lotPreferenceSupplierId;
    if (!supplierId || !tenantId || plan?.supplier_lot_preference?.supplier_id === supplierId) return;
    void getSupabase().from('supplier_document_import_preferences')
      .select('documents_without_lot').eq('tenant_id', tenantId).eq('supplier_id', supplierId).maybeSingle()
      .then(({ data, error }) => {
        if (error || !active || request !== lotPreferenceRequest.current) return;
        setPlan((previous) => {
          if (!previous || previous.supplier_id !== supplierId
            || previous.supplier_lot_preference?.supplier_id === supplierId) return previous;
          const noLot = data?.documents_without_lot === true;
          return { ...previous, supplier_lot_preference: { supplier_id: supplierId, no_lot: noLot },
            lines: applySupplierLotPreference(applySupplierLotPreference(previous.lines, false), noLot) };
        });
      });
    return () => { active = false; };
  }, [lotPreferenceSupplierId, profile?.active_tenant_id, dryRun?.request_id, plan?.supplier_lot_preference?.supplier_id]);

  async function rememberSupplierLotPreference(noLot: boolean): Promise<void> {
    if (!plan) return;
    lotPreferenceRequest.current++;
    const supplierId = plan.supplier_id;
    setPlan({ ...plan, supplier_lot_preference: { supplier_id: supplierId, no_lot: noLot },
      lines: applySupplierLotPreference(plan.lines, noLot) });
    if (!supplierId) return;
    try {
      await persistSupplierLotPreference(supplierId, noLot);
      setToast(noLot ? 'Memorizzato per questo fornitore: documenti senza lotto. I lotti presenti restano conservati.' : 'Preferenza rimossa: il lotto sarà verificato su ogni documento.');
    } catch (error) { showImportFailure(toUserMessage(error)); }
  }
  async function persistSupplierLotPreference(supplierId: string, noLot: boolean): Promise<void> {
    const { error } = await getSupabase().rpc('set_supplier_document_lot_preference', {
      p_tenant_id: profile?.active_tenant_id, p_supplier_id: supplierId, p_documents_without_lot: noLot,
    });
    if (error) throw new Error('Scelta applicata alla bozza, ma non memorizzata per i prossimi documenti. Riprova con la connessione attiva.');
  }

  const documentDraftResourceKey = importReviewId
    ? `import-review:${importReviewId}`
    : jobReviewId
      ? `job:${jobReviewId}`
      : reextractTarget
        ? `document:${reextractTarget.source}:${reextractTarget.id}`
        : dryRun?.request_id
          ? `request:${dryRun.request_id}`
          : null;
  const documentDraftPayload = useMemo(() => ({
    plan,
    reviewStep,
    currentLineIdx,
    itemTemps,
    conformity,
    conformityNotes,
    replaceMode,
  }), [conformity, conformityNotes, currentLineIdx, itemTemps, plan, replaceMode, reviewStep]);
  const documentWorkDraft = usePersistentWorkDraft({
    enabled: (isActive || commitInFlight.current) && !!plan && !!dryRun,
    accountId: profile?.id ?? null,
    tenantId: reviewTenantId ?? profile?.active_tenant_id ?? null,
    businessId,
    resourceKind: 'document_review',
    resourceKey: documentDraftResourceKey,
    payload: documentDraftPayload,
    onRestore: (saved) => {
      if (!saved || typeof saved !== 'object' || !('plan' in saved)) return;
      const restored = saved as typeof documentDraftPayload;
      if (restored.plan) setPlan(restored.plan);
      setReviewStep(restored.reviewStep ?? 'supplier');
      setCurrentLineIdx(restored.currentLineIdx ?? 0);
      setItemTemps(restored.itemTemps ?? {});
      setConformity(restored.conformity ?? 'pending');
      setConformityNotes(restored.conformityNotes ?? '');
      setReplaceMode(restored.replaceMode ?? false);
      setToast('Bozza del carico recuperata.');
    },
  });
  const documentDraftLocked = documentWorkDraft.status === 'locked';
  const documentDraftReadOnly = documentDraftLocked
    || documentWorkDraft.status === 'idle'
    || documentWorkDraft.status === 'acquiring';
  const documentDraftFinalizable = documentWorkDraft.status === 'editable';
  // T-375: mappa una riga del piano nel formato del semaforo (helper puro
  // condiviso). `batch_lot === ''` → null (mancante, non stringa vuota). Il
  // `name`/`category` reali servono al gate unita_incoerente sulla ri-validazione.
  const toReviewLine = (l: PlanLineDraft): {
    validation?: LineValidation; food_id?: string | null; batch_lot?: string | null;
    uom?: string | null; qty?: number | null; name?: string | null; category?: string | null;
    gates_ack?: boolean; lot_ack?: boolean;
    unit_price?: number | null; loaded_uom?: string | null; matched_food_uom?: string | null;
  } => ({
    validation: l.validation ?? undefined,
    food_id: l.food_id ?? (l.new_food_confirmed ? 'new-confirmed' : null),
    batch_lot: l.batch_lot.trim() === '' ? null : l.batch_lot,
    uom: l.uom,
    qty: Number(l.qty) || null,
    name: l.food_create.name,
    category: l.food_create.category,
    gates_ack: l.gates_ack,
    lot_ack: l.lot_ack,
    // T-460: prezzo + UdM caricata (extracted) + UdM base del food agganciato,
    // per ri-derivare i gate food-cost (costo al pezzo / unità incoerente).
    unit_price: deriveStockUnitCost({
      sourceQty: l.source_qty,
      sourceUnitPrice: l.source_unit_price ?? l.unit_price,
      sourceLineTotal: l.source_line_total ?? l.line_total,
      stockQty: Number(l.qty) || null,
    }),
    loaded_uom: l.uom,
    matched_food_uom: l.food_id ? l.uom : null,
  });
  const identityCandidatesFor = (line: PlanLineDraft, index: number) => importIdentityCandidates(
    line, dryRun ? sourceLineForPlan(dryRun.lines, line, index) : null, tenantFoods,
  );
  const identityNeedsReview = (line: PlanLineDraft, index: number) => needsImportIdentityReview(line, identityCandidatesFor(line, index));
  // One readiness rule drives the queue, summary and commit button. A generic
  // acknowledgement never resolves identity conflicts or invalid quantities.
  const isLineReady = (line: PlanLineDraft, index: number): boolean => line.skip || (
    !identityNeedsReview(line, index)
    && isImportLineCommitReady(line)
    && lineOpenReasons(toReviewLine(line)).length === 0
    && !(line.pack_needs_factor && !line.pack_factor_applied)
    && (line.gates_ack === true || extractionWarnings(sourceLineForPlan(dryRun?.lines ?? [], line, index)).length === 0)
  );
  const redLines = plan?.lines.filter((line, index) => !isLineReady(line, index)).length ?? 0;
  const canCommit = documentDraftFinalizable && !isDuplicate && !!plan && plan.lines.some((line) => !line.skip)
    && redLines === 0
    && (Boolean(plan.supplier_id) || !(dryRun?.supplier.suggestions?.length || dryRun?.supplier.existing) || plan.supplier_create_ack === true);
  const canFinalizeImport = canCommit
    && (reviewOriginalRecovery.status === 'not_needed' || reviewOriginalRecovery.status === 'deferred');

  const automaticImportRequested = new URLSearchParams(location.search).get('automatic') === '1';
  useEffect(() => {
    if (!isActive) return;
    if (!automaticImportRequested || !jobReviewId || !dryRun || !plan) return;
    if (!accountAccess.resolved) return;
    if (documentWorkDraft.status === 'idle' || documentWorkDraft.status === 'acquiring') return;
    const assessment = assessDocumentAutoImport(dryRun);
    if (accountAccess.isSharedTablet || !assessment.eligible || !canFinalizeImport) {
      history.replace(`/documents/capture?jobId=${jobReviewId}`);
      setToast('Controllo necessario: il documento è stato aperto in revisione.');
      return;
    }
    if (automaticCommitStartedRef.current === jobReviewId) return;
    automaticCommitStartedRef.current = jobReviewId;
    void commitPlan();
    // commitPlan è una function declaration locale; i dati che autorizzano il
    // one-shot sono tutti rappresentati dalle dipendenze sottostanti.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountAccess.isSharedTablet, accountAccess.resolved, automaticImportRequested, canFinalizeImport, documentWorkDraft.status, dryRun, history, isActive, jobReviewId, plan]);

  // T-514: per un fornitore già riconosciuto non obblighiamo lo staff a
  // ripassare dai dati documento. Si apre la prima riga realmente da
  // verificare; se non ce ne sono, si arriva direttamente al riepilogo.
  useEffect(() => {
    if (!plan || !dryRun) {
      if (!plan && !dryRun) autoRoutedReviewKeyRef.current = null;
      return;
    }
    const routingKey = jobReviewId
      ?? dryRun.request_id
      ?? `${dryRun.document.number ?? ''}:${dryRun.document.date ?? ''}:${dryRun.lines.length}`;
    if (autoRoutedReviewKeyRef.current === routingKey) return;
    autoRoutedReviewKeyRef.current = routingKey;

    if (!plan.supplier_id) {
      setReviewStep('supplier');
      return;
    }
    const firstReview = plan.lines.findIndex((line, index) => !isLineReady(line, index));
    if (firstReview >= 0) {
      setCurrentLineIdx(firstReview);
      setReviewStep('lines');
      return;
    }
    setReviewStep(canCommit ? 'summary' : 'lines');
    // Il routing è intenzionalmente one-shot per estrazione: le correzioni
    // successive del piano non devono spostare la schermata sotto le dita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCommit, dryRun, jobReviewId, plan]);

  // T-092 follow-up: prima di chiedere all'utente di scegliere il locale,
  // proviamo a recuperare l'ultima scelta da localStorage per quel tenant.
  // Così chi importa DDT regolarmente dallo stesso locale (caso tipico
  // cliente single-tenant ma multi-business, o consulente che lavora un
  // cliente per volta) non rivede il picker a ogni Share Intent.
  const lastBusinessKey = profile?.active_tenant_id
    ? `trackeat.last_business_id.${profile.active_tenant_id}`
    : null;
  useEffect(() => {
    if (businessId) return;
    if (profile?.active_business_id) {
      setBusinessId(profile.active_business_id);
      return;
    }
    // Recover dal localStorage: scelta precedente dell'utente per questo tenant.
    if (lastBusinessKey && tenantBusinesses.length > 0) {
      try {
        const remembered = localStorage.getItem(lastBusinessKey);
        if (remembered && tenantBusinesses.some((b) => b.id === remembered)) {
          setBusinessId(remembered);
          return;
        }
      } catch { /* localStorage non disponibile (rare): ignora */ }
    }
    if (tenantBusinesses.length === 1) {
      setBusinessId(tenantBusinesses[0].id);
    }
  }, [profile?.active_business_id, tenantBusinesses, businessId, lastBusinessKey]);

  // T-092 follow-up #2: quando arriviamo dal Share Intent e non siamo riusciti
  // ad auto-selezionare un locale (perché >1 business e nessuno ricordato in
  // localStorage), apri AUTOMATICAMENTE il picker invece di mostrare un toast
  // criptico. UX: l'utente fa Share PDF, l'app si apre, vede subito il dialog
  // "scegli il locale" — sceglie, e da quel momento futuri share saltano
  // direttamente al wizard.
  const sharePickerTriggered = useRef<string | null>(null);
  useEffect(() => {
    if (!isActive || businessId || tenantBusinesses.length <= 1) return;
    const params = new URLSearchParams(location.search);
    const path = params.get('sharePath');
    if (params.get('fromShare') !== '1' || !path || sharePickerTriggered.current === path) return;
    sharePickerTriggered.current = path;
    setLocalePickerOpen(true);
  }, [businessId, isActive, location.search, tenantBusinesses]);

  // PDF object URL per anteprima inline.
  const pdfObjectUrl = useMemo(() => {
    if (!imageB64 || imageMime !== 'application/pdf') return null;
    try {
      const cleaned = imageB64.replace(/\s+/g, '');
      const bytes = Uint8Array.from(atob(cleaned), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [imageB64, imageMime]);

  useEffect(() => () => {
    if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
  }, [pdfObjectUrl]);

  // T-092: ingresso da Share Intent Android. Pipeline a 2 step:
  //
  //  STEP 1 (a ogni nuova condivisione): legge il file da Capacitor Filesystem
  //  e lo mette in state `pendingShareFile`. NON chiama ancora ingestFile()
  //  perché potrebbe non esserci ancora il businessId (locale di
  //  destinazione) — in quel caso runExtractionWith abortirebbe col toast
  //  "Seleziona prima il locale".
  //
  //  STEP 2 (useEffect successivo, watch su pendingShareFile + businessId):
  //  quando ENTRAMBI sono pronti (file caricato + locale risolto, anche
  //  dopo che l'utente l'ha scelto dal picker auto-aperto), allora chiama
  //  ingestFile e l'AI parte. Senza questa attesa, l'estrazione partiva
  //  immediatamente con businessId=null e tutto saltava.
  const shareImportTriggered = useRef<string | null>(null);
  const [pendingShareFile, setPendingShareFile] = useState<
    { b64: string; mime: string; name: string; path: string } | null
  >(null);
  useEffect(() => {
    if (!isActive) return;
    const params = new URLSearchParams(location.search);
    if (params.get('fromShare') !== '1') return;
    const sharePath = params.get('sharePath');
    const shareMime = params.get('shareMime') ?? 'application/pdf';
    const shareName = params.get('shareName') ?? 'documento_condiviso.pdf';
    if (!sharePath || shareImportTriggered.current === sharePath) return;
    shareImportTriggered.current = sharePath;
    void (async () => {
      let succeeded = false;
      try {
        const { Filesystem } = await import('@capacitor/filesystem');
        const fileUri = sharePath.startsWith('file://') ? sharePath : `file://${sharePath}`;
        const result = await Filesystem.readFile({ path: fileUri });
        const dataB64 = typeof result.data === 'string' ? result.data : '';
        if (!dataB64) {
          setToast('File condiviso vuoto o illeggibile');
          return;
        }
        // Parcheggia: STEP 2 lo prenderà appena businessId è pronto.
        // Un nuovo share può arrivare mentre Filesystem sta ancora leggendo.
        if (shareImportTriggered.current !== sharePath) return;
        setPendingShareFile({ b64: dataB64, mime: shareMime, name: shareName, path: sharePath });
        succeeded = true;
        // NB: NON puliamo i query param qui — servono ai picker-useEffect e
        // share-picker-useEffect per riconoscere che siamo arrivati via Share.
        // La pulizia avviene in STEP 2 dopo che ingestFile è partito.
      } catch (err) {
        console.error('[CapturePhoto] share import failed', err);
        setToast('Impossibile caricare il file condiviso');
      } finally {
        // Cleanup SharedPreferences sempre — anche su fallimento — così
        // App.tsx resume polling non riaccoda lo stesso file rotto in loop.
        try {
          const { clearPendingShare, getPendingShare } = await import('../../lib/share/shareIntent.js');
          if ((await getPendingShare())?.path === sharePath) await clearPendingShare();
        } catch {
          /* no-op */
        }
        if (!succeeded && shareImportTriggered.current === sharePath) {
          shareImportTriggered.current = null;
          if (history.location.pathname === '/documents/capture'
            && new URLSearchParams(history.location.search).get('sharePath') === sharePath) {
            history.replace('/documents/capture');
          }
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, isActive, location.search]);

  // STEP 2: appena businessId è risolto (auto-pick / localStorage / scelta
  // utente dal picker auto-aperto) E abbiamo un file in pending, lancia
  // l'estrazione AI tramite il flusso ingestFile standard.
  const shareIngestStarted = useRef<string | null>(null);
  useEffect(() => {
    if (!isActive || busy || commitInFlight.current || !pendingShareFile || !businessId || !profile?.active_tenant_id) return;
    const params = new URLSearchParams(location.search);
    if (params.get('fromShare') !== '1' || params.get('sharePath') !== pendingShareFile.path) return;
    if (pendingShareFile.path !== shareImportTriggered.current) return;
    if (shareIngestStarted.current === pendingShareFile.path) return;
    shareIngestStarted.current = pendingShareFile.path;
    const { b64, mime, name, path } = pendingShareFile;
    setPendingShareFile(null);
    void (async () => {
      try {
        await ingestFile(b64, mime, name);
      } finally {
        // Pulisci i query param SOLO ora, dopo che l'ingest è partito.
        if (history.location.pathname === '/documents/capture'
          && new URLSearchParams(history.location.search).get('sharePath') === path) {
          history.replace('/documents/capture');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingShareFile, businessId, busy, history, isActive, location.search, profile?.active_tenant_id]);

  // Conserviamo il file senza ridimensionarlo, lo salviamo in stato e,
  // se c'è un tenant attivo,
  // lanciamo SUBITO l'estrazione AI senza richiedere un secondo tap "Estrai".
  // Il bottone "Estrai con AI" resta comunque visibile come fallback in caso
  // di errore o per ri-tentare.
  async function ingestFile(rawB64: string, rawMime: string, filename: string, context?: { businessId: string | null; docKind: DocKind }): Promise<void> {
    resetWizard();
    const request = extractionRequest.current;
    setBusy(true);
    setToast('Preparazione file…');
    try {
      const source = await prepareDocumentFile(rawB64, rawMime);
      if (request !== extractionRequest.current) return;
      setImageB64(source.b64);
      setImageMime(source.mime);
      setOriginalFilename(filename);
      const kb = Math.round(source.sizeBytes / 1024);
      setToast(`File pronto (${kb} KB) · estrazione in corso…`);
      // Usa il file preparato senza attendere l’aggiornamento dello stato React.
      await runExtractionWith(source.b64, source.mime, context ?? { businessId, docKind });
    } catch (err) {
      if (request !== extractionRequest.current) return;
      setBusy(false);
      setToast(toUserMessage(err, 'Errore lettura file'));
    }
  }

  function handleFileSelect(ev: ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 50_000_000) {
      setToast('Il file supera 50 MB. Seleziona un file più piccolo.');
      ev.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const idx = dataUrl.indexOf(',');
      if (idx < 0) {
        setToast('File illeggibile');
        return;
      }
      void ingestFile(
        dataUrl.slice(idx + 1),
        file.type || 'application/octet-stream',
        file.name,
      );
    };
    reader.onerror = () => setToast('Errore lettura file');
    reader.readAsDataURL(file);
    ev.target.value = '';
  }

  function resetWizard(): void {
    extractionRequest.current++;
    setDryRun(null);
    setPlan(null);
    setImportFailure(null);
    setReplaceMode(false);
    setReviewTenantId(null);
    setJobReviewId(null);
    setJobReviewDraftId(null);
    setImportReviewId(null);
    setReviewOriginalRecovery({ status: 'not_needed', expectedPages: null });
    // T-081 follow-up: reset anche del path staging quando si abbandona il
    // flow (es. utente cambia file). L'edge function aveva il cleanup al
    // commit; lo staging file orfano verrà spazzato da cron settimanale.
    setImageStoragePath(null);
  }

  // T-511: se il consulente cambia cliente mentre il riepilogo è aperto,
  // chiudiamo la revisione. Conservare il piano precedente sarebbe pericoloso
  // anche per i file inline, dove non esiste un path staging che il server
  // possa usare come ulteriore segnale cross-tenant.
  useEffect(() => {
    const activeTenantId = profile?.active_tenant_id;
    if (!reviewTenantId || !activeTenantId || reviewTenantId === activeTenantId) return;
    resetWizard();
    setImageB64(null);
    setOriginalFilename(null);
    setBusinessId(null);
    setToast('Cliente cambiato: la revisione precedente è stata chiusa.');
    if (isActive) history.replace('/documents');
  }, [history, isActive, profile?.active_tenant_id, reviewTenantId]);

  async function createBusiness(): Promise<void> {
    const name = newBusinessName.trim();
    if (!name || !profile?.active_tenant_id) return;
    setCreatingBusiness(true);
    try {
      const { data, error } = await getSupabase()
        .from('businesses')
        .insert({ tenant_id: profile.active_tenant_id, name })
        .select('id,tenant_id,name')
        .single();
      if (error || !data) throw new Error(error?.message ?? 'insert_failed');
      const row = data as BusinessRow;
      setRemoteBusinesses((prev) => [...prev, row]);
      setBusinessId(row.id);
      setNewBusinessName('');
      setToast(`Locale "${row.name}" creato`);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setCreatingBusiness(false);
    }
  }

  async function refreshDryRunMatches(dr: DryRunResponse, tenantId: string, targetBusinessId: string | null, supplierId = dr.supplier.existing?.id ?? null): Promise<DryRunResponse> {
    const response = await callEdge({ mode: 'rematch', tenant_id: tenantId, business_id: targetBusinessId,
      rematch_supplier_id: supplierId, rematch_supplier: dr.supplier.parsed, rematch_lines: dr.lines.map(line => line.parsed) });
    return { ...dr,
      supplier: { ...dr.supplier, existing: response.supplier as SupplierMatch | null, suggestions: response.supplier_suggestions as DryRunResponse['supplier']['suggestions'] },
      lines: mergeRefreshedLineMatches(dr, response.lines as RefreshedLineMatch[]),
    };
  }

  async function changeImportSupplier(supplierId: string): Promise<void> {
    if (!plan || !dryRun || !profile?.active_tenant_id) return;
    const previous = plan;
    setBusy(true);
    try {
      const refreshed = await refreshDryRunMatches(dryRun, profile.active_tenant_id, businessId, supplierId);
      if (latestImportState.current.plan !== previous || latestImportState.current.tenantId !== profile.active_tenant_id) return;
      const selected: PlanDraft = { ...previous, supplier_id: supplierId, supplier_create_ack: false, supplier_lot_preference: undefined,
        lines: previous.lines.map((line, index) => manuallyMatchedLines.has(index) ? { ...line, new_food_confirmed: false } : { ...line, food_id: null, new_food_confirmed: false, match_confirmed: false, identity_error: false, keep_current_lot: false, gates_ack: false }) };
      setDryRun(refreshed);
      setPlan(current => current === previous ? applyRefreshedMatches(selected, refreshed) : current);
      setSupplierPickerOpen(false);
    } catch (error) { showImportFailure(toUserMessage(error)); }
    finally { setBusy(false); }
  }

  function createDifferentSupplier(): void {
    if (!plan || !dryRun) return;
    setPlan({ ...plan, supplier_id: null, supplier_create_ack: false, supplier_lot_preference: undefined,
      lines: plan.lines.map((line, index) => manuallyMatchedLines.has(index) ? { ...line, new_food_confirmed: false } : { ...line, food_id: null, new_food_confirmed: false, match_confirmed: false, identity_error: false, keep_current_lot: false, gates_ack: false }) });
    setDryRun({ ...dryRun, lines: dryRun.lines.map(line => ({ ...line, food_match: null, match_source: null,
      alias_meta: null, identity_candidates: [], similar_food: null, similar_foods: [], current_lot: null, default_keep_current_lot: false, lot_date_comparison: 'unknown' })) });
  }

  function buildPlanFromDryRun(dr: DryRunResponse): PlanDraft {
    // AUTO-DETECT kind: priorità a `dr.document.type` (l'AI ha letto l'header)
    // → fallback a `detectKindFromNumber` (prefisso "DDT N°:" / "FT N°:") se
    // l'AI non ha popolato type. Override del docKind UI se differisce, con
    // logging per audit.
    const aiType = (dr.document.type ?? '').toLowerCase();
    const fromAi: 'fattura' | 'ddt' | null = aiType === 'ddt'
      ? 'ddt'
      : aiType === 'fattura' || aiType === 'nota_credito' || aiType === 'nota_debito' || aiType === 'autofattura'
        ? 'fattura'
        : null;
    const detectedKind = fromAi ?? detectKindFromNumber(dr.document.number);
    if (detectedKind && detectedKind !== docKind) {
      void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
        dbg.info('capture-photo', 'doc_kind_auto_corrected', {
          ui_selected: docKind, detected: detectedKind,
          ai_type: dr.document.type, doc_number: dr.document.number,
        }),
      );
      setDocKind(detectedKind);
    }
    const effectiveKind = detectedKind ?? docKind;
    // Numero documento PULITO dal prefisso (es. "DDT N°: 2026/503" → "2026/503").
    const cleanNumber = cleanDocNumber(dr.document.number);
    // Lotto di fallback per fatture e DDT che non riportano i lotti reali del
    // fornitore. Formato esplicito leggibile sull'etichetta: "Vedi DDT n.123
    // del 04/05/2026" (o "Vedi fattura n... del ..."), così il consumatore che
    // legge il QR capisce subito dove cercare il lotto reale.
    const fallbackLot = buildLotFallback(effectiveKind, cleanNumber, dr.document.date);
    return {
      supplier_id: dr.supplier.existing?.id ?? null,
      supplier_create: {
        name: dr.supplier.parsed?.name ?? '',
        piva: dr.supplier.parsed?.piva ?? '',
        codice_fiscale: dr.supplier.parsed?.codice_fiscale ?? '',
        category: dr.supplier.parsed?.category ?? 'altro',
        email: dr.supplier.parsed?.email ?? '',
        phone: dr.supplier.parsed?.phone ?? '',
        address: dr.supplier.parsed?.address ?? '',
        country: dr.supplier.parsed?.country ?? 'IT',
      },
      document: {
        number: cleanNumber ?? '',
        date: dr.document.date ?? '',
        total_amount: dr.document.total_amount?.toString() ?? '',
        total_amount_estimated: dr.document.total_amount_estimated ?? false,
        type: dr.document.type ?? (effectiveKind === 'ddt' ? 'fattura' : 'fattura'),
        currency: (dr.document.currency ?? 'EUR').trim().toUpperCase() || 'EUR',
      },
      // T-324 D: fonde le righe stesso articolo+lotto+scadenza (es. normale +
      // sconto-merce) prima di costruire le righe del piano.
      lines: aggregateExtractedLines(dr.lines).map((l) => {
        // Logica: se l'AI ha calcolato total_weight_kg (es. 12 pz × 18kg = 216), preferiscilo a qty.
        // L'utente può comunque sovrascrivere.
        const totalKg = l.parsed.total_weight_kg;
        const useTotal = totalKg != null && totalKg > 0 && (l.parsed.qty ?? 0) !== totalKg;
        let finalQty = useTotal ? totalKg : (l.parsed.qty ?? 0);
        const rawDesc = (l.parsed.description ?? '').trim();
        const cleanedDesc = cleanFoodName(rawDesc);
        // Solo una memoria persistita del fornitore può pre-impostare il food.
        // I confronti per nome restano suggerimenti da scegliere esplicitamente.
        const serverMatch = l.food_match;
        const trustedServerMatch = serverMatch && isTrustedDocumentMatch(l.match_source)
          ? serverMatch
          : null;
        // T-517: codice/alias persistiti sono deterministici. Un confronto per
        // nome resta solo un suggerimento e non può far saltare la verifica.
        const autoFoodId = trustedServerMatch?.id ?? null;
        // Se selezioniamo un food esistente, l'UoM di magazzino è quella del food.
        const matchedFood = autoFoodId
          ? (
            tenantFoods.find((food) => food.id === autoFoodId)
            ?? trustedServerMatch
            ?? null
          )
          : null;
        // Preferiamo la UOM canonicalizzata dal server (uom_canonical) se presente,
        // così righe scritte come "NR" o "CAD" diventano "pz" prima di entrare nel plan.
        const serverUom = (l.parsed.uom_canonical as string | undefined) ?? l.parsed.uom;
        // UoM HINT: se la descrizione DDT contiene "(kg X)" o simili, l'unità
        // "vera" del prodotto è kg/l, non pz. Sovrascrivi default_uom AI quando
        // il match è chiaro e l'AI aveva detto "pz" generico.
        const uomHint = extractUomHint(rawDesc);
        const finalUom = matchedFood?.default_uom
          ?? (useTotal ? 'kg' : (uomHint ?? serverUom ?? 'pz'));
        // T-481: congela le coordinate LETTE sul documento prima di qualsiasi
        // override verso l'unità del food o total_weight_kg. qty/uom restano le
        // coordinate derivate di magazzino; source_* restano il documento.
        const sourceQty = l.parsed.qty ?? finalQty;
        const sourceUom = l.parsed.uom ?? serverUom ?? finalUom;
        const metricQty = !useTotal && sourceQty > 0
          ? convertQty(sourceQty, serverUom ?? sourceUom, finalUom)
          : null;
        const usedMetricConversion = metricQty != null && metricQty !== sourceQty;
        if (metricQty != null) finalQty = metricQty;
        const conversionFactor = sourceQty > 0 ? finalQty / sourceQty : null;
        const sourceAndStockUomDiffer =
          normalizeUom(sourceUom) !== normalizeUom(finalUom);
        const category = l.parsed.category ?? matchedFood?.category ?? 'altro';
        // Default di revisione: salumi freschi, mai sovrascrivere una scelta
        // esplicita già salvata in anagrafica con un'euristica sul nome.
        const storageType = importStorageDefault(category, l.parsed.storage_type, matchedFood?.storage_type);
        // Allergeni: AI > suggerimenti euristici da nome+categoria.
        const aiAllergens = Array.isArray(l.parsed.allergens) ? l.parsed.allergens : [];
        const allergensFinal = aiAllergens.length > 0
          ? aiAllergens
          : suggestAllergens(cleanedDesc || rawDesc, category as never);
        return {
          parsed_index: l.index,
          // T-063: serve il raw_description del modello (testo grezzo del DDT)
          // come hint UI, distinto dalla description normalizzata che diventa
          // food_create.name.
          raw_description: (l.parsed as { raw_description?: string | null }).raw_description ?? null,
          // T-198: codice articolo del fornitore (per dedup + alias per-codice).
          supplier_code: (l.parsed as { supplier_code?: string | null }).supplier_code ?? null,
          food_id: autoFoodId,
          food_create: {
            // T-289: nome pulito da rumore commerciale e spec confezione,
            // capitalizzato per leggibilità — a meno che la normalizzazione sia
            // OFF (l'utente vuole la dicitura grezza del fornitore).
            name: normalizeNames
              ? (cleanedDesc ? cleanedDesc.replace(/\b\w/g, (c) => c.toUpperCase()) : rawDesc)
              : rawDesc,
            default_uom: finalUom,
            storage_type: storageType,
            category,
            allergens: allergensFinal,
          },
          qty: finalQty.toString(),
          uom: finalUom,
          batch_lot: (l.parsed.batch_lot && l.parsed.batch_lot.trim()) || fallbackLot,
          expiration_date: l.parsed.expiration_date ?? '',
          is_frozen: storageType === 'surgelato',
          notify_expiration: true,
          storage_unit_id: '',
          supplier_sku: '',
          skip: l.import_preference === 'skip',
          replace_active_lot: false,
          // T-131: se la fattura è più vecchia/uguale del lotto attivo, di
          // default registra il carico ma non sovrascrive il lotto attivo.
          // Vale solo quando c'è un match server con lotto attivo.
          keep_current_lot: serverMatch ? (l.default_keep_current_lot ?? false) : false,
          // T-284 (Food Cost): porta il prezzo/totale riga dal dry_run al draft.
          source_qty: sourceQty,
          source_uom: sourceUom,
          source_unit_price: l.parsed.unit_price ?? null,
          source_line_total: l.parsed.line_total ?? null,
          currency: (dr.document.currency ?? 'EUR').trim().toUpperCase() || 'EUR',
          // Alias legacy: mai convertire/dividere il prezzo del documento.
          unit_price: l.parsed.unit_price ?? null,
          line_total: l.parsed.line_total ?? null,
          // T-294B: unità ESTRATTA (può essere un collo) + flag "serve fattore".
          // `serverUom` è l'unità canonica dell'AI prima dell'override col food.
          // Serve il fattore se è un collo e la qty NON è già il contenuto reale
          // (useTotal=false → l'AI non ha calcolato il peso totale).
          extracted_uom: serverUom ?? null,
          pack_needs_factor: isPackUnit(serverUom) && !useTotal,
          pack_factor: null,
          pack_base_uom: null,
          pack_factor_applied: false,
          conversion_factor: conversionFactor,
          conversion_source: useTotal
            ? 'document_weight'
            : (usedMetricConversion
              ? 'metric'
              : (sourceAndStockUomDiffer ? 'user_confirmed' : 'identity')),
          conversion_confirmed: !sourceAndStockUomDiffer
            || useTotal
            || usedMetricConversion,
          // chiave alias = parsed.description normalizzata (come al salvataggio).
          ai_description: rawDesc || null,
          // T-375: semaforo. La validation arriva dal dry_run per riga; le
          // conferme esplicite partono spente (l'operatore le accende).
          validation: autoFoodId
            ? (l.validation ?? null)
            : requireExplicitDocumentMatch(l.validation),
          gates_ack: false,
          lot_ack: false,
        };
      }),
    };
  }

  /** La memoria fornitore decide unità operative e fattore; l'OCR fornisce
   * quantità e prezzi della nuova consegna. Riapplicata anche prima del commit.
   */
  async function applyStoredPackFactors(p: PlanDraft): Promise<PlanDraft> {
    if (!p.supplier_id || !profile?.active_tenant_id || isCorrectingImport) return p;
    try {
      const sb = getSupabase();
      const { data } = await sb
        .from('supplier_item_aliases')
        .select('food_id, raw_description_norm, supplier_code, pack_factor, pack_uom, base_uom')
        .eq('tenant_id', profile.active_tenant_id)
        .eq('supplier_id', p.supplier_id);
      if (!data || data.length === 0) return p;
      const aliases = data as SupplierAliasPackFactor[];
      const { data: liveFoods, error: foodsError } = await sb.from('foods')
        .select('id,default_uom').eq('tenant_id', profile.active_tenant_id)
        .is('archived_at', null).in('id', [...new Set(aliases.map(alias => alias.food_id))]);
      if (foodsError || !liveFoods) return p;
      const lines = p.lines.map((l) => {
        const food = liveFoods.find(food => food.id === l.food_id);
        const memory = l.food_id && food ? resolveSupplierUnitMemory(aliases, {
          foodId: l.food_id,
          foodUom: food.default_uom,
          supplierCode: l.supplier_code,
          descriptions: [l.raw_description, l.ai_description, l.food_create.name],
        }) : null;
        if (memory) {
          const sourceQty = Number(l.source_qty ?? l.qty.replace(',', '.'));
          if (!Number.isFinite(sourceQty) || sourceQty <= 0) return l;
          const reasons = l.validation?.reasons.filter(reason => reason !== 'unita_incoerente');
          return {
            ...l,
            qty: String(Number((sourceQty * memory.factor).toFixed(4))),
            uom: memory.stockUom,
            source_uom: memory.sourceUom,
            extracted_uom: memory.sourceUom,
            food_create: { ...l.food_create, default_uom: memory.stockUom },
            pack_factor: memory.factor,
            pack_base_uom: memory.stockUom,
            pack_factor_applied: true,
            pack_needs_factor: false,
            conversion_factor: memory.factor,
            conversion_source: 'supplier_alias' as const,
            conversion_confirmed: true,
            validation: l.validation && reasons ? {
              ...l.validation, reasons, status: reasons.length ? 'da_confermare' as const : 'ok' as const,
              suggested_uom: null,
            } : l.validation,
          };
        }
        if (l.new_food_confirmed || l.pack_factor_applied) return l;
        const rec = findTrustedPackFactor(aliases, {
          supplierCode: l.supplier_code,
          description: l.raw_description ?? l.ai_description ?? l.food_create.name,
          extractedUom: l.extracted_uom,
          foodId: l.food_id,
          foodUom: l.uom,
        });
        if (!rec) return l;
        const packCount = Number(l.source_qty ?? (l.qty || '').replace(',', '.'));
        if (!Number.isFinite(packCount) || packCount <= 0) return l;
        const base = rec.baseUom;
        const conv = Number((packCount * rec.factor).toFixed(4)); // R4 #2
        if (conv <= 0) return l; // contenuto sub-tracciabile → non auto-convertire
        return {
          ...l,
          food_id: l.food_id ?? rec.foodId,
          qty: String(conv),
          uom: base,
          source_uom: rec.packUom,
          extracted_uom: rec.packUom,
          // T-481: il prezzo unitario resta quello del collo sul documento.
          // Il costo sulla base si deriva dal valore riga / qty di magazzino.
          food_create: { ...l.food_create, default_uom: base },
          pack_factor: rec.factor,
          pack_base_uom: base,
          pack_factor_applied: true,
          pack_needs_factor: false,
          conversion_factor: rec.factor,
          conversion_source: 'supplier_alias',
          conversion_confirmed: true,
        };
      });
      return { ...p, lines };
    } catch {
      return p; // memoria fattori non disponibile → nessuna conversione, non-bloccante
    }
  }

  function selectExistingFoodAndRestorePackFactor(
    idx: number,
    food: FoodMatch,
  ): void {
    const currentPlan = plan;
    const currentLine = currentPlan?.lines[idx];
    if (!currentPlan || !currentLine) return;

    const selectedPlan: PlanDraft = {
      ...currentPlan,
      lines: currentPlan.lines.map((line, lineIdx) => lineIdx === idx
        ? {
          ...line,
          skip: false,
          skip_once: false,
          food_id: food.id,
          match_confirmed: true,
          new_food_confirmed: false,
          identity_error: false,
          uom: food.default_uom,
          food_create: { ...line.food_create, default_uom: food.default_uom },
        }
        : line),
    };
    setPlan(selectedPlan);
    void applyStoredPackFactors(selectedPlan).then((restoredPlan) => {
      const restored = restoredPlan.lines[idx];
      if (!restored?.pack_factor_applied) return;
      setPlan((latest) => {
        const latestLine = latest?.lines[idx];
        // Un risultato asincrono non deve mai sovrascrivere una scelta o una
        // correzione fatta nel frattempo su un altro alimento.
        if (!latest || !latestLine || latestLine !== selectedPlan.lines[idx]) return latest;
        const lines = latest.lines.slice();
        lines[idx] = {
          ...latestLine,
          qty: restored.qty,
          uom: restored.uom,
          food_create: { ...latestLine.food_create, default_uom: restored.uom },
          pack_factor: restored.pack_factor,
          pack_base_uom: restored.pack_base_uom,
          pack_factor_applied: restored.pack_factor_applied,
          pack_needs_factor: restored.pack_needs_factor,
          conversion_factor: restored.conversion_factor,
          conversion_source: restored.conversion_source,
          conversion_confirmed: restored.conversion_confirmed,
        };
        return { ...latest, lines };
      });
    });
  }

  /**
   * T-294B — Applica il fattore inserito a mano la PRIMA volta su una riga in
   * colli: qty_collo × fattore → contenuto reale nell'unità base. Il fattore
   * viene poi salvato al commit (vedi upsert RPC) e richiamato in automatico ai
   * carichi successivi (applyStoredPackFactors).
   */
  // fix #2: se l'utente edita qty/uom DOPO una conversione applicata, il fattore
  // memorizzato diventa stantio → non deve essere salvato nell'alias (avvelenerebbe
  // i carichi futuri). Azzera i campi pack: la riga resta committabile (la qty
  // editata a mano è quella reale), ma il fattore non viene persistito.
  function clearPackOnEdit(line: PlanLineDraft): Partial<PlanLineDraft> {
    return line.pack_factor_applied
      ? {
          pack_factor: null,
          pack_base_uom: null,
          pack_factor_applied: false,
          conversion_factor: null,
          conversion_source: 'user_confirmed',
        }
      : {};
  }

  function applySourceQuantityCorrection(
    idx: number,
    line: PlanLineDraft,
    draft: string,
  ): void {
    const edited = editSourceQuantityDraft({
      nextSourceQty: draft,
      currentStockQty: line.qty,
      stockUom: line.uom,
      sourceQty: line.source_qty,
      sourceUom: line.source_uom,
      sourceUnitPrice: line.source_unit_price,
      sourceLineTotal: line.source_line_total,
      conversionFactor: line.conversion_factor,
      conversionSource: line.conversion_source,
    });
    if (!edited) {
      showImportFailure(
        `“${line.food_create.name || line.raw_description || `Riga ${idx + 1}`}”: inserisci una quantità documento valida (> 0).`,
        idx,
      );
      return;
    }
    patchLine(idx, {
      source_qty: edited.sourceQty,
      qty: edited.stockQty,
      source_unit_price: edited.sourceUnitPrice,
      source_line_total: edited.sourceLineTotal,
      unit_price: edited.sourceUnitPrice,
      line_total: edited.sourceLineTotal,
      conversion_factor: edited.conversionFactor,
      conversion_source: edited.conversionSource,
      conversion_confirmed: edited.conversionConfirmed,
    });
    setSourceQtyDraft((current) => {
      const next = { ...current };
      delete next[idx];
      return next;
    });
    setImportFailure(null);
    setToast(
      `Quantità documento corretta: ${edited.sourceQty} ${line.source_uom}`
      + ` → ${edited.stockQty} ${line.uom}`,
    );
  }

  function applyManualPackFactor(
    idx: number,
    line: PlanLineDraft,
    factorStr: string,
    baseStr: string,
    sourceQtyStr: string,
  ): void {
    const f = Number((factorStr ?? '').replace(',', '.'));
    if (!Number.isFinite(f) || f <= 0) {
      showImportFailure(`“${line.food_create.name || `Riga ${idx + 1}`}”: inserisci un fattore valido (> 0).`, idx);
      return;
    }
    // fix #9: mirror del clamp RPC (0<f<=100000) — altrimenti il client
    // convertirebbe ma la RPC scarterebbe il fattore (NULL) senza avvisare.
    if (f > 100000) {
      showImportFailure(`“${line.food_create.name || `Riga ${idx + 1}`}”: fattore troppo grande (massimo 100000 per collo).`, idx);
      return;
    }
    // R3 #2: un food ESISTENTE con unità NON base (es. 'conf' da anagrafiche
    // vecchie) non può essere convertito qui senza mescolare le unità della sua
    // giacenza → indirizza alla correzione unità per-alimento (Magazzino).
    if (line.food_id && !isCorrectingImport && !isBaseUom(line.uom)) {
      showImportFailure(
        `L'unità anagrafica ("${line.uom}") non è convertibile in sicurezza durante l'import. `
        + "Verifica l'anagrafica o contatta l'assistenza; il DDT non è stato modificato.",
        idx,
      );
      return;
    }
    // fix #6/#11: per un food ESISTENTE la base è vincolata alla sua default_uom
    // (line.uom) → niente unità miste nella giacenza dello stesso alimento.
    const base = (baseStr || '').trim() || line.uom;
    if (line.food_id && !isCorrectingImport && normalizeUom(base) !== normalizeUom(line.uom)) {
      showImportFailure('Correggi prima l’unità del prodotto dal Magazzino, oppure riapri il documento con “Correggi importazione”.', idx);
      return;
    }
    // T-482: il moltiplicatore è SEMPRE la quantità commerciale sorgente.
    // `line.qty` può essere già il peso totale proposto dall'IA (0,8 kg);
    // usarlo qui trasformava 2 pz × 0,175 in 0,8 × 0,175 = 0,14 kg.
    const conversion = applyPackFactorDraft({
      sourceQty: sourceQtyStr,
      factor: f,
    });
    if (!conversion) {
      showImportFailure(
        `“${line.food_create.name || `Riga ${idx + 1}`}”: quantità documento o contenuto per pezzo non validi.`,
        idx,
      );
      return;
    }
    patchLine(idx, {
      qty: conversion.stockQty,
      uom: base,
      // La quantità sorgente resta il numero pezzi/colli confermato. Il prezzo
      // continua a riferirsi a questa coordinata documentale.
      source_qty: conversion.sourceQty,
      source_uom: line.source_uom ?? line.extracted_uom ?? line.uom,
      pack_factor: conversion.factor,
      pack_base_uom: base,
      pack_factor_applied: true,
      pack_needs_factor: false,
      conversion_factor: conversion.factor,
      conversion_source: 'user_confirmed',
      conversion_confirmed: true,
      food_create: { ...line.food_create, default_uom: line.food_id ? line.food_create.default_uom : base },
    });
    setSourceQtyDraft((current) => {
      const next = { ...current };
      delete next[idx];
      return next;
    });
    setImportFailure(null);
    setToast(
      `Convertito: ${conversion.sourceQty} ${line.source_uom ?? line.extracted_uom ?? 'colli'}`
      + ` × ${conversion.factor} ${base} = ${conversion.stockQty} ${base}`,
    );
  }

  async function callEdge(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { data } = await getSupabase().auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new UserFacingError('Sessione scaduta. Esci e rientra.');
    // T-081 follow-up: catturo anche fetch error (DNS/timeout/offline) per
    // loggarli con stack. Prima un network error usciva come "Failed to fetch"
    // generico senza traccia remota.
    let res: Response;
    try {
      res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/fiscal-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (fetchErr) {
      void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
        dbg.error('capture-photo', 'fiscal_photo_fetch_failed', {
          mode: payload.mode,
          doc_kind: payload.doc_kind,
          error: (fetchErr instanceof Error) ? fetchErr.message : String(fetchErr),
          stack: (fetchErr instanceof Error) ? fetchErr.stack?.slice(0, 500) : undefined,
        }),
      );
      throw fetchErr;
    }
    let json: Record<string, unknown>;
    try {
      json = (await res.json()) as Record<string, unknown>;
    } catch (parseErr) {
      void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
        dbg.error('capture-photo', 'fiscal_photo_response_not_json', {
          status: res.status,
          mode: payload.mode,
          error: (parseErr instanceof Error) ? parseErr.message : String(parseErr),
        }),
      );
      throw new UserFacingError(`Risposta server non leggibile (HTTP ${res.status}). Riprova.`);
    }
    if (!res.ok) {
      const errCode = (json.error as string) ?? `HTTP ${res.status}`;
      const detail = JSON.stringify(json);
      // Log ogni errore HTTP del fiscal-photo con il body completo: così quando
      // l'utente segnala "l'analisi AI fallisce" possiamo guardare nei traces
      // log-ingest e vedere esattamente cosa è successo (codice + hint + provider).
      void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
        dbg.error('capture-photo', 'fiscal_photo_http_error', {
          status: res.status,
          mode: payload.mode,
          doc_kind: payload.doc_kind,
          err_code: errCode,
          response_excerpt: detail.slice(0, 600),
        }),
      );
      if (/503|UNAVAILABLE|high demand/i.test(detail))
        throw new UserFacingError('Servizio AI sovraccarico. Riprova tra qualche secondo.');
      if (/missing_GOOGLE_AI_STUDIO_KEY|missing_OPENAI_API_KEY|missing_GEMINI_API_KEY/.test(detail))
        throw new UserFacingError('Configurazione AI mancante: contatta il supporto.');
      if (errCode === 'extraction_incomplete') {
        // T-081 follow-up: messaggio più informativo. L'hint del server dice se
        // è "numero documento", "totale" o "righe mancanti".
        // FIX: badRequest impacchetta i dettagli sotto `details` (plurale); il
        // codice leggeva `detail` (singolare) → l'hint NON veniva mai letto e si
        // cadeva sempre sul generico "documento illeggibile". Leggiamo `details`.
        const hint = (json as { hint?: string }).hint
          ?? ((json as { details?: { hint?: string } }).details?.hint)
          ?? ((json as { detail?: { hint?: string } }).detail?.hint);
        // T-197/T-201: documento multi-pagina troncato (rilevato "SEGUE"/totali
        // vuoti). In ri-estrazione il messaggio è diverso: non si "rifotografa"
        // un documento già salvato → va ricaricato da capo.
        if (hint === 'pages_missing') {
          const isReextract = new URLSearchParams(location.search).get('reextract') === '1';
          throw new UserFacingError(
            isReextract
              ? '📄 Il documento salvato ha una sola pagina (era stato importato prima dell\'aggiornamento multi-pagina). '
                + 'Per rileggerlo completo, ricaricalo da capo dalla fotocamera fotografando TUTTE le pagine.'
              : '📄 Questo documento continua su un\'altra pagina (manca la parte coi totali). '
                + 'Rifai l\'importazione fotografando TUTTE le pagine del documento, inclusa l\'ultima.',
          );
        }
        const hintIt =
          hint === 'total_amount_missing' ? 'totale documento'
          : hint === 'no_lines_in_ddt' ? 'nessuna riga prodotti'
          : 'numero o data documento';
        throw new UserFacingError(`Documento illeggibile (manca: ${hintIt}). Riprova con foto più nitida, ben dritta e a fuoco.`);
      }
      // T-200: lo staging del documento è scaduto/ripulito (draft vecchio). Non è
      // un errore opaco: l'utente deve riscattare le foto, non ritentare a vuoto.
      if (errCode === 'staging_expired') {
        throw new UserFacingError(
          'Le immagini di questo documento non sono più disponibili (sessione scaduta). ' +
          'Riscatta le foto del documento e riprova l\'importazione.',
        );
      }
      if (errCode === 'missing_business_id' || errCode === 'missing_business_id_for_ddt')
        throw new UserFacingError('Seleziona il locale (ristorante/azienda) di destinazione prima di importare.');
      if (errCode === 'duplicate_document' && (json as { existing?: ExistingDocument }).existing) {
        const ex = (json as { existing: ExistingDocument }).existing;
        const dateIt = ex.date ? new Date(ex.date).toLocaleDateString('it-IT') : '—';
        throw new UserFacingError(
          `🚫 Documento già caricato il ${dateIt} (n. ${ex.number}). ` +
          `Per ricaricarlo, prima elimina quello esistente da Documenti.`,
        );
      }
      if (errCode === 'active_lot_has_stock') {
        throw new UserFacingError(
          'Il lotto attivo ha ancora una giacenza o una prenotazione. '
          + 'L’import non ha modificato nulla: scegli “Aggiungi” per mantenere '
          + 'il vecchio lotto, oppure scaricalo esplicitamente da Magazzino e riprova.',
        );
      }
      if (
        errCode === 'document_stock_replace_conflict'
        || errCode === 'document_stock_cancel_conflict'
      ) {
        throw new UserFacingError(
          'Questo DDT non può essere sostituito o annullato perché parte del carico '
          + 'è già stata consumata o rettificata. Documento e magazzino non sono stati modificati. '
          + 'Mantieni il DDT e registra una rettifica di magazzino con le quantità effettive.',
        );
      }
      if (errCode === 'unresolved_gate') {
        const line = typeof json.line === 'number' ? json.line + 1 : null;
        throw new UserFacingError(
          `Completa la verifica${line ? ` dell'articolo ${line}` : ''} e conferma nuovamente l'importazione.`,
        );
      }
      if (errCode === 'supplier_requires_review') throw new UserFacingError('Il fornitore potrebbe già esistere. Scegli quello corretto oppure conferma esplicitamente che è diverso.');
      if (errCode === 'import_food_identity_changed' || errCode === 'import_identity_review_required' || errCode === 'similar_food_requires_review') {
        const conflicts = (json.conflicts ?? []) as Array<{ line: number; candidates: IdentityCandidate[] }>;
        if (dryRun && profile?.active_tenant_id) {
          try {
            const refreshed = await refreshDryRunMatches(dryRun, profile.active_tenant_id, businessId);
            setDryRun({ ...refreshed, lines: refreshed.lines.map(line => {
              const conflict = conflicts.find(item => item.line === line.index);
              return { ...line, identity_candidates: conflict?.candidates.length ? conflict.candidates
                : line.food_match ? [line.food_match] : line.identity_candidates };
            }) });
          } catch { /* Persist the highlighted rows even if refresh is unavailable. */ }
        }
        setPlan(current => current ? { ...current, lines: current.lines.map(line =>
          !line.food_id && !line.skip && (!conflicts.length || conflicts.some(conflict => conflict.line === line.parsed_index))
            ? { ...line, identity_error: true, new_food_confirmed: false } : line) } : current);
        setReviewStep('summary');
        throw new UserFacingError('Alcuni articoli sono già presenti o hanno più abbinamenti. Apri le righe rosse e scegli dove caricarli, oppure conferma un articolo distinto.');
      }
      if (errCode === 'existing_food_uom_mismatch') {
        throw new ExistingFoodUomMismatchError(
          Number(json.line),
          typeof json.food_id === 'string' ? json.food_id : null,
          typeof json.expected_uom === 'string' ? json.expected_uom : 'UdM anagrafica',
          typeof json.received_uom === 'string' ? json.received_uom : 'UdM documento',
        );
      }
      if (errCode === 'supplier_piva_matches_recipient') {
        throw new UserFacingError(
          'La P.IVA letta appartiene al destinatario, non al fornitore. '
          + 'Controlla il fornitore nella prima schermata; il documento e il magazzino non sono stati modificati.',
        );
      }
      if (
        errCode === 'invalid_stock_line'
        && json.detail === 'conversion_confirmation_required'
      ) {
        throw new UserFacingError(
          'La quantità nella nuova unità di misura non è stata confermata. '
          + 'Inserisci la quantità reale o applica il fattore collo: il documento e il magazzino non sono stati modificati.',
        );
      }
      // T-081 follow-up: gestione esplicita 429 (quota Gemini) + 401/403 (auth).
      if (res.status === 429 || errCode === 'quota_exceeded')
        throw new UserFacingError('Quota AI esaurita per oggi. Riprova più tardi o contattaci.');
      if (res.status === 401 || res.status === 403)
        throw new UserFacingError('Sessione scaduta o accesso negato. Esci e rientra.');
      // Espone il messaggio Postgrest se presente.
      const dm = (json.detail as { message?: string } | undefined)?.message;
      const incompatibleLine = dm?.match(/stock_command_v1_event_(\d+)_incompatible_mass_volume_uom/);
      if (incompatibleLine) {
        const acceptedIndex = Number(incompatibleLine[1]) - 1;
        const planIndex = plan ? planIndexForAcceptedLine(plan.lines, acceptedIndex) : null;
        const line = planIndex == null ? null : plan?.lines[planIndex];
        const product = line?.food_create.name?.trim() || line?.raw_description?.trim() || `riga ${acceptedIndex + 1}`;
        throw new ImportLineError(
          `“${product}”${line?.supplier_code ? ` (codice ${line.supplier_code})` : ''}: non posso convertire automaticamente ${line?.source_uom || 'l’unità del documento'} in ${line?.uom || 'unità di magazzino'} perché una misura di volume e una di peso non sono equivalenti. Se l’unità letta dal DDT è errata, correggila nella riga; altrimenti serve il dato di conversione reale del prodotto. Nessun dato è stato modificato.`,
          planIndex,
        );
      }
      if (dm?.includes('uom_rebase_') || dm?.includes('document_uom_correction_')) {
        throw new UserFacingError('Non posso cambiare l’unità nel documento perché il prodotto ha altri movimenti o giacenze da verificare. Correggi l’unità e le quantità dal Magazzino. Il documento e i suoi carichi sono rimasti invariati.');
      }
      if (dm) throw new UserFacingError(`Errore servizio AI (${errCode}): ${dm}`);
      // T-094: fallback con codice visibile. Senza il codice, qualunque errore
      // server sconosciuto cadeva nel toast generico "Si è verificato un
      // errore" senza pista — quando ogni 3-4 import l'AI sputa un codice
      // nuovo (timeout, internal_error, ecc.) almeno l'utente sa cosa dirci.
      throw new UserFacingError(
        `Analisi AI non riuscita (codice: ${errCode}). Riprova tra qualche secondo; ` +
        `se persiste, scrivici il codice qui sopra.`,
      );
    }
    return json;
  }

  // Foto rapida lotto/scadenza per una singola riga della fattura: l'utente scatta
  // foto del package fisico (etichetta lotto o data scadenza) e ai-vision-proxy
  // estrae batch_lot + expiration_date. Compensa il fatto che le fatture non
  // riportano i lotti reali del fornitore.
  // T-186: cattura MULTI-FOTO delle facce dello stesso prodotto (lotto su un
  // lato, scadenza sull'altro) → UNA sola estrazione che compila ENTRAMBI
  // batch_lot + expiration_date. Una sola call AI (quota +1, un evento), come il
  // carico DDT multi-pagina. Retrocompat lato server con image_b64 singola.
  // T-407: apre la cattura scattando la PRIMA foto, poi mostra un mini-modal
  // pulito (miniature + "Scatta un'altra foto" / "Analizza") invece del
  // window.confirm nativo con OK/Annulla. Più facce dello stesso prodotto
  // (lotto su un lato, scadenza sull'altro) si allegano insieme, stile WhatsApp.
  async function beginLabelCapture(idx: number): Promise<void> {
    try {
      const first = await getCaptureProvider().takePhoto('camera');
      if (!first) return;
      setLabelCapture({ idx, photos: [first.base64] });
    } catch (err) {
      setToast(toUserMessage(err, 'Cattura annullata'));
    }
  }

  async function addLabelCapturePhoto(): Promise<void> {
    try {
      const MAX_FACES = 3;
      const p = await getCaptureProvider().takePhoto('camera');
      if (p) {
        setLabelCapture((s) =>
          s ? { ...s, photos: [...s.photos, p.base64].slice(0, MAX_FACES) } : s,
        );
      }
    } catch (err) {
      setToast(toUserMessage(err, 'Cattura annullata'));
    }
  }

  function removeLabelCapturePhoto(i: number): void {
    setLabelCapture((s) => {
      if (!s) return s;
      const photos = s.photos.filter((_, j) => j !== i);
      return photos.length === 0 ? null : { ...s, photos };
    });
  }

  async function confirmLabelCapture(): Promise<void> {
    if (!labelCapture || labelCapture.photos.length === 0) return;
    const { idx, photos } = labelCapture;
    setLabelCapture(null);
    await runLabelExtraction(idx, photos);
  }

  // Estrazione lotto/scadenza dalle foto etichetta (era dentro captureLineDetailMulti).
  async function runLabelExtraction(idx: number, images: string[]): Promise<void> {
    setLinePhotoBusy((s) => ({ ...s, [idx]: true }));
    try {
      // T-407: SALVA la foto etichetta (parità col Carico rapido): la carichiamo
      // su lot-photos e agganciamo il path alla riga → al commit finisce nel
      // payload stock_in → foods.current_lot_label_path. La foto resta salvata
      // anche se l'estrazione non riconosce nulla.
      if (profile?.active_tenant_id && images[0]) {
        try {
          const path = await uploadLotPhoto(profile.active_tenant_id, images[0]);
          patchLine(idx, { label_photo_path: path });
        } catch {
          /* upload non riuscito: procedo comunque con l'estrazione */
        }
      }
      const { data: sess } = await getSupabase().auth.getSession();
      const tok = sess.session?.access_token;
      if (!tok) throw new UserFacingError('Sessione scaduta. Esci e rientra.');
      const res = await fetch(
        `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/ai-vision-proxy`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // T-407: estrazione LEAN "solo lotto+scadenza" (no-thinking) → molto
            // più veloce e affidabile del food_package generico (che leggeva anche
            // allergeni/ingredienti/peso e spesso tornava null su lotto/scadenza).
            kind: 'lot_expiry',
            images,
            business_id: businessId ?? undefined,
          }),
        },
      );
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) {
        const code = (json.error as string) ?? `HTTP ${res.status}`;
        if (code === 'quota_exceeded') throw new UserFacingError('Quota OCR esaurita. Riprova più tardi.');
        throw new UserFacingError(`OCR non riuscito (codice: ${code}). Riprova.`);
      }
      // T-407: ai-vision-proxy (kind='lot_expiry') ritorna { result: { batch_lot, expiration_date } }
      const result = (json.result ?? json) as { batch_lot?: string | null; expiration_date?: string | null };
      const patch: { batch_lot?: string; expiration_date?: string } = {};
      if (result.batch_lot) patch.batch_lot = result.batch_lot;
      if (result.expiration_date) patch.expiration_date = result.expiration_date;
      if (patch.batch_lot || patch.expiration_date) {
        patchLine(idx, patch);
        const parts: string[] = [];
        if (patch.batch_lot) parts.push(`lotto ${patch.batch_lot}`);
        if (patch.expiration_date) parts.push(`scad. ${new Date(patch.expiration_date).toLocaleDateString('it-IT')}`);
        setToast(`Letto: ${parts.join(' · ')}`);
      } else {
        setToast('Niente riconosciuto. Riprova con foto più nitide (lotto e scadenza ben inquadrati).');
      }
    } catch (err) {
      setToast(toUserMessage(err, 'Errore OCR'));
    } finally {
      setLinePhotoBusy((s) => ({ ...s, [idx]: false }));
    }
  }

  // T-081 follow-up: path su `fiscal-photo-staging` Storage bucket quando il
  // file è troppo grande per il body POST diretto (>2 MB raw, evita il
  // limite ~6 MB di Supabase Edge Functions). Si popola in `ingestFile`
  // e viene riutilizzato sia per dry_run che per commit (l'edge function
  // scarica dallo staging e a fine commit lo cancella).
  const [imageStoragePath, setImageStoragePath] = useState<string | null>(null);

  // T-081 follow-up: soglia oltre la quale il file viene caricato su Storage
  // staging invece di essere inviato inline come base64. 2 MB raw ≈ 2.7 MB
  // base64+overhead → ben sotto il limite Supabase Edge ma sopra il quale
  // ha senso bypassare il body POST per affidabilità.
  const STAGING_THRESHOLD_BYTES = 2_000_000;

  // T-081 follow-up: uploada un file su `fiscal-photo-staging/<tenant>/<uuid>.<ext>`
  // usando il JWT user (policy DDL in migration 20260523190000 valida il
  // primo segmento del path contro `jwt_tenants()`). Ritorna il path o
  // lancia eccezione.
  async function uploadToStaging(b64: string, mime: string): Promise<string> {
    if (!profile?.active_tenant_id) throw new UserFacingError('Nessun cliente attivo. Cambia ambiente dalle Impostazioni.');
    // Decode base64 → bytes (richiesto da Supabase storage.upload).
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const ext = mime === 'application/pdf' ? 'pdf'
      : mime === 'image/png' ? 'png'
        : mime === 'image/webp' ? 'webp'
          : 'jpg';
    const path = `${profile.active_tenant_id}/${crypto.randomUUID()}.${ext}`;
    const { error } = await getSupabase().storage
      .from('fiscal-photo-staging')
      .upload(path, bytes, { contentType: mime, upsert: false });
    if (error) throw new UserFacingError(`Upload file fallito: ${error.message}. Riprova.`);
    return path;
  }

  async function runExtractionWith(b64: string, mime: string, context?: { businessId: string | null; docKind: DocKind }): Promise<void> {
    const request = ++extractionRequest.current;
    const extractionBusinessId = context ? context.businessId : businessId;
    if (!profile?.active_tenant_id) {
      setBusy(false);
      setToast('Nessun cliente attivo. Cambia ambiente dalle Impostazioni.'); return;
    }
    const extractionTenantId = profile.active_tenant_id;
    // T-067: business_id OBBLIGATORIO sia per DDT che per fattura. Senza
    // locale selezionato il documento finirebbe orfano sul tenant — confonde
    // gli archivi del consulente che switcha tra clienti.
    if (!extractionBusinessId) {
      setBusy(false);
      setToast('Seleziona prima il locale (ristorante/azienda) di destinazione');
      return;
    }
    setBusy(true);
    try {
      // T-081 follow-up: file > 2 MB → upload su staging Storage PRIMA della
      // chiamata edge. Bypassa il limite payload ~6 MB delle Edge Functions
      // sui PDF grandi. File piccoli (foto compresse) restano inline come
      // prima per minor latenza e meno round-trip.
      const rawBytes = Math.floor((b64.length * 3) / 4);
      // A new file must not reuse the previous render's staging path after resetWizard.
      let storagePathLocal: string | null = context ? null : imageStoragePath;
      if (!storagePathLocal && rawBytes > STAGING_THRESHOLD_BYTES) {
        setToast('Upload in corso…');
        try {
          storagePathLocal = await uploadToStaging(b64, mime);
          if (request !== extractionRequest.current) return;
          setImageStoragePath(storagePathLocal);
          void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
            dbg.info('capture-photo', 'staging_uploaded', {
              raw_bytes: rawBytes,
              mime,
              path: storagePathLocal,
            }),
          );
        } catch (uploadErr) {
          if (request !== extractionRequest.current) return;
          void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
            dbg.error('capture-photo', 'staging_upload_failed', {
              raw_bytes: rawBytes,
              mime,
              error: (uploadErr instanceof Error) ? uploadErr.message : String(uploadErr),
            }),
          );
          setBusy(false);
          setToast(toUserMessage(uploadErr, 'Upload file fallito'));
          return;
        }
      }
      const payload: Record<string, unknown> = {
        tenant_id: extractionTenantId,
        business_id: extractionBusinessId,
        image_mime: mime,
        doc_kind: context?.docKind ?? docKind,
        mode: 'dry_run',
      };
      if (storagePathLocal) {
        payload.image_storage_path = storagePathLocal;
      } else {
        payload.image_b64 = b64;
      }
      const json = await callEdge(payload);
      const dr = json as unknown as DryRunResponse;
      const extractedPlan = await applyStoredPackFactors(buildPlanFromDryRun(dr));
      if (request !== extractionRequest.current) return;
      setReviewTenantId(extractionTenantId);
      setDryRun(dr);
      setPlan(extractedPlan);
      setReviewStep('supplier');
      setToast(`Estrazione completata · ${dr.lines.length} righe trovate`);
    } catch (err) {
      if (request === extractionRequest.current) setToast(toUserMessage(err));
    } finally {
      if (request === extractionRequest.current) setBusy(false);
    }
  }

  async function runExtraction(): Promise<void> {
    if (!imageB64) {
      setToast('Carica una foto/PDF prima'); return;
    }
    await runExtractionWith(imageB64, imageMime);
  }

  // T-144: la conformità ora si conferma da un popup finale; il popup passa la
  // scelta come argomento per evitare di leggere lo stato React ancora stale.
  async function commitPlan(confArg?: 'yes' | 'no', notesArg?: string): Promise<void> {
    if (commitInFlight.current) return;
    commitInFlight.current = true;
    const commitLocation = history.location;
    setBusy(true);
    try {
      await commitPlanOnce(confArg, notesArg);
    } catch (error) {
      showImportFailure(toUserMessage(error));
    } finally {
      commitInFlight.current = false;
      setBusy(false);
      if (history.location !== commitLocation) setLoadAfterCommit((value) => value + 1);
    }
  }

  async function commitPlanOnce(confArg?: 'yes' | 'no', notesArg?: string): Promise<void> {
    const isCurrent = capture();
    // T-170 1d: in review-from-job non c'è imageB64 inline ma imageStoragePath
    // (le pagine su staging, ancora valide) — il server le rifetcha al commit.
    if (!plan || !profile?.active_tenant_id || (!imageB64 && !imageStoragePath && !importReviewId)) return;
    const commitDraft = await applyStoredPackFactors(plan);
    if (reviewOriginalRecovery.status !== 'not_needed' && reviewOriginalRecovery.status !== 'deferred') {
      setToast(reviewOriginalRecovery.status === 'checking'
        ? 'Sto verificando il documento originale.'
        : 'Recupera prima tutte le pagine del documento originale.');
      return;
    }
    if (!documentDraftFinalizable) {
      setToast(documentDraftLocked
        ? 'Questo carico è già in modifica su un altro dispositivo.'
        : 'Attendi il salvataggio della bozza condivisa prima di confermare.');
      return;
    }
    if (reviewTenantMismatch({
      activeTenantId: profile.active_tenant_id,
      reviewTenantId,
      imageStoragePath,
    })) {
      resetWizard();
      setImageB64(null);
      setOriginalFilename(null);
      setBusinessId(null);
      setToast('Cliente cambiato: riapri il documento dal cliente corretto.');
      history.replace('/documents');
      return;
    }
    // T-067: doppia difesa — anche il commit deve avere il business_id.
    // Se per qualche motivo l'utente è arrivato qui senza locale (es. ha
    // cambiato tenant durante la review), blocchiamo qui invece di salvare
    // sul tenant senza cassetto.
    if (!businessId) {
      setToast('Seleziona prima il locale (ristorante/azienda) di destinazione');
      return;
    }
    if (!commitDraft.supplier_id && !commitDraft.supplier_create.name.trim()) {
      setToast('Manca il nome del fornitore'); return;
    }
    if (!commitDraft.document.number || !commitDraft.document.date) {
      setToast('Manca numero/data documento'); return;
    }
    const unresolved = commitDraft.lines.findIndex(identityNeedsReview);
    if (unresolved >= 0) {
      setReviewStep('summary');
      showImportFailure('Rivedi gli articoli evidenziati: scegli un alimento esistente oppure conferma un articolo distinto.', unresolved);
      return;
    }
    const validLines = commitDraft.lines.filter((l) => !l.skip);
    if (validLines.length === 0) {
      setToast('Nessuna riga da importare'); return;
    }
    // L'anagrafica può essere stata corretta dal Magazzino mentre questa
    // review (o il job AI) era già aperta. Il risultato dry-run è uno snapshot,
    // quindi per il gate finale rileggiamo le UdM server-authoritative invece
    // di bloccare sul vecchio master rimasto nella cache locale.
    const existingFoodIds = Array.from(new Set(
      validLines.flatMap((line) => line.food_id ? [line.food_id] : []),
    ));
    const authoritativeFoods = new Map<string, {
      id: string;
      name: string;
      default_uom: string;
    }>();
    if (existingFoodIds.length > 0) {
      const { data: liveFoods, error: liveFoodsError } = await getSupabase()
        .from('foods')
        .select('id,name,default_uom')
        .in('id', existingFoodIds);
      if (!liveFoodsError) {
        for (const food of liveFoods ?? []) {
          authoritativeFoods.set(food.id, food);
        }
      }
    }
    for (const l of validLines) {
      const originalIndex = commitDraft.lines.indexOf(l);
      const productLabel = l.food_create.name.trim() || l.raw_description?.trim() || `Riga ${originalIndex + 1}`;
      if (!isImportLineCommitReady(l)) {
        setReviewStep('lines');
        setCurrentLineIdx(originalIndex);
        showImportFailure(
          `“${productLabel}”${l.supplier_code ? ` (codice ${l.supplier_code})` : ''}: controlla quantità, unità di misura e valori economici.`,
          originalIndex,
        );
        return;
      }
      if (!l.food_id && !l.food_create.name.trim()) {
        setReviewStep('lines');
        setCurrentLineIdx(originalIndex);
        showImportFailure('Questa riga non ha un alimento associato. Scegline uno esistente oppure creane uno nuovo.', originalIndex);
        return;
      }
      if (
        l.conversion_source === 'user_confirmed'
        && !l.conversion_confirmed
      ) {
        setReviewStep('lines');
        setCurrentLineIdx(originalIndex);
        showImportFailure(
          `“${productLabel}”: conferma la quantità reale in ${l.uom || 'questa UdM'} `
          + 'oppure applica un fattore collo: la quantità del documento resta '
          + 'immutata e non può essere reinterpretata automaticamente.',
          originalIndex,
        );
        return;
      }
      if (l.food_id) {
        const existingFood = authoritativeFoods.get(l.food_id);
        if (
          existingFood
          && !isCorrectingImport
          && normalizeUom(existingFood.default_uom) !== normalizeUom(l.uom)
        ) {
          if (originalIndex >= 0) setCurrentLineIdx(originalIndex);
          setReviewStep('lines');
          showImportFailure(
            `"${existingFood.name}" usa ${existingFood.default_uom} come UdM di magazzino. `
            + 'Scegli un altro alimento oppure usa “Nuovo” se questa fornitura '
            + 'ha davvero una diversa unità di magazzino.',
            originalIndex,
          );
          return;
        }
      }
    }
    // T-294B (fix #1, gate fail-closed): una riga estratta a COLLI senza fattore
    // applicato caricherebbe il NUMERO DI COLLI come unità base (giacenza
    // errata, esattamente il bug che T-294B deve eliminare). Blocca finché non
    // viene convertita (o messa in skip).
    const pendingPack = validLines.filter((l) => l.pack_needs_factor && !l.pack_factor_applied);
    if (pendingPack.length > 0) {
      const originalIndex = commitDraft.lines.indexOf(pendingPack[0]);
      setReviewStep('lines');
      setCurrentLineIdx(originalIndex);
      showImportFailure(
        `${pendingPack.length === 1 ? '“' + (pendingPack[0].food_create.name || 'Questa riga') + '” deve' : pendingPack.length + ' righe devono'} essere convertita${pendingPack.length === 1 ? '' : 'e'} prima dell’importazione. Inserisci il contenuto per collo e premi “Converti”.`,
        originalIndex,
      );
      return;
    }
    setBusy(true);
    try {
      const planPayload = {
        supplier_id: commitDraft.supplier_id,
        supplier_create_ack: commitDraft.supplier_create_ack === true,
        supplier_create: commitDraft.supplier_id ? undefined : {
          name: commitDraft.supplier_create.name.trim(),
          piva: commitDraft.supplier_create.piva || null,
          codice_fiscale: commitDraft.supplier_create.codice_fiscale || null,
          category: commitDraft.supplier_create.category,
          email: commitDraft.supplier_create.email || null,
          phone: commitDraft.supplier_create.phone || null,
          address: commitDraft.supplier_create.address || null,
          country: commitDraft.supplier_create.country || null,
        },
        document: {
          number: commitDraft.document.number,
          date: commitDraft.document.date,
          total_amount: commitDraft.document.total_amount ? Number(commitDraft.document.total_amount) : null,
          type: commitDraft.document.type,
          currency: commitDraft.document.currency,
        },
        lines: commitDraft.lines.filter((l) => !l.skip).map((l) => {
          if (!isImportLineCommitReady(l)) {
            throw new UserFacingError(
              'Quantità, unità di misura o valori economici non validi in una riga',
            );
          }
          const stockQty = Number(l.qty);
          const sourceQty = l.source_qty;
          const sourceUom = l.source_uom.trim();
          const sourceUnitPrice = l.source_unit_price;
          const sourceLineTotal = l.source_line_total;
          // Il fattore serializzato deve conservare esattamente la quantità
          // confermata; `pack_factor` resta separato come memoria fornitore.
          const conversionFactor =
            sourceQty > 0 && stockQty > 0 ? stockQty / sourceQty : null;
          const stockUnitCost = deriveStockUnitCost({
            sourceQty,
            sourceUnitPrice,
            sourceLineTotal,
            stockQty,
          });
          return {
          parsed_index: l.parsed_index,
          food_id: l.food_id,
          food_create: l.food_id ? undefined : {
            name: l.food_create.name.trim(),
            // La scelta finale dell'operatore è la coordinata stock e deve
            // coincidere con la default_uom del nuovo master.
            default_uom: l.uom.trim(),
            storage_type: l.food_create.storage_type,
            category: l.food_create.category,
          },
          // Coordinate derivate del movimento di magazzino.
          qty: stockQty,
          uom: l.uom.trim(),
          // Coordinate economiche/documentali immutabili. Additive per i
          // server nuovi; gli alias legacy sotto contengono gli stessi valori.
          source_qty: sourceQty,
          source_uom: sourceUom,
          source_unit_price: sourceUnitPrice,
          source_line_total: sourceLineTotal,
          currency: (l.currency || commitDraft.document.currency || 'EUR').trim().toUpperCase() || 'EUR',
          stock_unit_cost: stockUnitCost,
          conversion_factor: conversionFactor,
          conversion_source: l.conversion_source
            ?? (conversionFactor !== 1 || normalizeUom(sourceUom) !== normalizeUom(l.uom)
              ? 'user_confirmed'
              : 'identity'),
          conversion_confirmed: l.conversion_confirmed,
          batch_lot: l.batch_lot || null,
          expiration_date: l.expiration_date || null,
          // is_frozen derivato direttamente da storage_type — il toggle UI
          // separato è stato rimosso 2026-05-04 (era ridondante).
          is_frozen: l.food_create.storage_type === 'surgelato',
          notify_expiration: l.notify_expiration,
          storage_unit_id: l.storage_unit_id || null,
          supplier_sku: l.supplier_sku || null,
          // T-198: codice articolo + raw_description per dedup e alias per-codice.
          supplier_code: l.supplier_code || null,
          raw_description: l.raw_description || null,
          // T-131: registra il carico senza sovrascrivere il lotto attivo
          // (solo se c'è un food esistente — su nuovo alimento non ha senso).
          keep_current_lot: !!l.food_id && l.keep_current_lot,
          // La chiusura del lotto attivo entra nel commit atomico DDT come
          // secondo Stock Command; non viene più accodata dopo la response.
          replace_active_lot: !!l.food_id && l.replace_active_lot,
          // T-284 (Food Cost): propaga prezzo/totale riga al commit.
          unit_price: sourceUnitPrice,
          line_total: sourceLineTotal,
          // T-344: se il pack-factor è già stato applicato lato client (qty/uom
          // sono nell'unità base scelta dall'utente), dillo al server così non
          // ri-converte count→peso dalla descrizione (bug dip caricate in kg).
          pack_factor: l.pack_factor,
          extracted_uom: l.extracted_uom,
          ai_description: l.ai_description,
          match_confirmed: l.match_confirmed === true || manuallyMatchedLines.has(commitDraft.lines.indexOf(l)),
          new_food_confirmed: l.new_food_confirmed === true,
          pack_factor_applied: l.pack_factor_applied,
          pack_base_uom: l.pack_base_uom,
          // T-375: conferme esplicite dei gate a semaforo. Il server ri-valida e
          // rifiuta (409 unresolved_gate) le righe con gate residuo NON confermato.
          gates_ack: l.gates_ack,
          lot_ack: l.lot_ack,
          // T-407: foto etichetta salvata (parità col Carico rapido).
          label_photo_path: l.label_photo_path || null,
          };
        }),
      };
      // T-081 follow-up: se il file è già su staging (dry_run ha uploadato),
      // riusiamo il path. L'edge function lo scarica una sola volta, salva il
      // documento permanente, e cancella lo staging dopo il commit.
      const commitPayload: Record<string, unknown> = {
        tenant_id: profile.active_tenant_id,
        business_id: businessId ?? undefined,
        image_mime: imageMime,
        doc_kind: docKind,
        mode: 'commit',
        plan: planPayload,
        // T-668: se la bozza proviene da una review staff, l'RPC atomica
        // approva quella stessa richiesta dopo le correzioni del revisore.
        review_request_id: importReviewId ?? undefined,
        // T-179: id del job di provenienza → commit idempotente lato server
        // (se già committed, niente staging_download_failed su re-commit).
        document_job_id: jobReviewId ?? undefined,
        // T-375: questo client supporta la schermata a semaforo (sa risolvere i
        // gate e mandare gates_ack/lot_ack) → l'edge può applicare l'enforcement
        // 409 unresolved_gate. I client vecchi non mandano il flag (legacy).
        client_supports_gates: true,
        // T-519: commitPlan parte soltanto dal popup finale. Dopo questo consenso
        // gli avvisi semantici sono responsabilità operativa accettata; il server
        // continua comunque a validare quantità, UdM, valori, tenant e atomicità.
        operator_review_confirmed: !automaticImportRequested,
        automatic_import: automaticImportRequested,
        device_id: await getDeviceId(),
        operator_assertion: getActiveOperatorAssertion() ?? undefined,
        work_draft_guard: documentWorkDraft.guard ?? undefined,
      };
      // T-671: una review riaperta può avere ancora il payload inline salvato
      // dal server, mentre il path di staging è già stato ripulito. Per una
      // review preferiamo quindi la copia inline; sul primo import manteniamo
      // il path per non gonfiare il POST dei file grandi.
      if (importReviewId && imageB64 && imageB64.length <= 3_000_000) {
        commitPayload.image_b64 = imageB64;
      } else if (imageStoragePath) {
        commitPayload.image_storage_path = imageStoragePath;
      } else {
        commitPayload.image_b64 = imageB64;
      }
      const json = await callEdge({
        ...commitPayload,
        // T-061: controllo conformità al ricevimento. Se la card UI è in stato
        // 'pending' (non dovrebbe accadere perché canCommit è false), passiamo
        // null e l'edge non crea l'event — il documento resta senza badge.
        conformity: (() => {
          const eff = confArg ?? (conformity === 'pending' ? null : conformity);
          if (!eff) return null;
          const notes = notesArg ?? conformityNotes;
          return {
            conform: eff === 'yes',
            notes: eff === 'no' ? notes.trim() : null,
            disclaimer_items: Array.from(CONFORMITY_DISCLAIMER_ITEMS),
            // T-291: temperatura di ricevimento per articolo (se il flag è ON e
            // l'utente ha compilato almeno una temperatura). Additivo: l'edge la
            // persiste in conformity_check.item_temps solo se presente.
            item_temps: buildItemTemps(),
          };
        })(),
        // T-074 follow-up: se l'utente ha attivato la modalità sostituzione
        // dal banner anti-duplicato, passiamo l'id del vecchio documento.
        // L'edge fa rollback (cancellazione + stock_out compensativi) e
        // poi commit normale del nuovo.
        // T-201: in ri-estrazione forziamo la sostituzione del doc di partenza
        // (indipendente dal rilevamento duplicato, che potrebbe non combaciare se
        // l'AI rilegge numero/data diversamente). Review fix: onoriamo il target
        // SOLO se siamo ancora in modalità ri-estrazione (URL ?reextract=1) o in
        // correzione import (URL ?correct_import=1, T-375 P2), così un target
        // stantio non può sostituire per errore un altro documento.
        replace_document: (reextractTarget && (() => {
          const sp = new URLSearchParams(location.search);
          return sp.get('reextract') === '1' || sp.get('correct_import') === '1';
        })())
          ? { id: reextractTarget.id, source: reextractTarget.source }
          : (replaceMode && dryRun?.existing_document)
            ? { id: dryRun.existing_document.id, source: dryRun.existing_document.source }
            : null,
      });
      const r = json as unknown as CommitResponse;
      // T-179: il server ha rilevato che questo job è già stato importato
      // (re-commit / retry / re-ingresso). Niente errore, niente doppione:
      // chiudiamo il draft, marchiamo il job e usciamo con un messaggio chiaro.
      if ((r as { already_committed?: boolean }).already_committed) {
        if (jobReviewId) { try { await markJobCommitted(jobReviewId); } catch { /* best-effort */ } }
        if (jobReviewDraftId) { try { await finishDraft(jobReviewDraftId); } catch { /* best-effort */ } }
        try { await documentWorkDraft.complete(); } catch { /* il commit server resta autoritativo */ }
        setToast('Questo documento è già stato importato.');
        setBusy(false);
        setTimeout(() => { if (isCurrent()) history.replace('/documents'); }, 900);
        return;
      }
      if (r.status === 'awaiting_review' && r.review_request_id) {
        try { await documentWorkDraft.complete(); } catch { /* richiesta gia registrata */ }
        setToast('Documento inviato in revisione. Il magazzino non è stato modificato.');
        setBusy(false);
        setTimeout(() => { if (isCurrent()) history.replace('/documents'); }, 1_200);
        return;
      }
      const createdN = Object.keys(r.created_foods ?? {}).length;
      setToast(
        `Importato! ${r.lines_accepted} righe · scrivo in magazzino…` +
        (createdN > 0 ? ` · ${createdN} nuovi alimenti creati` : ''),
      );

      // ====================================================================
      // Writeback diretto: il server ha già scritto tutto sul DB. Anche se il
      // sync-pull avesse problemi (Funnel instabile, fetch race in WebView),
      // applichiamo subito i record nella cache locale così la UI vede tutto.
      // ====================================================================
      if (r.writeback) {
        try {
          const db = await openLocalDb(null);
          // Foods
          for (const f of r.writeback.foods ?? []) {
            const tenantId = (f.tenant_id as string | null | undefined) ?? null;
            await db.run(
              `INSERT INTO masters_local(table_name,id,tenant_id,version,row,archived,updated_at)
               VALUES(?,?,?,?,?,0,datetime('now'))
               ON CONFLICT(table_name,id) DO UPDATE SET
                 row = excluded.row,
                 version = excluded.version,
                 updated_at = excluded.updated_at`,
              ['foods', f.id, tenantId, 0, JSON.stringify(f)],
            );
          }
          // Supplier (se appena creato/aggiornato)
          if (r.writeback.supplier) {
            const s = r.writeback.supplier;
            const tenantId = (s.tenant_id as string | null | undefined) ?? null;
            await db.run(
              `INSERT INTO masters_local(table_name,id,tenant_id,version,row,archived,updated_at)
               VALUES(?,?,?,?,?,0,datetime('now'))
               ON CONFLICT(table_name,id) DO UPDATE SET
                 row = excluded.row, version = excluded.version, updated_at = excluded.updated_at`,
              ['suppliers', s.id, tenantId, 0, JSON.stringify(s)],
            );
          }
          // Events (stock_in)
          const wbEvents = r.writeback.events ?? [];
          let wbServerIdFallbacks = 0;
          const wbKeys: string[] = [];
          for (const e of wbEvents) {
            const key = confirmedEventLocalIdentity(e);
            // T-311: key the local row on client_uuid — EXACTLY like queueEvent
            // and pull() (see eventsLocalKey.ts). Keying on the server `e.id`
            // here made the writeback row and the later sync-pull row two
            // distinct rows on the importing device → doubled giacenza on slow
            // devices. The shared key makes them the same row (INSERT OR REPLACE).
            if (key.source === 'server_id') wbServerIdFallbacks++;
            wbKeys.push(key.id);
            await db.run(
              `INSERT OR REPLACE INTO events_local(id, server_id, tenant_id, kind, business_id, client_ts, server_ts, rev, payload, self_hash, prev_hash)
               VALUES(?,?,?,?,?,?,?,?,?,?,?)`,
              [
                key.id,
                key.serverId,
                e.tenant_id,
                e.kind,
                (e.business_id as string | null) ?? null,
                (e.client_ts as string | null) ?? new Date().toISOString(),
                (e.server_ts as string | null) ?? null,
                (e.server_rev as number | null) ?? null,
                JSON.stringify(e.payload),
                (e.self_hash as string | null) ?? null,
                (e.prev_hash as string | null) ?? null,
              ],
            );
          }
          await flushLocalDb();
          // T-312/#4: traccia il writeback (il writer che causò T-311). Logga le
          // PK scritte e quanti fallback su server_id (0 = corretto). Se in futuro
          // ricompare il doppio carico, qui si vede CHE chiavi ha usato l'import.
          if (wbEvents.length > 0) {
            void import('../../lib/debug/debugLog.js').then(({ dbg }) =>
              dbg[wbServerIdFallbacks > 0 ? 'warn' : 'info'](
                'events_local',
                wbServerIdFallbacks > 0 ? 'write_keyed_on_server_id' : 'import_writeback',
                {
                  writer: 'import_writeback',
                  count: wbEvents.length,
                  server_id_fallbacks: wbServerIdFallbacks,
                  ids: wbKeys.slice(0, 20),
                },
              ),
            );
          }
        } catch (wbErr) {
          console.warn('[capture] writeback locale fallito', wbErr);
        }
      }

      // Tick best-effort (non blocchiamo se fallisce). Aiuta il drain dei
      // pending eventi locali (se ce ne sono) ma non è più critico.
      void getSyncEngine().tick().catch(() => undefined);

      // T-068: popola la memoria fornitore↔alimento. Per OGNI riga
      // committata risolviamo (raw_description, food_id) e chiamiamo
      // l'RPC `upsert_supplier_item_alias`. Source='manual' per le righe
      // che l'utente ha *attivamente* scelto dal picker, 'ai_match' per
      // le altre. Errori non-bloccanti: il commit dell'import è già
      // andato a buon fine, gli alias sono valore aggiunto.
      const resolvedSupplierId = r.supplier_id ?? commitDraft.supplier_id ?? null;
      let aliasFailed = false; // fix #10: esito memoria, riportato nel toast finale
      let skipMemoryFailed = false;
      if (resolvedSupplierId) {
        try {
          if (commitDraft.supplier_lot_preference) await persistSupplierLotPreference(resolvedSupplierId, commitDraft.supplier_lot_preference.no_lot);
          await Promise.all(
            commitDraft.lines.filter(remembersImportSkip).map((line) => (
              persistImportSkip(line, true, resolvedSupplierId)
            )),
          );
        } catch (skipError) {
          console.warn('[capture] memoria Salta import fallita', skipError);
          skipMemoryFailed = true;
        }
        try {
          const sb = getSupabase();
          if (!r.alias_memory_persisted) await Promise.all(
            commitDraft.lines.map(async (l, i) => {
              if (l.skip) return;
              // La chiave deve essere quella della riga appena confermata,
              // non un indice della risposta dry-run che può precedere una
              // correzione o il consolidamento delle righe.
              const rawDesc = (
                l.raw_description
                ?? l.ai_description
                ?? dryRun?.lines[i]?.parsed.description
                ?? ''
              ).trim();
              if (!rawDesc) return;
              const foodId = l.food_id
                // fix #12: il server chiavizza created_foods sul nome TRIMMATO.
                ?? (l.food_create?.name ? r.created_foods?.[l.food_create.name.trim()] : null)
                ?? null;
              if (!foodId) return;
              const { error: aliasError } = await sb.rpc('upsert_supplier_item_alias', {
                p_tenant_id: profile.active_tenant_id,
                p_supplier_id: resolvedSupplierId,
                p_raw_description: rawDesc,
                p_food_id: foodId,
                p_source: l.match_confirmed || l.new_food_confirmed || manuallyMatchedLines.has(i) ? 'manual' : 'ai_match',
                // T-198: memorizza anche il codice articolo come chiave di match
                // deterministica per gli import successivi dello stesso fornitore.
                p_supplier_code: l.supplier_code || null,
                // T-294B: memorizza il FATTORE DI CONVERSIONE collo→base, così
                // il prossimo carico dello stesso articolo si converte da solo.
                p_pack_factor: l.pack_factor ?? null,
                p_pack_uom: l.pack_factor != null ? (l.extracted_uom ?? null) : null,
                p_base_uom: l.pack_factor != null ? (l.pack_base_uom ?? null) : null,
              });
              if (aliasError) throw aliasError;
            }),
          );
        } catch (aliasErr) {
          // T-294B (hardening affidabilità): il salvataggio della memoria
          // fornitore era "best-effort silenzioso" → se falliva, la memoria non
          // si popolava e l'errore si ripeteva al carico dopo. Ora avvisiamo.
          // eslint-disable-next-line no-console
          console.warn('[capture] popolazione alias fallita', aliasErr);
          aliasFailed = true; // fix #10: NON sovrascrivere subito (lo farebbe il toast di successo) → riportato nel toast finale
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      // T-204: la lista Documenti legge da una query dedicata (['fiscal_archive'])
      // + i widget dashboard (['today_recent_docs']) e la tracciabilità
      // (['fiscal_docs_meta']) — non coperti da 'masters'. Senza queste, dopo
      // l'import il documento non compariva finché non si chiudeva/riapriva
      // l'app. Invalidiamo subito.
      await queryClient.invalidateQueries({ queryKey: ['fiscal_archive'] });
      await queryClient.invalidateQueries({ queryKey: ['fiscal_docs_meta'] });
      await queryClient.invalidateQueries({ queryKey: ['today_recent_docs'] });
      // T-170 1d: se la review proveniva da un job (?jobId=), marcalo committed
      // così esce da "In elaborazione" + chiudi il draft locale.
      if (jobReviewId) {
        try { await markJobCommitted(jobReviewId); } catch { /* best-effort */ }
        if (jobReviewDraftId) {
          try { await finishDraft(jobReviewDraftId); } catch { /* draft cleanup best-effort */ }
        }
      }
      try { await documentWorkDraft.complete(); } catch { /* import gia atomico lato server */ }
      setToast(
        `Importato! ${r.lines_accepted} righe in magazzino` +
        (createdN > 0 ? ` · ${createdN} nuovi alimenti creati` : '') +
        (aliasFailed ? ' · ⚠️ memoria fornitore non aggiornata (match/fattori): verrà richiesta di nuovo al prossimo carico' : '') +
        (skipMemoryFailed ? ' · ⚠️ alcune preferenze del fornitore non sono state memorizzate' : ''),
      );
      setTimeout(
        () => { if (isCurrent()) history.replace(automaticImportRequested ? '/documents?automaticImported=1' : '/documents'); },
        800,
      );
    } catch (err) {
      let failureLineIndex = err instanceof ImportLineError ? err.planLineIndex : null;
      if (err instanceof ExistingFoodUomMismatchError) {
        const lineIndex = commitDraft.lines.findIndex((line) => (
          line.parsed_index === err.parsedLine
          || (err.foodId != null && line.food_id === err.foodId)
        ));
        if (lineIndex >= 0) {
          setReviewStep('lines');
          setCurrentLineIdx(lineIndex);
          failureLineIndex = lineIndex;
        }
      }
      if (isCurrent() && automaticImportRequested && jobReviewId) {
        history.replace(`/documents/capture?jobId=${jobReviewId}`);
        automaticCommitStartedRef.current = null;
      }
      showImportFailure(toUserMessage(err), failureLineIndex);
    }
  }

  // Helper updaters per commitDraft.
  function patchPlan(p: Partial<PlanDraft>): void {
    setPlan((prev) => {
      if (!prev) return prev;
      if ('supplier_id' in p && p.supplier_id !== prev.supplier_id) {
        return { ...prev, ...p, supplier_lot_preference: undefined, lines: applySupplierLotPreference(prev.lines, false) };
      }
      return { ...prev, ...p };
    });
  }
  function patchSupplierCreate(p: Partial<PlanDraft['supplier_create']>): void {
    setPlan((prev) => prev ? { ...prev, supplier_create_ack: false, supplier_create: { ...prev.supplier_create, ...p } } : prev);
  }
  function patchDocument(p: Partial<PlanDraft['document']>): void {
    setPlan((prev) => prev ? { ...prev, document: { ...prev.document, ...p } } : prev);
  }
  function patchLine(idx: number, p: Partial<PlanLineDraft>): void {
    if (p.uom) {
      const selectedUom = p.uom;
      setPackBaseDraft((current) => {
        const next = { ...current, [idx]: selectedUom };
        if (isCorrectingImport && plan?.lines[idx]?.food_id) {
          plan.lines.forEach((line, i) => {
            if (line.food_id === plan.lines[idx].food_id) next[i] = selectedUom;
          });
        }
        return next;
      });
    }
    setPlan((prev) => {
      if (!prev) return prev;
      const lines = patchImportLineDraft(prev.lines, idx, p, isCorrectingImport);
      return { ...prev, lines };
    });
  }
  function addManualImportLine(): void {
    if (!plan) return;
    const parsedIndex = plan.lines.reduce(
      (max, line) => Math.max(max, line.parsed_index),
      -1,
    ) + 1;
    const manualLine: PlanLineDraft = {
      parsed_index: parsedIndex,
      raw_description: null,
      supplier_code: null,
      food_id: null,
      food_create: {
        name: '',
        default_uom: 'kg',
        storage_type: 'fresco',
        category: 'altro',
        allergens: [],
      },
      qty: '1',
      uom: 'kg',
      batch_lot: buildLotFallback(docKind, plan.document.number, plan.document.date),
      expiration_date: '',
      is_frozen: false,
      notify_expiration: true,
      storage_unit_id: '',
      supplier_sku: '',
      skip: false,
      replace_active_lot: false,
      keep_current_lot: false,
      source_qty: 1,
      source_uom: 'kg',
      source_unit_price: null,
      source_line_total: null,
      currency: plan.document.currency || 'EUR',
      unit_price: null,
      line_total: null,
      extracted_uom: 'kg',
      pack_needs_factor: false,
      pack_factor: null,
      pack_base_uom: null,
      pack_factor_applied: false,
      conversion_factor: 1,
      conversion_source: 'identity',
      conversion_confirmed: true,
      ai_description: null,
      validation: null,
      gates_ack: true,
      lot_ack: true,
      label_photo_path: null,
    };
    setPlan({ ...plan, lines: [...plan.lines, manualLine] });
    setCurrentLineIdx(plan.lines.length);
    setDetailsOpen(true);
    setImportFailure(null);
  }
  function showImportFailure(message: string, lineIndex: number | null = null): void {
    setToast(null);
    setImportFailure({ message, lineIndex });
  }
  async function persistImportSkip(
    line: PlanLineDraft,
    skip: boolean,
    supplierIdOverride?: string | null,
  ): Promise<void> {
    const supplierId = supplierIdOverride ?? plan?.supplier_id ?? null;
    const tenantId = profile?.active_tenant_id ?? null;
    const description = line.ai_description?.trim()
      || line.raw_description?.trim()
      || line.food_create.name.trim();
    if (!supplierId || !tenantId || (!description && !line.supplier_code)) return;
    const { error } = await getSupabase().rpc('set_supplier_item_import_skip', {
      p_tenant_id: tenantId,
      p_supplier_id: supplierId,
      p_raw_description: description,
      p_supplier_code: line.supplier_code || null,
      p_skip: skip,
    });
    if (error) {
      const lineIndex = plan?.lines.indexOf(line) ?? -1;
      throw new ImportLineError(
        `Non sono riuscito a ${skip ? 'salvare' : 'rimuovere'} la scelta “Salta import” per “${line.food_create.name || description}”${line.supplier_code ? ` (codice ${line.supplier_code})` : ''}.`,
        lineIndex >= 0 ? lineIndex : null,
      );
    }
  }
  async function confirmDistinctFood(idx: number): Promise<void> {
    if (!canCreateFoods || !plan?.lines[idx]) return;
    const line = plan.lines[idx];
    const confirmed = await confirmDialog(
      `Creare un articolo distinto per “${line.food_create.name}”? La scelta verrà ricordata per questo articolo del fornitore. Le giacenze esistenti non saranno unite.`,
      { header: 'Conferma articolo distinto', okText: 'Crea distinto', cancelText: 'Annulla' },
    );
    if (!confirmed) return;
    patchLine(idx, { food_id: null, new_food_confirmed: true, match_confirmed: false, identity_error: false,
      skip: false, skip_once: false, keep_current_lot: false, replace_active_lot: false });
  }

  function patchLineFoodCreate(idx: number, p: Partial<PlanLineDraft['food_create']>): void {
    setPlan((prev) => {
      if (!prev) return prev;
      const lines = prev.lines.slice();
      lines[idx] = { ...lines[idx], new_food_confirmed: p.name !== undefined && p.name !== lines[idx].food_create.name ? false : lines[idx].new_food_confirmed, food_create: { ...lines[idx].food_create, ...p } };
      return { ...prev, lines };
    });
  }

  // T-289: applica/rimuove la normalizzazione dei nomi su tutte le righe della
  // bozza corrente (azione dal toggle). ON ⇒ nome pulito+capitalizzato; OFF ⇒
  // testo grezzo del DDT (raw_description). Preferenza persistita per-device.
  function applyNameNormalization(on: boolean): void {
    try { localStorage.setItem('trackeat.normalize_article_names', on ? 'on' : 'off'); } catch { /* noop */ }
    setNormalizeNames(on);
    setPlan((prev) => {
      if (!prev) return prev;
      const lines = prev.lines.map((l) => {
        const raw = (l.raw_description ?? l.food_create.name) || '';
        const cleaned = cleanFoodName(raw);
        const nextName = on
          ? (cleaned ? cleaned.replace(/\b\w/g, (c) => c.toUpperCase()) : raw)
          : raw;
        if (!nextName || nextName === l.food_create.name) return l;
        return { ...l, food_create: { ...l.food_create, name: nextName } };
      });
      return { ...prev, lines };
    });
  }

  // T-291: costruisce item_temps[{food_id,temp_c}] dalla mappa itemTemps.
  // Solo righe non-skippate con food_id esistente e temperatura numerica.
  function buildItemTemps(): { food_id: string; temp_c: number }[] | undefined {
    if (!receivingTempPerItem || !plan) return undefined;
    const out: { food_id: string; temp_c: number }[] = [];
    for (const l of plan.lines) {
      if (l.skip || !l.food_id) continue;
      const raw = itemTemps[l.parsed_index];
      if (raw == null || raw.trim() === '') continue;
      const t = Number(raw.replace(',', '.'));
      if (!Number.isFinite(t)) continue;
      out.push({ food_id: l.food_id, temp_c: t });
    }
    return out.length > 0 ? out : undefined;
  }

  // T-478: fornitore attualmente collegato — dal match del server (dryRun) o, se
  // assente (es. correzione import), risolto dall'anagrafica via supplier_id.
  const linkedSupplierId = plan?.supplier_id ?? null;
  const linkedSupplier = linkedSupplierId
    ? (dryRun?.supplier.existing ?? cachedSuppliers.find((s) => s.id === linkedSupplierId) ?? null)
    : null;
  const importQueueItems: ImportQueueItem[] = plan
    ? plan.lines.map((line, index) => {
      const identityReview = identityNeedsReview(line, index);
      const ready = isLineReady(line, index);
      const liveFood = line.food_id
        ? tenantFoods.find((food) => food.id === line.food_id)
        : null;
      const title = liveFood?.name
        ?? line.food_create.name
        ?? line.raw_description
        ?? `Riga ${index + 1}`;
      return {
        index,
        title,
        meta: line.skip
          ? 'Ignorato'
          : !ready
            ? identityReview ? 'Scegli dove caricarlo' : 'Da rivedere'
            : `${line.qty} ${line.uom} · pronto`,
        status: line.skip ? 'skipped' : ready ? 'ready' : 'review',
      };
    })
    : [];

  if (automaticImportRequested) {
    return (
      <IonPage>
        <IonContent fullscreen>
          {importFailure && (
            <ImportFailureBanner
              failure={importFailure}
              reportText={importFailureReport}
              onClose={() => setImportFailure(null)}
              safeArea
            />
          )}
          <div
            role="status"
            aria-live="polite"
            style={{
              minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24,
              background: 'radial-gradient(circle at 50% 28%, rgba(99,102,241,.18), transparent 42%), var(--ion-background-color)',
            }}
          >
            <div style={{ textAlign: 'center', maxWidth: 360 }}>
              <div style={{
                width: 72, height: 72, borderRadius: 22, margin: '0 auto 20px',
                display: 'grid', placeItems: 'center', color: '#fff',
                background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                boxShadow: '0 16px 40px rgba(79,70,229,.28)',
              }}>
                <AiRobotIcon size={38} />
              </div>
              <h1 style={{ margin: '0 0 8px', fontSize: 24 }}>TrackEAT AI sta importando</h1>
              <p style={{ margin: '0 0 18px', color: 'var(--ion-color-medium-shade)', lineHeight: 1.5 }}>
                Fornitore e codici articolo sono già conosciuti. Sto registrando il DDT e aggiornando il magazzino.
              </p>
              <IonSpinner name="dots" color="primary" />
            </div>
          </div>
          <IonToast isOpen={!!toast} message={toast ?? ''} duration={3500} onDidDismiss={() => setToast(null)} />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <>{quickLabel ? <IonButton onClick={() => history.replace('/documents')}>Indietro</IonButton> : <IonBackButton defaultHref="/documents" />}</>
          </IonButtons>
          <IonTitle>{plan ? 'Conferma importazione' : quickLabel ? 'Scontrino e confezioni' : 'Foto fattura / DDT'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className={plan ? 'te-import-review-page' : 'ion-padding'}>
        {importFailure && (
          <ImportFailureBanner
            failure={importFailure}
            reportText={importFailureReport}
            onClose={() => setImportFailure(null)}
          />
        )}
        {/* ============ STEP 1 (redesign) — gerarchia chiara ============
            1. InfoBar "Stai caricando per: Cliente / Locale" (compatta)
            2. Toggle DDT / Fattura come 2 card grandi (hero)
            3. Dropzone unica centrale (no più 3 tile multi-colore)
            4. Indicatore stato inline (busy/error), no CTA grande in basso
            La logica (qualità originale + auto-trigger + commit) è invariata. */}
        {!plan && (() => {
          const activeBiz = tenantBusinesses.find((b) => b.id === businessId);
          const missingLocale = !businessId;
          const missingTenant = !profile?.active_tenant_id;
          const localeOk = !missingLocale && !missingTenant;
          return (
            <>
              {/* ─── 1. InfoBar contestuale ───
                  T-073: il bottone "Cambia ›" e il click sull'InfoBar si
                  attivano solo se c'è almeno una scelta da fare:
                  - più di un business nel tenant attivo, OPPURE
                  - più di un tenant nelle membership dell'utente, OPPURE
                  - errore (missing tenant/locale → tap per andare a impostazioni).
                  Altrimenti niente "Cambia" e niente cursore pointer:
                  l'utente ha un solo ristorante, non c'è nulla da scegliere. */}
              {(() => {
                const hasMultipleBusinesses = tenantBusinesses.length > 1;
                const hasMultipleTenants = (profile?.tenants?.length ?? 0) > 1;
                const showSwitcher = !quickLabel && (!localeOk || hasMultipleBusinesses || hasMultipleTenants);
                return (
                  <div
                    role={showSwitcher ? 'button' : undefined}
                    onClick={showSwitcher ? () => setLocalePickerOpen(true) : undefined}
                    style={{
                      margin: '0 0 18px',
                      padding: '10px 12px',
                      borderRadius: 10,
                      background: localeOk ? '#fff' : palette.dangerSoft,
                      border: `1px solid ${localeOk ? palette.border : palette.danger}`,
                      display: 'flex', alignItems: 'center', gap: 10,
                      cursor: showSwitcher ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
                      {localeOk ? '🏢' : '⚠️'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: localeOk ? palette.textFaint : '#991b1b', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                        Stai caricando per
                      </div>
                      <div style={{
                        fontSize: 13, fontWeight: 600,
                        color: localeOk ? palette.text : '#7f1d1d',
                        lineHeight: 1.3, marginTop: 1,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {missingTenant
                          ? 'Nessun cliente attivo'
                          : missingLocale
                            ? `${activeTenantName ?? 'Cliente'} · seleziona locale`
                            : `${activeTenantName ?? 'Cliente'} · ${activeBiz?.name ?? '—'}`}
                      </div>
                    </div>
                    {showSwitcher && (
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: localeOk ? palette.accent : palette.danger,
                        flexShrink: 0,
                      }}>
                        Cambia ›
                      </span>
                    )}
                  </div>
                );
              })()}

              {uploadShortcutFood && (
                <div style={{ margin: '0 0 14px', padding: '10px 12px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', fontSize: 13, lineHeight: 1.4 }}>
                  <b>Caricamento per: {uploadShortcutFood.name}</b><br />
                  Carica fattura o DDT e, nella revisione, verifica che la riga sia associata a questo articolo.
                </div>
              )}

              {/* ─── T-077: niente più scelta DDT/Fattura — l'AI rileva il
                  tipo dalla foto via `detectedKind`. Il banner "Lotti da
                  inserire manualmente" si vede dopo l'estrazione (quando
                  effettivamente sappiamo che è una fattura), non a vuoto.
                  Lo step iniziale ora è SOLO i 3 bottoni di import. ─── */}

              {/* T-176: vassoio multi-documento — sempre disponibile. Sostituisce
                  i 3 bottoni a file singolo: si caricano più file (foto/galleria/
                  PDF) e si inviano in un colpo solo; l'analisi gira in background
                  (coda document_jobs) e l'utente è libero di fare altro. */}
              <DocumentUploadTray
                accountId={profile?.id ?? null}
                initialMode={quickLabel ? 'single' : undefined}
                singleDocumentHint={quickLabel ? 'Fotografa lo scontrino e tutte le etichette del commerciante: saranno analizzati e conservati insieme.' : undefined}
                tenantId={profile?.active_tenant_id ?? null}
                businessId={businessId}
                onNeedLocale={() => setLocalePickerOpen(true)}
              />

              {/* Stato B: file caricato → preview + azioni */}
              {imageB64 && (
                <div style={{
                  border: `1px solid ${palette.border}`,
                  borderRadius: 14,
                  background: palette.card,
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
                }}>
                  {/* Preview */}
                  <button
                    type="button"
                    onClick={() => setPreviewFullscreen(true)}
                    style={{
                      display: 'block', width: '100%', padding: 0,
                      border: 0, background: '#0f172a', cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    {imageMime !== 'application/pdf' ? (
                      <img
                        src={`data:${imageMime};base64,${imageB64}`}
                        alt="Anteprima"
                        style={{
                          display: 'block', width: '100%', maxHeight: 260,
                          objectFit: 'contain', background: '#0f172a',
                        }}
                      />
                    ) : pdfObjectUrl ? (
                      <div style={{ height: 220, pointerEvents: 'none', position: 'relative', background: '#fafafa' }}>
                        <iframe
                          src={`${pdfObjectUrl}#toolbar=0&navpanes=0&view=FitH`}
                          title="Anteprima PDF"
                          style={{ width: '100%', height: '100%', border: 0 }}
                        />
                      </div>
                    ) : null}
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      padding: '3px 8px', borderRadius: 999,
                      background: 'rgba(15,23,42,.78)', color: '#fff',
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                    }}>
                      Tocca per ingrandire
                    </div>
                  </button>
                  {/* Meta + azioni */}
                  <div style={{
                    padding: '10px 12px',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: palette.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {originalFilename ?? 'Documento'}
                      </div>
                      <div style={{ fontSize: 11, color: palette.textFaint }}>
                        {imageMime === 'application/pdf' ? 'PDF' : 'Immagine'} · {Math.round((imageB64.length * 3) / 4 / 1024)} KB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        resetWizard();
                        setImageB64(null);
                        setOriginalFilename(null);
                      }}
                      disabled={busy}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${palette.border}`,
                        borderRadius: 8,
                        background: 'transparent',
                        color: palette.textMuted,
                        fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Rimuovi
                    </button>
                  </div>
                </div>
              )}

              {/* Indicatore stato: busy o retry */}
              <div style={{ marginTop: 14, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {busy ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 999,
                    background: '#eff6ff', color: palette.accent,
                    fontSize: 13, fontWeight: 700,
                  }}>
                    <IonSpinner name="dots" color="primary" />
                    Analisi in corso…
                  </div>
                ) : imageB64 && !dryRun ? (
                  // File caricato ma estrazione non riuscita: bottone retry.
                  <button
                    type="button"
                    onClick={() => { void runExtraction(); }}
                    style={{
                      padding: '12px 20px', borderRadius: 10,
                      border: `1px solid ${palette.accent}`, background: '#fff',
                      color: palette.accent, fontSize: 14, fontWeight: 700,
                      cursor: 'pointer', display: 'inline-flex',
                      alignItems: 'center', gap: 8,
                    }}
                  >
                    <span>↻</span> Ri-prova estrazione
                  </button>
                ) : (
                  <div style={{ fontSize: 12, color: palette.textFaint, textAlign: 'center', lineHeight: 1.4 }}>
                    {!imageB64
                      ? "Una volta caricato il documento,\nl'estrazione AI parte automaticamente."
                      : null}
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {/* Locale picker inline (modal con select) */}
              <IonModal
                isOpen={localePickerOpen}
                onDidDismiss={() => setLocalePickerOpen(false)}
                initialBreakpoint={0.6}
                breakpoints={[0, 0.6, 1]}
              >
                <div style={{ padding: 20 }}>
                  <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: palette.text }}>
                    Cliente · Locale
                  </h2>
                  <p style={{ margin: '0 0 16px', fontSize: 13, color: palette.textFaint, lineHeight: 1.4 }}>
                    Su quale locale stai caricando questo documento?
                  </p>
                  {missingTenant ? (
                    <div style={{ fontSize: 13, color: palette.danger }}>
                      Nessun cliente attivo. Apri Impostazioni per scegliere
                      l'ambiente di lavoro.
                    </div>
                  ) : tenantBusinesses.length === 0 ? (
                    <>
                      <div style={{ marginBottom: 12, fontSize: 13, color: palette.textMuted }}>
                        Nessun locale per <b>{activeTenantName}</b>. Creane uno:
                      </div>
                      <IonList inset>
                        <IonItem>
                          <IonInput
                            label="Nome del locale"
                            labelPlacement="stacked"
                            value={newBusinessName}
                            onIonInput={(ev) => setNewBusinessName(String(ev.detail.value ?? ''))}
                          />
                        </IonItem>
                        <IonItem>
                          <IonButton slot="end" disabled={creatingBusiness || !newBusinessName.trim()} onClick={() => { void createBusiness(); }}>
                            {creatingBusiness ? <IonSpinner name="dots" /> : 'Crea locale'}
                          </IonButton>
                        </IonItem>
                      </IonList>
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {tenantBusinesses.map((b) => {
                        const selected = b.id === businessId;
                        return (
                          <button
                            key={b.id}
                            type="button"
                            onClick={() => {
                              setBusinessId(b.id);
                              setLocalePickerOpen(false);
                              // T-092 follow-up: persisti la scelta per future
                              // sessioni → auto-pick al prossimo Share Intent.
                              if (lastBusinessKey) {
                                try { localStorage.setItem(lastBusinessKey, b.id); } catch { /* noop */ }
                              }
                            }}
                            style={{
                              padding: '12px 14px',
                              border: `1px solid ${selected ? palette.accent : palette.border}`,
                              borderRadius: 10,
                              background: selected ? '#eff6ff' : palette.card,
                              cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 10,
                              textAlign: 'left',
                            }}
                          >
                            <span style={{ fontSize: 20, flexShrink: 0 }}>📍</span>
                            <span style={{
                              flex: 1, fontSize: 14, fontWeight: selected ? 700 : 500,
                              color: selected ? palette.accent : palette.text,
                            }}>
                              {b.name}
                            </span>
                            {selected && (
                              <span style={{ color: palette.accent, fontSize: 18 }}>✓</span>
                            )}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => { history.push('/settings'); setLocalePickerOpen(false); }}
                        style={{
                          marginTop: 10, padding: '10px 14px',
                          background: 'transparent', border: 'none',
                          color: palette.accent, fontSize: 13, fontWeight: 600,
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        + Crea nuovo locale in Impostazioni
                      </button>
                    </div>
                  )}
                </div>
              </IonModal>

              {/* T-077: l'action sheet `uploadSheetOpen` è stato rimosso —
                  i 3 bottoni di import (foto/galleria/file) sono ora
                  direttamente in-page nello stato vuoto. */}
            </>
          );
        })()}

        {/* ============ STEP 2: wizard review (due step "sovrapposti") ============ */}
        {plan && dryRun && (
          <>
            {/* T-074: banner anti-duplicato + flow "sostituisci".
                Se il backend ha rilevato un documento già caricato con stessa
                chiave logica (supplier + numero + data), 3 scelte:
                  1. Apri esistente  → naviga al doc precedente
                  2. Sostituisci     → l'utente vuole modificare, replace ON
                  3. Annulla         → resetta wizard e file picker
                In modalità "Sostituisci" il banner diventa verde "modalità
                sostituzione attiva" e il commit invia replace_document. */}
            {/* T-144: banner rosso "documento già caricato" rimosso da qui →
                ora è un IonAlert popup (vedi in fondo al componente). */}

            {/* T-079: banner replace mode compatto a 1 riga. Prima era una
                card grande con titolo + 2 righe descrizione + bottone — qui
                un chip verde sottile con label e link "Annulla". */}
            {dryRun.existing_document && replaceMode && (
              <div style={{
                margin: '0 16px 8px',
                padding: '6px 12px',
                borderRadius: 8,
                background: '#ecfdf5',
                border: '1px solid #6ee7b7',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, fontSize: 12, color: '#065f46',
              }}>
                <span style={{ fontWeight: 700 }}>🔁 Modalità sostituzione attiva</span>
                <button
                  type="button"
                  onClick={() => setReplaceMode(false)}
                  style={{
                    background: 'transparent', color: '#065f46',
                    border: 0, fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', padding: 4, textDecoration: 'underline',
                  }}
                >
                  Annulla
                </button>
              </div>
            )}

            {/* T-205: avviso NON bloccante "possibile pagina mancante". Prima il
                server rifiutava (HTTP 400 "Documento illeggibile") i documenti
                che l'AI riteneva continuassero su un'altra pagina — sbagliato
                per i DDT senza totale, caricati una pagina = un documento. Ora
                passa alla review con questo flag: si avvisa ma si lascia
                procedere. La pagina eventualmente mancante si carica come
                documento separato. */}
            {dryRun.possible_missing_page && (
              <div style={{
                margin: '0 16px 8px',
                padding: '8px 12px',
                borderRadius: 8,
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                fontSize: 12, color: '#92400e', lineHeight: 1.4,
              }}>
                <span style={{ fontWeight: 700 }}>📄 Possibile pagina mancante.</span>{' '}
                Il documento potrebbe proseguire su un&apos;altra pagina. Puoi <b>confermarlo così com&apos;è</b>; se manca una pagina, caricala come documento separato (o rifai l&apos;importazione fotografando tutte le pagine).
              </div>
            )}

            <div className={`te-work-draft-status te-work-draft-status--${documentWorkDraft.status}`} role="status" aria-live="polite">
              {documentWorkDraft.status === 'locked'
                ? 'Solo lettura: un altro dispositivo sta modificando questo carico.'
                : documentWorkDraft.status === 'offline'
                  ? 'Bozza salvata sul dispositivo. Torna online prima di caricare il magazzino.'
                  : documentWorkDraft.status === 'editable'
                    ? 'Bozza del carico salvata automaticamente per questo ristorante.'
                    : 'Preparo la bozza condivisa…'}
            </div>
            {documentDraftLocked && (
              <IonButton onClick={async () => {
                const confirmed = await confirmDialog(
                  'Riprendi subito l’ultima bozza salvata. L’altra schermata non potrà più salvare modifiche su questo documento.',
                  { header: 'Riprendi bozza', okText: 'Riprendi qui' },
                );
                if (!confirmed) return;
                try { await documentWorkDraft.resume(); }
                catch { setToast('La bozza è cambiata o la connessione non è disponibile. Controlla i dati e riprova.'); }
              }}>Riprendi qui subito</IonButton>
            )}
            <div className={documentDraftReadOnly ? 'te-work-draft-readonly' : undefined} aria-disabled={documentDraftReadOnly}>

            {/* T-076: rimossa la riga "Provider: X · Confidenza: Y%" — è
                debug info tecnica interna, non interessa al cliente. Il
                messaggio "Rivedi i dati" è stato spostato nel toast post
                estrazione. */}

            <nav className="te-import-steps" aria-label="Avanzamento import">
              <button
                type="button"
                className={`te-import-step${reviewStep === 'supplier' ? ' is-active' : ' is-done'}`}
                onClick={() => setReviewStep('supplier')}
              >
                <span className="te-import-step__number">{reviewStep === 'supplier' ? '1' : '✓'}</span>
                Documento
              </button>
              <button
                type="button"
                className={`te-import-step${reviewStep === 'lines' ? ' is-active' : reviewStep === 'summary' ? ' is-done' : ''}`}
                onClick={() => setReviewStep('lines')}
              >
                <span className="te-import-step__number">{reviewStep === 'summary' ? '✓' : '2'}</span>
                Articoli
              </button>
              <button
                type="button"
                className={`te-import-step${reviewStep === 'summary' ? ' is-active' : ''}`}
                disabled={!canCommit}
                onClick={() => setReviewStep('summary')}
              >
                <span className="te-import-step__number">3</span>
                Riepilogo
              </button>
            </nav>

            {reviewStep === 'supplier' && <div className="te-import-summary">

            {/* DOCUMENTO */}
            <IonList inset>
              <IonListHeader><IonLabel>Documento</IonLabel></IonListHeader>
              <IonItem>
                <IonInput
                  label="Numero"
                  labelPlacement="stacked"
                  value={plan.document.number}
                  onIonInput={(ev) => patchDocument({ number: String(ev.detail.value ?? '') })}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  label="Data"
                  labelPlacement="stacked"
                  type="date"
                  value={plan.document.date}
                  onIonInput={(ev) => patchDocument({ date: String(ev.detail.value ?? '') })}
                />
              </IonItem>
              {docKind === 'fattura' && (
                <IonItem>
                  <IonInput
                    label="Totale (EUR)"
                    labelPlacement="stacked"
                    type="number"
                    inputmode="decimal"
                    value={plan.document.total_amount}
                    onIonInput={(ev) => patchDocument({ total_amount: String(ev.detail.value ?? '') })}
                  />
                </IonItem>
              )}
              {/* T-197: il totale non era leggibile sul documento → stimato dalle
                  righe. Avvisa l'utente di verificarlo prima di confermare. */}
              {docKind === 'fattura' && plan.document.total_amount_estimated && (
                <IonItem lines="none">
                  <IonNote color="warning" style={{ fontSize: 12 }}>
                    ⚠️ Totale non leggibile sul documento: stimato dalle righe. Verifica e correggi col totale reale prima di confermare.
                  </IonNote>
                </IonItem>
              )}
            </IonList>

            {/* FORNITORE */}
            <IonList inset>
              <IonListHeader>
                <IonLabel>Fornitore</IonLabel>
                {plan.supplier_id && (
                  <IonNote color="success" slot="end">✓ Esistente</IonNote>
                )}
                {!plan.supplier_id && plan.supplier_create.name && (
                  <IonNote color="warning" slot="end">Sarà creato</IonNote>
                )}
              </IonListHeader>
              {/* T-478: fornitore corrente risolto dall'anagrafica anche quando
                  dryRun.supplier.existing è null (correzione import) → lo step non
                  resta muto e resta modificabile. */}
              {plan.supplier_id && linkedSupplier && (
                <IonItem color="light">
                  <IonLabel className="ion-text-wrap">
                    <h2 style={{ color: '#2dd36f', fontWeight: 600 }}>✓ {linkedSupplier.name}</h2>
                    <p style={{ margin: '4px 0' }}>
                      <b>P.IVA</b> {linkedSupplier.piva ?? '—'}
                      {linkedSupplier.category ? <> · <b>Categoria</b> {linkedSupplier.category}</> : null}
                    </p>
                    <p style={{ fontSize: 12, color: '#666' }}>Il documento verrà collegato a questo fornitore.</p>
                  </IonLabel>
                  <IonButton slot="end" fill="outline" size="small" color="medium" onClick={createDifferentSupplier}>Crea nuovo</IonButton>
                </IonItem>
              )}
              {plan.supplier_lot_preference?.no_lot && (
                <IonItem><IonLabel className="ion-text-wrap">Preferenza fornitore: documenti senza lotto. I lotti indicati sul documento restano conservati.</IonLabel>
                  <IonButton fill="clear" onClick={() => void rememberSupplierLotPreference(false)}>Rimuovi</IonButton></IonItem>
              )}
              {/* T-478: scegli un fornitore già in anagrafica (l'AI può non matchare). */}
              <IonItem button detail onClick={() => { setSupplierSearch(''); setSupplierPickerOpen(true); }}>
                <IonLabel color="primary">🔎 Scegli un fornitore già in anagrafica…</IonLabel>
              </IonItem>
              {!plan.supplier_id && dryRun.supplier.existing && (
                <IonItem color="warning">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ margin: 0, fontSize: 13 }}>
                      ⚠️ Stai creando un nuovo fornitore anche se in anagrafica esiste già{' '}
                      <b>{dryRun.supplier.existing.name}</b>
                      {dryRun.supplier.existing.piva ? ` (P.IVA ${dryRun.supplier.existing.piva})` : ''}.
                    </p>
                  </IonLabel>
                  <IonButton
                    slot="end"
                    size="small"
                    onClick={() => void changeImportSupplier(dryRun.supplier.existing!.id)}
                  >
                    Usa esistente
                  </IonButton>
                </IonItem>
              )}
              {/* T-466: double-check — sta creando un NUOVO fornitore ma ce ne sono
                  di simili in anagrafica. Forza l'operatore a controllare (causa
                  radice dei doppioni: dipende da chi carica). */}
                {!plan.supplier_id && (dryRun.supplier.suggestions?.length ?? 0) > 0 && (
                <IonItem color="warning" lines="none">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ margin: '4px 0', fontSize: 13, fontWeight: 600 }}>
                      ⚠️ Stai creando il fornitore «{plan.supplier_create.name || dryRun.supplier.parsed?.name}». Forse è già uno di questi?
                    </p>
                    {dryRun.supplier.suggestions!.map((s) => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                        <span style={{ flex: 1, fontSize: 13 }}>
                          <b>{s.name}</b>{s.piva ? ` · P.IVA ${s.piva}` : ''}
                        </span>
                        <IonButton size="small" onClick={() => void changeImportSupplier(s.id)}>Usa questo</IonButton>
                      </div>
                    ))}
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: '#78716c' }}>
                      Se è davvero un fornitore nuovo, prosegui con la creazione qui sotto.
                    </p>
                  </IonLabel>
                </IonItem>
                )}
              {!plan.supplier_id && Boolean(dryRun.supplier.suggestions?.length || dryRun.supplier.existing) && (
                <IonItem>
                  <IonCheckbox checked={plan.supplier_create_ack === true} onIonChange={event => patchPlan({ supplier_create_ack: event.detail.checked })}>
                    Ho verificato i fornitori proposti: è un fornitore diverso
                  </IonCheckbox>
                </IonItem>
              )}
              {!plan.supplier_id && (
                <>
                  <IonItem>
                    <IonInput
                      label="Ragione sociale *"
                      labelPlacement="stacked"
                      value={plan.supplier_create.name}
                      onIonInput={(ev) => patchSupplierCreate({ name: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="P.IVA"
                      labelPlacement="stacked"
                      value={plan.supplier_create.piva}
                      onIonInput={(ev) => patchSupplierCreate({ piva: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="Codice fiscale"
                      labelPlacement="stacked"
                      value={plan.supplier_create.codice_fiscale}
                      onIonInput={(ev) => patchSupplierCreate({ codice_fiscale: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonSelect
                      label="Categoria"
                      labelPlacement="stacked"
                      value={SUPPLIER_CATEGORIES.includes(plan.supplier_create.category) ? plan.supplier_create.category : 'altro'}
                      onIonChange={(ev) => patchSupplierCreate({ category: String(ev.detail.value) })}
                    >
                      {SUPPLIER_CATEGORIES.map((c) => (
                        <IonSelectOption key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="Email"
                      labelPlacement="stacked"
                      type="email"
                      value={plan.supplier_create.email}
                      onIonInput={(ev) => patchSupplierCreate({ email: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="Telefono"
                      labelPlacement="stacked"
                      type="tel"
                      value={plan.supplier_create.phone}
                      onIonInput={(ev) => patchSupplierCreate({ phone: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="Indirizzo"
                      labelPlacement="stacked"
                      placeholder="Via, città, CAP"
                      value={plan.supplier_create.address}
                      onIonInput={(ev) => patchSupplierCreate({ address: String(ev.detail.value ?? '') })}
                    />
                  </IonItem>
                  <IonItem>
                    <IonSelect
                      label="Paese"
                      labelPlacement="stacked"
                      value={plan.supplier_create.country || 'IT'}
                      onIonChange={(ev) => patchSupplierCreate({ country: String(ev.detail.value) })}
                    >
                      <IonSelectOption value="IT">Italia</IonSelectOption>
                      <IonSelectOption value="FR">Francia</IonSelectOption>
                      <IonSelectOption value="DE">Germania</IonSelectOption>
                      <IonSelectOption value="ES">Spagna</IonSelectOption>
                      <IonSelectOption value="AT">Austria</IonSelectOption>
                      <IonSelectOption value="CH">Svizzera</IonSelectOption>
                      <IonSelectOption value="SI">Slovenia</IonSelectOption>
                      <IonSelectOption value="HR">Croazia</IonSelectOption>
                      <IonSelectOption value="GR">Grecia</IonSelectOption>
                      <IonSelectOption value="PT">Portogallo</IonSelectOption>
                      <IonSelectOption value="NL">Olanda</IonSelectOption>
                      <IonSelectOption value="BE">Belgio</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                </>
              )}
            </IonList>

            <div style={{ display: 'flex', gap: 8, margin: '8px 16px 16px' }}>
              <IonButton fill="outline" expand="block" disabled={busy} onClick={() => resetWizard()} style={{ flex: 1 }}>
                Annulla
              </IonButton>
              <IonButton expand="block" color="primary" disabled={busy} onClick={() => setReviewStep('lines')} style={{ flex: 2 }}>
                Avanti · Alimenti →
              </IonButton>
            </div>

            </div>}

            {reviewStep === 'lines' && (
            <div className="te-import-review-layout">
              <ImportReviewQueue
                documentLabel={`${docKind === 'ddt' ? 'DDT' : 'Fattura'} ${plan.document.number || 'senza numero'}`}
                items={importQueueItems}
                currentIndex={Math.min(currentLineIdx, Math.max(0, plan.lines.length - 1))}
                onSelect={setCurrentLineIdx}
              />
              <div className="te-import-review-main">

            <div className="te-import-add-line">
              <div>
                <strong>Manca un articolo?</strong>
                <span>Aggiungilo alla revisione anche se l’IA non lo ha letto.</span>
              </div>
              <IonButton fill="outline" size="small" onClick={addManualImportLine}>
                + Aggiungi articolo
              </IonButton>
            </div>

            <details className="te-document-options">
              <summary>Opzioni documento</summary>
              <div className="te-document-options__body">
                <IonItem lines="none">
                  <IonLabel className="ion-text-wrap">
                    <h3>Normalizza nomi articoli</h3>
                    <p>
                      {normalizeNames
                        ? 'Rimuove confezioni e rumore prima del confronto con l’anagrafica.'
                        : 'Usa la dicitura originale riportata sul documento.'}
                    </p>
                  </IonLabel>
                  <IonToggle
                    slot="end"
                    disabled={busy}
                    checked={normalizeNames}
                    onIonChange={(event) => applyNameNormalization(event.detail.checked)}
                  />
                </IonItem>
              </div>
            </details>

            {/* T-079: invece di .map() su tutte le righe (scroll lungo →
                tap accidentali sul segmento), mostriamo SOLO la riga
                corrente. La nav sopra/sotto permette di spostarsi.
                L'idx assoluto resta `currentLineIdx` per il piano; i dati
                sorgente passano invece da `parsed_index`, perché dopo
                l'aggregazione le due posizioni possono divergere. */}
            {[plan.lines[Math.min(currentLineIdx, plan.lines.length - 1)]].filter(Boolean).map((line) => {
              const idx = Math.min(currentLineIdx, plan.lines.length - 1);
              const dl = sourceLineForPlan(dryRun.lines, line, idx);
              const match = dl?.food_match;
              const parsed = dl?.parsed;
              const hasPackHint =
                parsed?.pack_size != null &&
                parsed.pack_size > 0 &&
                parsed.total_weight_kg != null &&
                parsed.total_weight_kg > 0;
              // Il candidato server è il double check Levenshtein/token. Il
              // fuzzy locale resta solo fallback per dry-run legacy/sintetici.
              const serverSimilar = dl?.similar_foods?.find(candidate => candidate.id === line.food_id) ?? dl?.similar_food ?? null;
              const serverSimilarMatch: FoodMatch | null = serverSimilar
                ? tenantFoods.find((food) => food.id === serverSimilar.id)
                  ?? (serverSimilar.default_uom ? { id: serverSimilar.id, name: serverSimilar.name, default_uom: serverSimilar.default_uom, storage_type: serverSimilar.storage_type, category: serverSimilar.category } : null)
                : null;
              const fuzzyClient = !match && !serverSimilar
                ? findFuzzyFoodMatch(parsed?.description ?? '', tenantFoods)
                : null;
              const liveMatch = tenantFoods.find((food) => (
                food.id === (line.food_id ?? match?.id)
              )) ?? null;
              const bestMatch: FoodMatch | null =
                liveMatch ?? match ?? serverSimilarMatch ?? fuzzyClient?.food ?? null;
              // T-131: lotto attivo unificato — dal server (dl.current_lot, vale
              // anche per i match per memoria/ilike) o dal food locale (fuzzy).
              const activeLot: string | null = dl?.current_lot?.batch_lot ?? bestMatch?.current_batch_lot ?? null;
              const identityReview = identityNeedsReview(line, idx);
              const identityCandidates = identityCandidatesFor(line, idx);
              const action: 'update' | 'new' | 'skip' = line.skip
                ? 'skip'
                : line.food_id
                  ? 'update'
                  : 'new';
              const nameOnlySuggestion = dl?.match_source === 'ilike'
                || Boolean(serverSimilar)
                || Boolean(fuzzyClient && !match);
              const matchNeedsReview = nameOnlySuggestion
                && !manuallyMatchedLines.has(idx);
              const setAction = (next: 'update' | 'new' | 'skip'): void => {
                if (next === 'skip') {
                  patchLine(idx, { skip: true, skip_once: false, replace_active_lot: false, keep_current_lot: false });
                  void persistImportSkip(line, true).catch((error) => {
                    showImportFailure(toUserMessage(error), idx);
                  });
                  return;
                }
                if (next === 'update' && bestMatch) {
                  if (line.skip && !line.skip_once) {
                    void persistImportSkip(line, false).catch((error) => {
                      showImportFailure(toUserMessage(error), idx);
                    });
                  }
                  if (nameOnlySuggestion) {
                    setManuallyMatchedLines((current) => {
                      const nextSet = new Set(current);
                      nextSet.add(idx);
                      return nextSet;
                    });
                  }
                  selectExistingFoodAndRestorePackFactor(idx, bestMatch);
                  return;
                }
                // T-079: 'update' senza bestMatch → apri direttamente il
                // picker anagrafica (prima il segmento era disabled). L'utente
                // sceglie un food → patchLine col food_id selezionato dal modal.
                if (next === 'update' && !bestMatch) {
                  patchLine(idx, { skip_once: false, skip: false });
                  if (line.skip && !line.skip_once) {
                    void persistImportSkip(line, false).catch((error) => {
                      showImportFailure(toUserMessage(error), idx);
                    });
                  }
                  setFoodPickerForLine(idx);
                  setFoodPickerSearch(
                    (line.food_create.name || parsed?.description || '')
                      .toLowerCase()
                      .split(/\s+/)
                      .filter((t) => t.length >= 3)
                      .slice(0, 2)
                      .join(' '),
                  );
                  return;
                }
                if (!canCreateFoods) return;
                if (identityCandidatesFor(line, idx).length || line.identity_error) {
                  void confirmDistinctFood(idx);
                  return;
                }
                // 'new': reset replace_active_lot / keep_current_lot (non hanno
                // senso su un nuovo alimento, che non ha lotto attivo).
                patchLine(idx, { skip_once: false, skip: false, food_id: null, replace_active_lot: false, keep_current_lot: false });
                if (line.skip && !line.skip_once) {
                  void persistImportSkip(line, false).catch((error) => {
                    showImportFailure(toUserMessage(error), idx);
                  });
                }
              };
              return (
                // T-076: ogni riga è una CARD distinta con bordo, ombra e
                // header colorato — prima erano IonList inset uno dietro
                // l'altro e l'occhio non distingueva una riga dalla
                // successiva.
                <div
                  key={idx}
                  ref={reviewCardRef}
                  className="te-import-line-card"
                  style={{
                    background: palette.card,
                    border: `${identityReview ? 2 : 1}px solid ${identityReview ? palette.danger : palette.border}`,
                    borderRadius: 14,
                    margin: '12px 4px',
                    boxShadow: '0 1px 2px rgba(15,23,42,.05), 0 2px 8px rgba(15,23,42,.04)',
                    opacity: line.skip ? 0.55 : 1,
                    overflow: 'hidden',
                  }}
                >
                  {/* Header card: numero riga + descrizione AI + chip stato */}
                  <div
                    className="te-import-line-card__header"
                    data-testid="ddt-article-action-header"
                  >
                    <div
                      className="te-import-line-card__number"
                    >
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="te-import-line-card__name">
                        {action === 'update' && bestMatch
                          ? bestMatch.name
                          : (line.food_create.name || parsed?.description || '—')}
                      </div>
                    </div>
                    <InfoBadge
                      open={openAccessoryInfo === `line-${idx}`}
                      label={openAccessoryInfo === `line-${idx}`
                        ? 'Nascondi informazioni sulla lettura'
                        : 'Mostra informazioni sulla lettura'}
                      onToggle={() => setOpenAccessoryInfo((current) => (
                        current === `line-${idx}` ? null : `line-${idx}`
                      ))}
                    />
                  </div>

                  {openAccessoryInfo === `line-${idx}` && (
                    <div className="te-accessory-info" role="status">
                      <button
                        type="button"
                        className="te-accessory-info__copy"
                        onClick={() => {
                          const txt = parsed?.raw_description
                            ?? parsed?.description
                            ?? line.raw_description
                            ?? '';
                          void navigator.clipboard?.writeText(txt).then(
                            () => setToast('Testo del DDT copiato'),
                            () => setToast('Copia non riuscita'),
                          );
                        }}
                      >
                        <strong>Testo letto sul documento</strong><br />
                        “{parsed?.raw_description
                          ?? parsed?.description
                          ?? line.raw_description
                          ?? '—'}”
                        <span>Tocca per copiare</span>
                      </button>
                      {line.supplier_code && (
                        <div><strong>Codice articolo fornitore:</strong> {line.supplier_code}</div>
                      )}
                      {dl?.match_source && (
                        <div>
                          <strong>Perché è stato associato:</strong>{' '}
                          {dl.match_source === 'code'
                            ? 'codice articolo già collegato'
                            : dl.match_source === 'alias_manual'
                              ? 'associazione confermata da te'
                              : dl.match_source === 'alias_ai'
                                ? 'descrizione già riconosciuta'
                                : dl.match_source === 'alias_first_setup'
                                  ? 'configurazione iniziale'
                                  : dl.match_source === 'exact_name'
                                    ? 'nome identico già presente in magazzino'
                                    : manuallyMatchedLines.has(idx)
                                      ? 'corrispondenza scelta da te per questa importazione'
                                      : 'suggerimento per nome, non applicato automaticamente'}
                          {dl.alias_meta?.last_used_at
                            ? ` · ultima volta ${fmtDayMonth(dl.alias_meta.last_used_at)}`
                            : ''}
                        </div>
                      )}
                      {extractionWarnings(dl).map((warning) => (
                        <div key={warning}>
                          <strong>Attenzione:</strong> {warning}.
                        </div>
                      ))}
                    </div>
                  )}
                  {identityReview && (
                    <div role="alert" style={{ padding: 16, color: palette.danger }}>
                      <strong>Articolo da rivedere: già presente o abbinamento ambiguo</strong>
                      <p>Scegli dove caricare questa fornitura. La scelta sarà ricordata anche senza codice articolo.</p>
                      {identityCandidates.map(candidate => (
                        <IonItem key={candidate.id} lines="none">
                          <IonLabel className="ion-text-wrap"><strong>{candidate.name}</strong>
                            <p>Magazzino: {candidate.default_uom ?? '—'}{candidate.current_batch_lot ? ` · Lotto ${candidate.current_batch_lot}` : ''}</p>
                          </IonLabel>
                          <IonButton slot="end" style={{ height: 44, alignSelf: 'center' }} disabled={!candidate.default_uom} onClick={() => {
                            if (!candidate.default_uom) return;
                            setManuallyMatchedLines(current => new Set([...current, idx]));
                            selectExistingFoodAndRestorePackFactor(idx, { ...candidate, default_uom: candidate.default_uom });
                          }}>Carica qui</IonButton>
                        </IonItem>
                      ))}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                        <IonButton style={{ height: 44 }} fill="outline" onClick={() => { setFoodPickerForLine(idx); setFoodPickerSearch(''); }}>Scegli altro alimento</IonButton>
                        {canCreateFoods && <IonButton style={{ height: 44 }} fill="outline" color="danger" onClick={() => void confirmDistinctFood(idx)}>È un articolo distinto</IonButton>}
                      </div>
                    </div>
                  )}
                  {line.new_food_confirmed && !line.skip && <p style={{ padding: '0 16px' }}>Articolo distinto confermato. La scelta sarà memorizzata al caricamento.</p>}
                  {/* T-079: 3 azioni per riga.
                      - "Aggiorna" SEMPRE cliccabile: senza match apre il
                        picker anagrafica.
                      - Label corte (1 parola) per entrare anche su schermi
                        piccoli + font ridotto.
                      - `touchAction: manipulation` riduce falsi positivi
                        durante lo scroll (browser non aspetta double-tap-zoom).
                      - Padding verticale ridotto per chip più compatte.
                  */}
                  <div className="te-import-line-card__actions">
                    <IonSegment
                      className="te-import-line-card__segment"
                      value={action}
                      onIonChange={(ev) => setAction((ev.detail.value as 'update' | 'new' | 'skip') ?? 'new')}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <IonSegmentButton value="update" layout="icon-start">
                        <IonLabel style={{ fontSize: 11, fontWeight: 700 }}>
                          Aggiorna
                        </IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="new" layout="icon-start" disabled={!canCreateFoods}>
                        <IonLabel style={{ fontSize: 11, fontWeight: 700 }}>
                          {canCreateFoods ? 'Nuovo' : 'Revisione'}
                        </IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="skip" layout="icon-start">
                        <IonLabel style={{ fontSize: 11, fontWeight: 700 }}>
                          Salta
                        </IonLabel>
                      </IonSegmentButton>
                    </IonSegment>
                  </div>
                  {/* T-517: l'esito resta visibile in una riga compatta; UoM,
                      provenienza del match, lotto e azioni secondarie si
                      aprono solo dalla i. */}
                  {!identityReview && !line.new_food_confirmed && !match && (dl?.similar_foods?.length ?? 0) > 0 && (
                    <ImportReviewSection tone="food" icon={restaurantOutline} title="Alimenti simili già presenti">
                      {dl!.similar_foods!.map(candidate => (
                        <IonItem key={candidate.id}>
                          <IonLabel className="ion-text-wrap">{candidate.name}</IonLabel>
                          <IonButton slot="end" disabled={!candidate.default_uom} onClick={() => {
                            if (!candidate.default_uom) return;
                            setManuallyMatchedLines(current => new Set([...current, idx]));
                            selectExistingFoodAndRestorePackFactor(idx, { id: candidate.id, name: candidate.name,
                              default_uom: candidate.default_uom, storage_type: candidate.storage_type, category: candidate.category });
                          }}>Usa questo</IonButton>
                        </IonItem>
                      ))}
                    </ImportReviewSection>
                  )}
                  {action === 'update' && bestMatch && (
                    <ImportReviewSection
                      tone="food"
                      icon={restaurantOutline}
                      title="Dettagli alimento"
                    >
                      <div className={`te-linked-food-summary${matchNeedsReview ? ' is-review' : ''}`}>
                        <div className="te-linked-food-summary__text">
                          <strong>
                            {matchNeedsReview
                              ? `Abbinamento da verificare: ${bestMatch.name}`
                              : `Alimento collegato: ${bestMatch.name}`}
                          </strong>
                          {line.keep_current_lot && activeLot && (
                            <span>Lotto attivo invariato</span>
                          )}
                        </div>
                        <InfoBadge
                          inline
                          open={openAccessoryInfo === `match-${idx}`}
                          label={openAccessoryInfo === `match-${idx}`
                            ? 'Nascondi dettagli su abbinamento e lotto'
                            : 'Mostra dettagli su abbinamento e lotto'}
                          onToggle={() => setOpenAccessoryInfo((current) => (
                            current === `match-${idx}` ? null : `match-${idx}`
                          ))}
                        />
                      </div>
                      {openAccessoryInfo === `match-${idx}` && (
                    <div className="te-linked-food-details">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: '#15803d', fontWeight: 700, fontSize: 14,
                          lineHeight: 1.3,
                        }}>
                          ✓ {bestMatch.name}
                        </div>
                        <div style={{ fontSize: 12, color: '#166534', marginTop: 3 }}>
                          UoM {bestMatch.default_uom} · {bestMatch.storage_type ?? '—'}
                          {(serverSimilar || (fuzzyClient && !match))
                            ? ` · similarità ${Math.round((serverSimilar?.score ?? fuzzyClient?.score ?? 0) * 100)}%`
                            : ''}
                        </div>
                        {/* T-131: memoria del match — fa capire al ristoratore PERCHÉ
                            quell'alimento è stato proposto, così si fida dell'auto-match. */}
                        {match && dl?.match_source && (() => {
                          // T-294B (hardening): il match 'ilike' (per nome, fuzzy)
                          // è quello a rischio falso-positivo (es. "Gambero 4°" vs
                          // "Gambero 3°"). Lo segnaliamo come INCERTO (ambra) così
                          // l'utente verifica, invece del chip neutro precedente.
                          const weak = dl.match_source === 'ilike';
                          return (
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              marginTop: 5, padding: '3px 8px', borderRadius: 999,
                              fontSize: 11, fontWeight: 600,
                              background: dl.match_source === 'alias_manual' ? '#dcfce7' : weak ? '#fef3c7' : '#eff6ff',
                              color: dl.match_source === 'alias_manual' ? '#15803d' : weak ? '#92400e' : '#1d4ed8',
                              border: `1px solid ${dl.match_source === 'alias_manual' ? '#86efac' : weak ? '#fcd34d' : '#bfdbfe'}`,
                            }}>
                              {documentMatchDetailLabel(
                                dl.match_source,
                                manuallyMatchedLines.has(idx),
                              )}
                              {dl.alias_meta?.last_used_at
                                ? ` · ultima volta ${fmtDayMonth(dl.alias_meta.last_used_at)}`
                                : ''}
                            </div>
                          );
                        })()}
                      </div>

                      {/* T-105: sub-toggle "Aggiungi / Sostituisci" visibile
                          SOLO se il food ha già un lotto attivo. Se non c'è
                          lotto attivo, aggiungere è l'unica opzione — niente
                          da scegliere. */}
                      {activeLot && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, color: '#166534', marginBottom: 6, fontWeight: 600 }}>
                            Lotto attivo: <span style={{ fontFamily: 'monospace' }}>{activeLot}</span>
                            {dl?.current_lot?.document_date ? ` · del ${fmtDayMonth(dl.current_lot.document_date)}` : ''}
                          </div>
                          {/* T-151: gestione lotto collassata di default (compatta). */}
                          {!lotMgmtOpen && !line.keep_current_lot ? (
                            <button
                              type="button"
                              onClick={() => setLotMgmtOpen(true)}
                              style={{
                                background: 'none', border: '1px solid #bbf7d0', borderRadius: 8,
                                padding: '6px 10px', color: '#15803d', fontSize: 12, fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              Gestisci lotto attivo ▾
                            </button>
                          ) : line.keep_current_lot ? (
                            /* T-131: fattura più vecchia/uguale del lotto attivo →
                               registra solo il carico senza cambiare il lotto attivo. */
                            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1.5px solid #fde68a' }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                                📌 Lotto attivo invariato
                              </div>
                              <div style={{ fontSize: 11, color: '#78350f', marginTop: 3, lineHeight: 1.35 }}>
                                Questa fattura{dryRun.document.date ? ` (${fmtDayMonth(dryRun.document.date)})` : ''} è più vecchia del lotto attivo: registro il carico ma non cambio il lotto attivo dell'alimento.
                              </div>
                              <button
                                type="button"
                                onClick={() => patchLine(idx, { keep_current_lot: false, replace_active_lot: false })}
                                style={{
                                  marginTop: 8, padding: '6px 10px', borderRadius: 8,
                                  border: '1.5px solid #f59e0b', background: '#fff',
                                  color: '#92400e', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                }}
                              >
                                Aggiorna comunque il lotto attivo →
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: 6 }}>
                                {[
                                  { value: false, icon: '➕', label: 'Aggiungi', sub: 'mantieni il vecchio' },
                                  { value: true,  icon: '🔁', label: 'Sostituisci', sub: 'solo se esaurito' },
                                ].map(({ value, icon, label, sub }) => {
                                  const isActive = line.replace_active_lot === value;
                                  return (
                                    <button
                                      key={label}
                                      type="button"
                                      onClick={() => patchLine(idx, { replace_active_lot: value })}
                                      style={{
                                        flex: 1,
                                        padding: '8px 6px',
                                        borderRadius: 8,
                                        border: isActive
                                          ? `2px solid ${value ? '#db2777' : '#16a34a'}`
                                          : '1.5px solid #bbf7d0',
                                        background: isActive
                                          ? (value ? '#fdf2f8' : '#f0fdf4')
                                          : '#fff',
                                        cursor: 'pointer',
                                        textAlign: 'center' as const,
                                      }}
                                    >
                                      <div style={{ fontSize: 16, lineHeight: 1 }}>{icon}</div>
                                      <div style={{
                                        fontSize: 11, fontWeight: 700, marginTop: 3,
                                        color: isActive ? (value ? '#9d174d' : '#15803d') : '#374151',
                                      }}>{label}</div>
                                      <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{sub}</div>
                                    </button>
                                  );
                                })}
                              </div>
                              {line.replace_active_lot && (
                                <div style={{
                                  marginTop: 7,
                                  padding: '7px 9px',
                                  borderRadius: 8,
                                  background: '#fff7ed',
                                  border: '1px solid #fed7aa',
                                  color: '#9a3412',
                                  fontSize: 10,
                                  lineHeight: 1.4,
                                }}>
                                  Non vengono scaricati residui in automatico. Se il lotto attivo
                                  ha ancora giacenza o prenotazioni, l’import sarà bloccato senza
                                  modificare il magazzino. Scaricalo prima da Magazzino oppure usa
                                  “Aggiungi”.
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => patchLine(idx, { keep_current_lot: true, replace_active_lot: false })}
                                style={{
                                  marginTop: 7, background: 'none', border: 'none', padding: '4px 0',
                                  color: '#92400e', fontSize: 11, fontWeight: 600,
                                  textDecoration: 'underline', cursor: 'pointer', textAlign: 'left' as const,
                                }}
                              >
                                Fattura più vecchia? Registra senza cambiare il lotto attivo
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {/* Link discreto "Cambia alimento" — staccato dal segmento
                          per ridurre tap accidentali. Tap area 36px, padding
                          generoso, colore tenue diverso dalle chip primarie. */}
                      <button
                        type="button"
                        onClick={() => {
                          setFoodPickerForLine(idx);
                          setFoodPickerSearch(
                            (line.food_create.name || parsed?.description || '')
                              .toLowerCase()
                              .split(/\s+/)
                              .filter((t) => t.length >= 3)
                              .slice(0, 2)
                              .join(' '),
                          );
                        }}
                        style={{
                          marginTop: 10,
                          padding: '8px 12px',
                          background: 'transparent',
                          border: '1px dashed #86efac',
                          borderRadius: 8,
                          color: '#15803d',
                          fontSize: 12, fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          minHeight: 36,
                        }}
                      >
                        ↻ Cambia alimento
                      </button>
                    </div>
                      )}
                    </ImportReviewSection>
                  )}
                  {/* T-078: caso edge — action='update' ma nessun match (può
                      capitare se l'utente ha forzato 'update' senza scegliere
                      ancora il food). Mostriamo un piccolo CTA per aprire il
                      picker. Non confondibile col segmento (è dentro card). */}
                  {action === 'update' && !bestMatch && !line.food_id && (
                    <div style={{
                      margin: '4px 14px 12px',
                      padding: '12px 14px',
                      borderRadius: 12,
                      background: '#fffbeb',
                      border: '1px dashed #fde68a',
                    }}>
                      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>
                        Nessun alimento collegato. Scegline uno dall'anagrafica:
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFoodPickerForLine(idx);
                          setFoodPickerSearch(
                            (line.food_create.name || parsed?.description || '')
                              .toLowerCase()
                              .split(/\s+/)
                              .filter((t) => t.length >= 3)
                              .slice(0, 2)
                              .join(' '),
                          );
                        }}
                        style={{
                          padding: '8px 12px',
                          background: '#fff',
                          border: '1px solid #fcd34d',
                          borderRadius: 8,
                          color: '#92400e',
                          fontSize: 12, fontWeight: 700,
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          minHeight: 36,
                        }}
                      >
                        🔎 Scegli dall'anagrafica
                      </button>
                    </div>
                  )}
                  {!line.skip && (
                    <>
                      {/* Il testo originale resta disponibile (e copiabile) nel
                          badge informazioni dell'header, senza duplicarlo qui. */}
                      {!line.food_id && (canCreateFoods ? (
                        <ImportReviewSection
                          tone="food"
                          icon={restaurantOutline}
                          title="Dettagli alimento"
                        >
                          {/* T-157: nome/categoria/conservazione/allergeni dietro
                              accordion chiuso di default → card compatta, niente
                              scroll. Si apre solo per correggere i dati. */}
                          <button
                            type="button"
                            onClick={() => setDetailsOpen((o) => !o)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                              margin: '6px 0', padding: '10px 12px', borderRadius: 10,
                              border: '1px solid #e2e8f0', background: '#f8fafc',
                              color: '#0f172a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            <span aria-hidden="true">⚙️</span>
                            <span style={{ flex: 1, textAlign: 'left' as const }}>Dettagli alimento (categoria, conservazione, allergeni)</span>
                            <span aria-hidden="true">{detailsOpen ? '▾' : '▸'}</span>
                          </button>
                          {detailsOpen && (
                          <>
                          <IonItem>
                            <IonInput
                              label="Nome alimento *"
                              labelPlacement="stacked"
                              value={line.food_create.name}
                              onIonInput={(ev) => patchLineFoodCreate(idx, { name: String(ev.detail.value ?? '') })}
                            />
                          </IonItem>
                          <IonItem>
                            <IonSelect
                              label="Categoria"
                              labelPlacement="stacked"
                              value={line.food_create.category}
                              onIonChange={(ev) => patchLineFoodCreate(idx, { category: String(ev.detail.value) })}
                            >
                              {FOOD_CATEGORIES.map((c) => (
                                <IonSelectOption key={c.value} value={c.value}>{c.label}</IonSelectOption>
                              ))}
                            </IonSelect>
                          </IonItem>
                          <IonItem>
                            <IonSelect
                              label="Conservazione"
                              labelPlacement="stacked"
                              value={line.food_create.storage_type}
                              onIonChange={(ev) => patchLineFoodCreate(idx, { storage_type: String(ev.detail.value) })}
                            >
                              {STORAGE_TYPES.map((s) => (
                                <IonSelectOption key={s.value} value={s.value}>{s.label}</IonSelectOption>
                              ))}
                            </IonSelect>
                          </IonItem>
                          <IonItem lines="none">
                            <IonLabel position="stacked">Allergeni (Reg. UE 1169/2011)</IonLabel>
                          </IonItem>
                          <IonItem lines="none">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 0 12px' }}>
                              {(Object.entries(ALLERGEN_LABELS_IT) as [Allergen, string][]).map(([key, label]) => {
                                const selected = line.food_create.allergens.includes(key);
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    onClick={() => {
                                      const next = selected
                                        ? line.food_create.allergens.filter((a) => a !== key)
                                        : [...line.food_create.allergens, key];
                                      patchLineFoodCreate(idx, { allergens: next });
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: 999,
                                      border: `1px solid ${selected ? '#dc2626' : '#cbd5e1'}`,
                                      background: selected ? '#fee2e2' : '#fff',
                                      color: selected ? '#991b1b' : '#475569',
                                      fontSize: 12,
                                      fontWeight: selected ? 600 : 400,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {selected ? '✓ ' : ''}{label}
                                  </button>
                                );
                              })}
                            </div>
                          </IonItem>
                          {line.food_create.allergens.length === 0 && (
                            <IonNote color="medium" style={{ display: 'block', padding: '0 16px 8px', fontSize: 11 }}>
                              Nessun allergene rilevato. Verifica e aggiungi se serve (la responsabilità HACCP è dell'OSA).
                            </IonNote>
                          )}
                          </>
                          )}
                        </ImportReviewSection>
                      ) : (
                        <IonNote color="warning" className="ion-text-wrap" style={{ display: 'block', margin: '8px 16px 12px' }}>
                          Alimento non presente: puoi associarne uno esistente oppure inviare l’intero documento in revisione. Nessun carico verrà applicato prima dell’approvazione.
                        </IonNote>
                      ))}
                      <ImportReviewSection
                        tone="quantity"
                        icon={scaleOutline}
                        title="Quantità e prezzo"
                      >
                      <IonItem>
                        <IonInput
                          label={`Quantità ${docKind === 'ddt' ? 'DDT' : 'fattura'} (${line.source_uom || 'UdM'}) *`}
                          labelPlacement="stacked"
                          type="number"
                          inputmode="decimal"
                          value={sourceQtyDraft[idx] ?? String(line.source_qty)}
                          onIonInput={(ev) => {
                            const draft = String(ev.detail.value ?? '');
                            setSourceQtyDraft((current) => ({ ...current, [idx]: draft }));
                            // Dopo che esiste un fattore confermato, la quantità
                            // di magazzino segue subito ogni correzione del DDT.
                            if (line.conversion_factor != null) {
                              const edited = editSourceQuantityDraft({
                                nextSourceQty: draft,
                                currentStockQty: line.qty,
                                stockUom: line.uom,
                                sourceQty: line.source_qty,
                                sourceUom: line.source_uom,
                                sourceUnitPrice: line.source_unit_price,
                                sourceLineTotal: line.source_line_total,
                                conversionFactor: line.conversion_factor,
                                conversionSource: line.conversion_source,
                              });
                              if (edited) {
                                patchLine(idx, {
                                  source_qty: edited.sourceQty,
                                  qty: edited.stockQty,
                                  source_unit_price: edited.sourceUnitPrice,
                                  source_line_total: edited.sourceLineTotal,
                                  unit_price: edited.sourceUnitPrice,
                                  line_total: edited.sourceLineTotal,
                                  conversion_factor: edited.conversionFactor,
                                  conversion_source: edited.conversionSource,
                                  conversion_confirmed: edited.conversionConfirmed,
                                });
                              }
                            }
                          }}
                          onIonBlur={() => {
                            const draft = sourceQtyDraft[idx];
                            if (draft !== undefined && draft !== String(line.source_qty)) {
                              applySourceQuantityCorrection(idx, line, draft);
                            } else if (draft !== undefined) {
                              setSourceQtyDraft((current) => {
                                const next = { ...current };
                                delete next[idx];
                                return next;
                              });
                            }
                          }}
                        />
                        <div slot="end" className="te-item-actions">
                          <InfoBadge
                            inline
                            open={openAccessoryInfo === `quantity-${idx}`}
                            label={openAccessoryInfo === `quantity-${idx}`
                              ? 'Nascondi spiegazione della quantità documento'
                              : 'Mostra spiegazione della quantità documento'}
                            onToggle={() => setOpenAccessoryInfo((current) => (
                              current === `quantity-${idx}` ? null : `quantity-${idx}`
                            ))}
                          />
                          <IonButton
                            fill="outline"
                            size="small"
                            onClick={() => applySourceQuantityCorrection(
                              idx,
                              line,
                              sourceQtyDraft[idx] ?? String(line.source_qty),
                            )}
                          >
                            Applica
                          </IonButton>
                        </div>
                      </IonItem>
                      <div style={{ padding: '8px 16px 2px' }}>
                        <UomSelect
                          label={`Unità sul ${docKind === 'ddt' ? 'DDT' : 'documento'}`}
                          value={line.source_uom}
                          allowCustom
                          onChange={(sourceUom) => {
                            const change = changeSourceUomDraft({
                              sourceQty: line.source_qty,
                              nextSourceUom: sourceUom,
                              stockQty: line.qty,
                              stockUom: line.uom,
                            });
                            patchLine(idx, {
                              source_uom: sourceUom,
                              extracted_uom: sourceUom,
                              qty: change.stockQty,
                              pack_needs_factor: isPackUnit(sourceUom),
                              pack_factor: null,
                              pack_base_uom: null,
                              pack_factor_applied: false,
                              conversion_factor: change.conversionFactor,
                              conversion_source: change.conversionSource,
                              conversion_confirmed: change.conversionConfirmed,
                            });
                            setImportFailure(null);
                          }}
                        />
                        <IonNote color="medium" className="te-field-help">
                          Correggila se l’IA ha letto, per esempio, litri al posto di kg.
                        </IonNote>
                      </div>
                      {openAccessoryInfo === `quantity-${idx}` && (
                        <IonNote
                          color="medium"
                          className="te-field-help"
                        >
                          Correggi qui il numero di pezzi/colli letto dall’IA.
                          Il peso per singolo pezzo si imposta separatamente sotto.
                        </IonNote>
                      )}
                      <IonItem>
                        <IonInput
                          label={`Quantità da caricare in magazzino (${line.uom}) *`}
                          labelPlacement="stacked"
                          type="number"
                          inputmode="decimal"
                          value={line.qty}
                          onIonInput={(ev) => {
                            const qty = String(ev.detail.value ?? '');
                            const edited = editStockQuantityDraft({
                              nextQty: qty,
                              stockUom: line.uom,
                              sourceQty: line.source_qty,
                              sourceUom: line.source_uom,
                              sourceUnitPrice: line.source_unit_price,
                              sourceLineTotal: line.source_line_total,
                              conversionFactor: line.conversion_factor,
                              conversionSource: line.conversion_source,
                            });
                            patchLine(idx, {
                              ...clearPackOnEdit(line),
                              qty: edited.qty,
                              source_qty: edited.sourceQty,
                              source_unit_price: edited.sourceUnitPrice,
                              source_line_total: edited.sourceLineTotal,
                              unit_price: edited.sourceUnitPrice,
                              line_total: edited.sourceLineTotal,
                              conversion_factor: edited.conversionFactor,
                              conversion_source: edited.conversionSource,
                              conversion_confirmed: edited.conversionConfirmed,
                            });
                          }}
                        />
                      </IonItem>
                      {/* Dopo una correzione esplicita non riproporre il vecchio
                          suggerimento IA: nel caso paprika continuava a mostrare
                          2 × 400 g = 0,8 kg accanto al valore corretto 0,35 kg. */}
                      {hasPackHint && !line.pack_factor_applied && (
                        <div style={{
                          margin: '6px 14px 0',
                          padding: '8px 10px',
                          borderRadius: 8,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          fontSize: 11,
                          lineHeight: 1.4,
                        }}>
                          {/* T-079: pack hint compatto. Info su 1 riga,
                              bottoni inline più piccoli. */}
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            💡 <b>{parsed!.qty} {parsed!.uom ?? 'pz'}</b> ×{' '}
                            <b>{parsed!.pack_size} {parsed!.pack_uom ?? 'kg'}</b> ={' '}
                            <b>{parsed!.total_weight_kg} kg</b>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <IonButton
                              size="small"
                              fill={Number(line.qty) === parsed!.total_weight_kg ? 'solid' : 'outline'}
                              style={{ '--padding-start': '8px', '--padding-end': '8px', fontSize: 10, flex: 1 } as React.CSSProperties}
                              onClick={() => patchLine(idx, {
                                qty: String(parsed!.total_weight_kg),
                                uom: 'kg',
                                conversion_factor: line.source_qty > 0
                                  ? Number(parsed!.total_weight_kg) / line.source_qty
                                  : null,
                                conversion_source: 'document_weight',
                                conversion_confirmed: true,
                              })}
                            >
                              Peso ({parsed!.total_weight_kg} kg)
                            </IonButton>
                            <IonButton
                              size="small"
                              fill={Number(line.qty) === parsed!.qty ? 'solid' : 'outline'}
                              style={{ '--padding-start': '8px', '--padding-end': '8px', fontSize: 10, flex: 1 } as React.CSSProperties}
                              onClick={() => {
                                // R4 #1: usa l'unità CANONICA, non quella grezza. Se è un
                                // collo (cassa/ct/conf), scegliere "Pezzi" (= numero colli)
                                // RIARMA il gate T-294B così l'utente deve convertire al
                                // contenuto reale — niente N colli caricati come giacenza base.
                                const canon = normalizeUom((parsed as { uom_canonical?: string }).uom_canonical ?? parsed!.uom ?? 'pz') || 'pz';
                                const isPack = isPackUnit(canon);
                                patchLine(idx, {
                                  qty: String(parsed!.qty),
                                  uom: canon,
                                  extracted_uom: canon,
                                  pack_needs_factor: isPack,
                                  pack_factor: null,
                                  pack_base_uom: null,
                                  pack_factor_applied: false,
                                  conversion_factor: 1,
                                  conversion_source: 'identity',
                                  conversion_confirmed: true,
                                });
                              }}
                            >
                              Pezzi ({parsed!.qty})
                            </IonButton>
                          </div>
                        </div>
                      )}
                      {/* T-324 C: pulsante sempre disponibile per definire "1 collo =
                          X base" ANCHE quando l'AI ha caricato in pz (cartoni etichettati
                          pz): attiva l'input pack-factor (sotto), pre-compilato col
                          suggerimento da pack_size dell'AI; il fattore confermato è
                          salvato per i prossimi carichi di questo articolo. */}
                      {!line.pack_needs_factor && !line.pack_factor_applied && !line.skip && (
                        <div style={{ margin: '4px 14px 0' }}>
                          <IonButton
                            fill="clear"
                            size="small"
                            style={{ textTransform: 'none', '--padding-start': '4px', fontSize: 11 } as React.CSSProperties}
                            onClick={() => patchLine(idx, { pack_needs_factor: true })}
                          >
                            📦 Peso/contenuto di 1 {line.source_uom || line.extracted_uom || 'pezzo/collo'}
                          </IonButton>
                        </div>
                      )}
                      {/* T-294B: fattore di conversione collo→base. Se già applicato
                          (da memoria o a mano) mostra la nota verde; se serve, alert
                          inline con suggerimento dedotto dalla descrizione. */}
                      {line.pack_factor_applied && (
                        <div style={{ margin: '6px 14px 0', padding: '8px 10px', borderRadius: 8, background: '#ecfdf5', border: '1px solid #a7f3d0', fontSize: 11.5, color: '#065f46' }}>
                          <div>
                            ✓ <b>{line.source_qty} {line.source_uom}</b> ×{' '}
                            <b>{line.pack_factor} {line.pack_base_uom}/{line.source_uom}</b>
                            {' '}→ carico <b>{line.qty} {line.uom}</b>{' '}
                            <span style={{ color: '#047857' }}>
                              {line.conversion_source === 'supplier_alias'
                                ? '(fattore memorizzato)'
                                : '(fattore confermato)'}
                            </span>
                          </div>
                          <IonButton
                            fill="clear"
                            size="small"
                            style={{ textTransform: 'none', '--padding-start': '0', marginTop: 4, fontSize: 11 } as React.CSSProperties}
                            onClick={() => {
                              setPackFactorDraft((current) => ({
                                ...current,
                                [idx]: String(line.pack_factor ?? ''),
                              }));
                              setPackBaseDraft((current) => ({
                                ...current,
                                [idx]: line.pack_base_uom ?? line.uom,
                              }));
                              patchLine(idx, {
                                pack_needs_factor: true,
                                pack_factor_applied: false,
                                conversion_confirmed: false,
                              });
                            }}
                          >
                            Modifica peso/contenuto per {line.source_uom || 'pezzo'}
                          </IonButton>
                        </div>
                      )}
                      {line.pack_needs_factor && !line.pack_factor_applied && (() => {
                        // fix #5: preferisci pack_size/pack_uom STRUTTURATI dall'AI
                        // (se in unità base) al parsing della descrizione testuale.
                        const structured = (parsed?.pack_size != null && parsed.pack_size > 0 && isBaseUom(parsed?.pack_uom ?? null))
                          ? { factor: parsed.pack_size as number, baseUom: (parsed.pack_uom as string).toLowerCase() }
                          : null;
                        const sug = suggestPackFactor(line.raw_description ?? line.ai_description ?? line.food_create.name, line.uom) ?? structured;
                        // fix #11(a): per un food esistente la base suggerita è la sua default_uom.
                        const foodBase = isBaseUom(line.uom) ? line.uom : null;
                        // fix #1: il valore di `sug` è nella SUA unità (sug.baseUom);
                        // se la base è vincolata a quella del food e differisce,
                        // riconcilia: converti se stessa famiglia, altrimenti NON
                        // proporre un numero (sarebbe in unità sbagliata).
                        let sugFactor = sug ? sug.factor : null;
                        let sugBase = sug ? sug.baseUom : null;
                        if (sug && foodBase && sug.baseUom !== foodBase) {
                          const c = convertQty(sug.factor, sug.baseUom, foodBase);
                          sugFactor = c != null ? Number(c.toFixed(4)) : null;
                          sugBase = foodBase;
                        }
                        // fix (R3 #1): NON pre-compilare il fattore col suggerimento
                        // (il parsing della descrizione è euristico e può sbagliare di
                        // un ordine di grandezza su tagli tipo "9x9"): l'utente lo digita,
                        // o tocca il suggerimento per usarlo consapevolmente.
                        const fVal = packFactorDraft[idx] ?? '';
                        const bVal = packBaseDraft[idx] ?? foodBase ?? (isBaseUom(line.uom) ? line.uom : sugBase) ?? 'kg';
                        return (
                          <div style={{ margin: '6px 14px 0', padding: '10px', borderRadius: 8, background: palette.warnSoft, border: `1px solid ${palette.warn}55`, fontSize: 11.5 }}>
                            <div style={{ color: '#92400e', fontWeight: 600, marginBottom: 6 }}>
                              📦 Sul documento: <b>{line.source_qty} {line.source_uom}</b>
                              {' '}— qual è il peso/contenuto di 1 {line.source_uom || 'pezzo/collo'}?
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              <div style={{ flex: '0 0 96px' }}>
                                <IonInput
                                  label={`Peso/contenuto per ${line.source_uom || 'pezzo'}`}
                                  labelPlacement="stacked"
                                  type="number"
                                  inputmode="decimal"
                                  value={fVal}
                                  onIonInput={(ev) => setPackFactorDraft((s) => ({ ...s, [idx]: String(ev.detail.value ?? '') }))}
                                  style={{ border: '1px solid #fcd34d', borderRadius: 8, '--padding-start': '8px' } as React.CSSProperties}
                                />
                              </div>
                              <div style={{ flex: '1 1 120px', minWidth: 120 }}>
                                <UomSelect label="Unità base" value={bVal} disabled={!!line.food_id && !isCorrectingImport} options={BASE_UOM_OPTIONS} allowCustom={false} onChange={(u) => setPackBaseDraft((s) => ({ ...s, [idx]: u }))} />
                              </div>
                              <IonButton
                                size="small"
                                onClick={() => applyManualPackFactor(
                                  idx,
                                  line,
                                  fVal,
                                  bVal,
                                  sourceQtyDraft[idx] ?? String(line.source_qty),
                                )}
                                style={{ textTransform: 'none' } as React.CSSProperties}
                              >
                                Converti
                              </IonButton>
                            </div>
                            {sugFactor != null && packFactorDraft[idx] === undefined && (
                              <div
                                role="button"
                                onClick={() => {
                                  setPackFactorDraft((s) => ({ ...s, [idx]: String(sugFactor) }));
                                  setPackBaseDraft((s) => ({ ...s, [idx]: sugBase ?? bVal }));
                                }}
                                style={{ color: '#1d4ed8', marginTop: 4, cursor: 'pointer', textDecoration: 'underline' }}
                              >
                                💡 Dalla descrizione sembra 1 {line.extracted_uom} = {sugFactor} {sugBase} — tocca per usare (poi verifica).
                              </div>
                            )}
                            <div style={{ color: '#b45309', marginTop: 2 }}>Salvato per i prossimi carichi di questo articolo.</div>
                          </div>
                        );
                      })()}
                      {/* T-290: UdM = menu a tendina (canoniche + personalizzate
                          + "Altro…"), non più campo libero → niente unità
                          incoerenti in magazzino che rompono lo scarico ricette.
                          T-313: la UoM ha una riga PROPRIA — prima condivideva
                          l'IonItem con la Quantità e veniva schiacciata fino a non
                          vedersi (segnalazione LeBoccerie "le UdM non si vedono"). */}
                      <div className={line.food_id ? 'te-field-with-info' : undefined}>
                        <IonItem>
                          <UomSelect
                            label={line.food_id && !isCorrectingImport ? 'UdM magazzino (da anagrafica)' : 'UdM magazzino'}
                            value={line.uom}
                            disabled={!!line.food_id && !isCorrectingImport}
                            onChange={(u) => {
                              const change = changeStockUomDraft({
                                currentQty: line.qty,
                                currentUom: line.uom,
                                nextUom: u,
                                sourceQty: line.source_qty,
                                sourceUom: line.source_uom,
                                previousConversionSource: line.conversion_source,
                              });
                              const nonMetricNeedsFactor = (
                                !change.conversionConfirmed
                                && (
                                  isPackUnit(line.uom)
                                  || isPackUnit(u)
                                )
                              );
                              patchLine(idx, {
                                ...clearPackOnEdit(line),
                                qty: change.qty,
                                uom: change.uom,
                                conversion_factor: change.conversionFactor,
                                conversion_source: change.conversionSource,
                                conversion_confirmed:
                                  change.conversionConfirmed,
                                pack_needs_factor: nonMetricNeedsFactor,
                                pack_factor: null,
                                pack_base_uom: null,
                                pack_factor_applied: false,
                                // Per un nuovo alimento la UdM scelta è anche la
                                // coordinata canonica del master che verrà creato.
                                // Tenerle disallineate farebbe fallire il command
                                // atomico (o, nel legacy, salverebbe costo e stock
                                // riferiti a unità diverse).
                                food_create: line.food_id
                                  ? line.food_create
                                  : { ...line.food_create, default_uom: u },
                              });
                            }}
                          />
                        </IonItem>
                        {line.food_id && !isCorrectingImport && (
                          <div className="te-field-with-info__badge">
                            <InfoBadge
                              inline
                              open={openAccessoryInfo === `uom-${idx}`}
                              label={openAccessoryInfo === `uom-${idx}`
                                ? 'Nascondi spiegazione dell’unità di magazzino'
                                : 'Mostra spiegazione dell’unità di magazzino'}
                              onToggle={() => setOpenAccessoryInfo((current) => (
                                current === `uom-${idx}` ? null : `uom-${idx}`
                              ))}
                            />
                          </div>
                        )}
                      </div>
                      {line.food_id && !isCorrectingImport && openAccessoryInfo === `uom-${idx}` && (
                        <IonNote className="te-field-help">
                          L’unità di magazzino è quella dell’alimento esistente e
                          non cambia con il formato del fornitore. Se il DDT usa
                          pezzi, colli o confezioni diversi, modifica sotto il
                          peso/contenuto per unità: quantità e costo verranno
                          riproporzionati senza alterare l’anagrafica.
                        </IonNote>
                      )}
                      {!line.conversion_confirmed
                        && line.conversion_source === 'user_confirmed'
                        && (
                          <IonNote
                            color="warning"
                            style={{
                              display: 'block',
                              padding: '4px 16px 8px',
                              fontSize: 11,
                            }}
                          >
                            Inserisci la quantità reale in {line.uom || 'questa UdM'}
                            {' '}oppure applica un fattore collo. Il carico resta
                            bloccato finché la conversione non è confermata.
                          </IonNote>
                        )}
                      {(
                        normalizeUom(line.source_uom) !== normalizeUom(line.uom)
                        || Math.abs(line.source_qty - (Number(line.qty) || 0)) > 1e-12
                      ) && (
                        <IonNote
                          color="medium"
                          style={{
                            display: 'block',
                            padding: '0 16px 8px',
                            fontSize: 11,
                          }}
                        >
                          Documento: <b>{line.source_qty} {line.source_uom}</b>
                          {' '}· Magazzino: <b>{line.qty || '—'} {line.uom}</b>
                        </IonNote>
                      )}
                      {/* T-284 (Food Cost): prezzo/totale riga ESTRATTI dall'AI,
                          visibili e correggibili. Vanno nel costo del lotto al
                          carico → poi nel Food Cost. Vuoto = costo non noto. */}
                      <IonItem>
                        <IonInput
                          label={`Prezzo €/${
                            line.source_uom || "UdM"
                          }`}
                          labelPlacement="stacked"
                          type="number"
                          inputmode="decimal"
                          placeholder="—"
                          value={moneyInputValue(
                            line.source_unit_price ?? line.unit_price,
                          )}
                          step="0.01"
                          onIonInput={(ev) => {
                            const v = ev.detail.value;
                            const editedUnitPrice = v == null || v === ''
                              ? null
                              : Number(String(v).replace(',', '.'));
                            const economics = sourceEconomicsFromUnitPrice(
                              line.source_qty,
                              editedUnitPrice,
                            );
                            patchLine(idx, {
                              source_unit_price: economics.sourceUnitPrice,
                              source_line_total: economics.sourceLineTotal,
                              unit_price: economics.sourceUnitPrice,
                              line_total: economics.sourceLineTotal,
                            });
                          }}
                        />
                        <IonInput
                          label="Totale riga €"
                          labelPlacement="stacked"
                          type="number"
                          inputmode="decimal"
                          placeholder="—"
                          value={moneyInputValue(
                            line.source_line_total ?? line.line_total,
                          )}
                          step="0.01"
                          onIonInput={(ev) => {
                            const v = ev.detail.value;
                            const editedLineTotal = v == null || v === ''
                              ? null
                              : Number(String(v).replace(',', '.'));
                            const economics = sourceEconomicsFromLineTotal(
                              line.source_qty,
                              editedLineTotal,
                            );
                            patchLine(idx, {
                              source_unit_price: economics.sourceUnitPrice,
                              source_line_total: economics.sourceLineTotal,
                              unit_price: economics.sourceUnitPrice,
                              line_total: economics.sourceLineTotal,
                            });
                          }}
                        />
                      </IonItem>
                      {(() => {
                        const sourceLineValue = line.source_line_total
                          ?? (
                            line.source_unit_price != null
                              ? line.source_unit_price * line.source_qty
                              : null
                          );
                        const stockUnitCost = deriveStockUnitCost({
                          sourceQty: line.source_qty,
                          sourceUnitPrice: line.source_unit_price,
                          sourceLineTotal: line.source_line_total,
                          stockQty: Number(line.qty) || null,
                        });
                        if (sourceLineValue == null) return null;
                        return (
                          <IonNote
                            style={{
                              display: 'block',
                              padding: '0 16px 6px',
                              fontSize: 11,
                            }}
                          >
                            Valore effettivo usato: <b>€ {formatMoney(sourceLineValue)}</b>
                            {stockUnitCost != null && (
                              <> · <b>€ {formatMoney(stockUnitCost)}/{line.uom}</b> in magazzino</>
                            )}
                          </IonNote>
                        );
                      })()}
                      </ImportReviewSection>
                      <ImportReviewSection
                        tone="lot"
                        icon={calendarOutline}
                        title="Lotto e scadenza"
                      >
                      {/* T-145: rimosso il flag "Non tracciato a lotto" (T-133).
                          Il "-" resta digitabile manualmente nel campo Lotto; il
                          lotto reale si carica poi da fattura. */}
                      <IonItem>
                        {/* T-314: IonTextarea autoGrow al posto di IonInput → il
                            campo Lotto si adatta alla lunghezza del testo (es.
                            "Vedi DDT n.12676 del 17/06/2026") invece di troncarlo
                            (segnalazione LeBoccerie). */}
                        <IonTextarea
                          label="Lotto"
                          labelPlacement="stacked"
                          autoGrow
                          rows={1}
                          value={line.batch_lot}
                          onIonInput={(ev) => patchLine(idx, { batch_lot: String(ev.detail.value ?? '') })}
                        />
                        <IonButton
                          slot="end"
                          fill="outline"
                          size="small"
                          disabled={!!linePhotoBusy[idx]}
                          onClick={() => { void beginLabelCapture(idx); }}
                          aria-label="Scatta foto lotto e scadenza"
                        >
                          {linePhotoBusy[idx] ? (
                            <IonSpinner name="dots" />
                          ) : (
                            <>
                              <IonIcon icon={camera} slot="start" />
                              Lotto + scad.
                            </>
                          )}
                        </IonButton>
                      </IonItem>
                      <IonItem>
                        <IonInput
                          label="Scadenza"
                          labelPlacement="stacked"
                          type="date"
                          value={line.expiration_date}
                          onIonInput={(ev) => patchLine(idx, { expiration_date: String(ev.detail.value ?? '') })}
                        />
                      </IonItem>
                      <IonItem>
                        <IonLabel>Notifica scadenza</IonLabel>
                        <IonToggle
                          slot="end"
                          checked={line.notify_expiration}
                          onIonChange={(ev) => patchLine(idx, { notify_expiration: ev.detail.checked })}
                        />
                      </IonItem>
                      </ImportReviewSection>
                    </>
                  )}
                  <IonButton
                    expand="block"
                    fill="outline"
                    color={line.skip_once ? 'medium' : 'danger'}
                    onClick={async () => {
                      if (line.skip_once) {
                        patchLine(idx, { skip: false, skip_once: false });
                        return;
                      }
                      const confirmed = await confirmDialog(
                        'La riga sarà esclusa solo da questo carico. Potrai ripristinarla prima di confermare il documento. La memoria del fornitore resterà invariata.',
                        { header: 'Rimuovere la riga dal carico?', okText: 'Rimuovi', cancelText: 'Annulla', danger: true },
                      );
                      if (confirmed) patchLine(idx, { skip: true, skip_once: true });
                    }}
                  >
                    {line.skip_once ? 'Ripristina in questo carico' : 'Rimuovi solo da questo carico'}
                  </IonButton>
                  {line.skip_once && <p>Riga esclusa solo da questo documento. La memoria del fornitore resta invariata.</p>}
                </div>
              );
            })}

            {/* T-514: verifiche e conferme vivono nel footer, accanto ad
                "Avanti". La parte alta resta dedicata al prodotto. */}
            {plan.lines.length > 0 && (() => {
              const total = plan.lines.length;
              const cur = Math.min(currentLineIdx, total - 1);
              const current = plan.lines[cur];
              const currentSource = sourceLineForPlan(dryRun.lines, current, cur);
              const currentReasons = current.skip
                ? []
                : lineOpenReasons(toReviewLine(current));
              const currentExtractionWarnings = current.skip
                ? []
                : extractionWarnings(currentSource);
              const currentReady = isLineReady(current, cur);
              const needsReview = (line: PlanLineDraft, index: number): boolean => !isLineReady(line, index);
              const after = plan.lines.findIndex(
                (line, index) => index > cur && needsReview(line, index),
              );
              const before = plan.lines.findIndex(
                (line, index) => index < cur && needsReview(line, index),
              );
              const nextReview = after >= 0 ? after : before;
              const nextLabel = !currentReady
                ? 'Completa la verifica'
                : nextReview >= 0
                  ? 'Avanti · prossima verifica'
                  : canCommit
                    ? 'Vai al riepilogo'
                    : 'Avanti';
              return (
                <div style={{
                  margin: '4px 16px 16px',
                  padding: 10,
                  background: palette.card,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 10,
                  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
                }}>
                  <div className={`te-review-footer-status${currentReady ? ' is-ready' : ' is-review'}`}>
                    <strong>{currentReady ? '✓ Articolo pronto' : 'Verifica prima di continuare'}</strong>
                    {identityNeedsReview(current, cur) && <span>Scegli dove caricarlo oppure conferma un articolo distinto nel riquadro rosso.</span>}
                    {!currentReady && currentReasons.length > 0 && (
                      <span>
                        {currentReasons.map((reason) => GATE_REASON_LABELS[reason] ?? reason).join(' · ')}
                      </span>
                    )}
                    {!currentReady
                      && !current.gates_ack
                      && currentExtractionWarnings.map((warning) => (
                        <span key={warning}>{warning}</span>
                      ))}
                    {!currentReady && current.pack_needs_factor && !current.pack_factor_applied && (
                      <span>Indica il contenuto del collo.</span>
                    )}
                  </div>
                  {!current.skip
                    && (
                      currentReasons.length > 0
                      || (
                        currentExtractionWarnings.length > 0
                        && !current.gates_ack
                      )
                    )
                    && (
                    <div className="te-review-footer-actions">
                      {currentReasons.includes('lotto_sospetto') && (
                        <IonButton
                          size="small"
                          fill="outline"
                          onClick={() => void rememberSupplierLotPreference(true)}
                        >
                          Questo fornitore invia documenti senza lotto
                        </IonButton>
                      )}
                      {(currentReasons.some((reason) => reason !== 'lotto_sospetto')
                        || (
                          currentExtractionWarnings.length > 0
                          && !current.gates_ack
                        )) && (
                        <IonButton
                          size="small"
                          fill="outline"
                          onClick={() => patchLine(cur, { gates_ack: true })}
                        >
                          Conferma i dati
                        </IonButton>
                      )}
                    </div>
                    )}
                  <div className="te-review-footer-nav">
                  <button
                    type="button"
                    disabled={cur === 0}
                    onClick={() => setCurrentLineIdx((index) => Math.max(0, index - 1))}
                  >
                    ← Indietro
                  </button>
                  <span>{cur + 1}/{total}</span>
                  <button
                    type="button"
                    className="is-primary"
                    disabled={!currentReady}
                    onClick={() => {
                      if (nextReview >= 0) {
                        setCurrentLineIdx(nextReview);
                      } else if (canCommit) {
                        setReviewStep('summary');
                      } else {
                        setCurrentLineIdx((index) => Math.min(total - 1, index + 1));
                      }
                    }}
                  >
                    {nextLabel} →
                  </button>
                  </div>
                </div>
              );
            })()}

            {/* T-144: la scheda di controllo conformità HACCP (T-061) NON è più
                inline qui — è un POPUP che appare dopo "Conferma e carica
                magazzino" (IonAlert in fondo al componente), per non occupare
                spazio nella revisione. */}
            {false && (
            <div
              style={{
                marginTop: 16,
                padding: 14,
                background: conformity === 'yes' ? '#ecfdf5' : conformity === 'no' ? '#fef3c7' : '#f8fafc',
                border: `1px solid ${conformity === 'yes' ? '#10b981' : conformity === 'no' ? '#f59e0b' : '#e5e7eb'}`,
                borderRadius: 12,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: palette.text, marginBottom: 6 }}>
                Controllo conformità al ricevimento (HACCP)
              </div>
              <div style={{ fontSize: 13, color: palette.textMuted, lineHeight: 1.4, marginBottom: 8 }}>
                Verifica che la merce sia conforme rispetto a:
              </div>
              <ul style={{ margin: '0 0 12px 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.55, color: palette.text }}>
                {CONFORMITY_DISCLAIMER_ITEMS.map((it) => (
                  <li key={it}>{it}</li>
                ))}
              </ul>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    setConformity('yes');
                    setConformityNotes('');
                  }}
                  disabled={busy}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: conformity === 'yes' ? '2px solid #059669' : '1px solid #cbd5e1',
                    background: conformity === 'yes' ? '#059669' : '#fff',
                    color: conformity === 'yes' ? '#fff' : '#0f172a',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  ✅ Sì, tutto conforme
                </button>
                <button
                  type="button"
                  onClick={() => setConformity('no')}
                  disabled={busy}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: conformity === 'no' ? '2px solid #d97706' : '1px solid #cbd5e1',
                    background: conformity === 'no' ? '#d97706' : '#fff',
                    color: conformity === 'no' ? '#fff' : '#0f172a',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  ⚠️ No, ci sono non conformità
                </button>
              </div>

              {conformity === 'no' && (
                <div style={{ marginTop: 12 }}>
                  <label
                    htmlFor="conformity-notes"
                    style={{ display: 'block', fontSize: 13, fontWeight: 600, color: palette.text, marginBottom: 4 }}
                  >
                    Descrivi le non conformità riscontrate *
                  </label>
                  <textarea
                    id="conformity-notes"
                    value={conformityNotes}
                    onChange={(ev) => setConformityNotes(ev.target.value)}
                    disabled={busy}
                    rows={3}
                    placeholder="Es: 3 confezioni di formaggio fresco con confezione gonfia; temperatura cella trasporto rilevata a +9 °C invece di +4."
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: `1px solid ${conformityNotesValid ? '#10b981' : '#fca5a5'}`,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                    }}
                  />
                  {!conformityNotesValid && (
                    <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 4 }}>
                      Inserisci almeno 5 caratteri per descrivere la non conformità.
                    </div>
                  )}
                </div>
              )}

              {/* T-291: temperatura di ricevimento per articolo (gated sul flag
                  tenant receiving_temp_per_item). Solo righe non-skippate. La
                  temperatura si registra solo per articoli già in anagrafica
                  (food_id); i nuovi articoli verranno associati ai carichi
                  successivi. */}
              {receivingTempPerItem && plan && (() => {
                const p = plan!;
                if (!p.lines.some((l) => !l.skip)) return null;
                return (
                <div style={{ marginTop: 14, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: palette.text, marginBottom: 2 }}>
                    🌡️ Temperatura al ricevimento (per articolo)
                  </div>
                  <div style={{ fontSize: 11, color: palette.textMuted, marginBottom: 8, lineHeight: 1.4 }}>
                    Facoltativa. Rilevata alla consegna; finisce nel MOD 01. Ammessi valori negativi (surgelati).
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                    {p.lines.map((l) => {
                      if (l.skip) return null;
                      const label = l.food_create.name || l.raw_description || `Riga ${l.parsed_index + 1}`;
                      const isNew = !l.food_id;
                      return (
                        <div key={l.parsed_index} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 8, padding: '6px 8px', background: '#fff',
                          border: '1px solid #e5e7eb', borderRadius: 8,
                        }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{
                              fontSize: 13, fontWeight: 600, color: palette.text,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{label}</div>
                            {isNew && (
                              <div style={{ fontSize: 10, color: '#b45309' }}>nuovo articolo — temp non registrata ora</div>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.1"
                              disabled={busy || isNew}
                              value={itemTemps[l.parsed_index] ?? ''}
                              placeholder="—"
                              onChange={(ev) => {
                                const v = ev.target.value;
                                setItemTemps((prev) => ({ ...prev, [l.parsed_index]: v }));
                              }}
                              style={{
                                width: 64, padding: '6px 8px', borderRadius: 8,
                                border: '1px solid #cbd5e1', fontSize: 14, textAlign: 'right',
                                background: isNew ? '#f1f5f9' : '#fff', boxSizing: 'border-box',
                              }}
                            />
                            <span style={{ fontSize: 13, color: palette.textMuted }}>°C</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}
            </div>
            )}

              </div>
            </div>
            )}

            {reviewStep === 'summary' && (
              <div className="te-import-summary">
                {importReviewId && reviewOriginalRecovery.status === 'required' && reviewTenantId && (
                  <ReviewOriginalRecoveryPanel
                    tenantId={reviewTenantId}
                    reviewId={importReviewId}
                    expectedPages={reviewOriginalRecovery.expectedPages}
                    onRecovered={() => {
                      setReviewOriginalRecovery({ status: 'not_needed', expectedPages: reviewOriginalRecovery.expectedPages });
                      setToast('Originale completo salvato. Ora puoi confermare il carico.');
                    }}
                    onDeferred={() => {
                      setReviewOriginalRecovery({ status: 'deferred', expectedPages: reviewOriginalRecovery.expectedPages });
                      setToast('Rinvio registrato. Puoi caricare il magazzino e aggiungere l’originale in seguito.');
                    }}
                  />
                )}
                <div className="te-section-card">
                  <div className="te-section-card__header">
                    <div>
                      <h2 className="te-section-title">Riepilogo del carico</h2>
                      <p className="te-section-help">
                        Controlla l’esito prima di modificare il magazzino. Tocca un articolo per rivederlo.
                      </p>
                    </div>
                    <span className={`te-status ${canFinalizeImport ? 'te-status--success' : 'te-status--warning'}`}>
                      {!canFinalizeImport
                        ? 'Da rivedere'
                        : reviewOriginalRecovery.status === 'not_needed'
                        ? 'Pronto'
                        : reviewOriginalRecovery.status === 'deferred'
                          ? 'Originale da integrare'
                          : 'Originale richiesto'}
                    </span>
                  </div>
                  <div className="te-section-card__body">
                    <div className="te-import-summary__stats">
                      <div className="te-import-summary__stat">
                        <strong>{plan.lines.filter((line) => !line.skip).length}</strong>
                        <span>articoli da caricare</span>
                      </div>
                      <div className="te-import-summary__stat">
                        <strong>{plan.lines.filter((line) => !line.skip && !!line.food_id).length}</strong>
                        <span>anagrafiche esistenti</span>
                      </div>
                      <div className="te-import-summary__stat">
                        <strong>{plan.lines.filter((line) => line.skip).length}</strong>
                        <span>righe ignorate</span>
                      </div>
                    </div>
                    <IonList inset>
                      {plan.lines.map((line, lineIndex) => {
                        if (line.skip) return null;
                        const identityReview = identityNeedsReview(line, lineIndex);
                        const lineReady = isLineReady(line, lineIndex);
                        const linkedFood = line.food_id
                          ? tenantFoods.find((food) => food.id === line.food_id)
                          : null;
                        return (
                          <IonItem
                            key={`${line.parsed_index}-${lineIndex}`}
                            style={!lineReady ? { border: `2px solid ${palette.danger}`, borderRadius: 10, marginBottom: 8 } : undefined}
                            button
                            detail
                            onClick={() => {
                              setCurrentLineIdx(lineIndex);
                              setReviewStep('lines');
                            }}
                            aria-label={`Rivedi ${linkedFood?.name ?? line.food_create.name ?? `riga ${lineIndex + 1}`}`}
                          >
                            <IonLabel className="ion-text-wrap">
                              <h3>{linkedFood?.name ?? line.food_create.name ?? `Riga ${lineIndex + 1}`}</h3>
                              {identityReview && <p style={{ color: palette.danger }}>Già presente o abbinamento ambiguo: scegli dove caricarlo.</p>}
                              <p>
                                Documento: {line.source_qty} {line.source_uom}
                                {' · '}
                                Magazzino: {line.qty} {line.uom}
                              </p>
                            </IonLabel>
                            <span className={`te-status ${lineReady ? 'te-status--success' : 'te-status--warning'}`} slot="end">{lineReady ? 'Pronto' : 'Da rivedere'}</span>
                          </IonItem>
                        );
                      })}
                  </IonList>
                  </div>
                </div>
                <div className="te-action-bar">
                  <IonButton fill="outline" disabled={busy} onClick={() => setReviewStep('lines')}>
                    Torna agli articoli
                  </IonButton>
                  <IonButton color="success" disabled={busy || !canFinalizeImport} onClick={() => setConfirmOpen(true)}>
                    {busy
                      ? <IonSpinner name="dots" />
                      : (!canCreateFoods && plan.lines.some((line) => !line.skip && !line.food_id))
                        ? 'Invia in revisione'
                        : 'Conferma e carica magazzino'}
                  </IonButton>
                </div>
              </div>
            )}
            </div>
          </>
        )}

        {/* T-068: Modal "Scegli alimento dall'anagrafica del locale".
            Search bar in alto + lista filtrata di `tenantFoods`. Tap su
            un food → patch della riga col food_id scelto, ricalcolo
            dell'uom dal default del food, e chiusura modal. La memoria
            alias verrà popolata dopo dal commitPlan. */}
        {(() => {
          const idx = foodPickerForLine;
          const open = idx != null;
          const search = foodPickerSearch.trim().toLowerCase();
          // T-132: gli alimenti usati in almeno una ricetta vanno IN CIMA
          // (ordinati per numero di ricette desc), gli altri sotto in ordine
          // alfabetico. Così chi sceglie a mano trova subito gli ingredienti
          // "veri" della cucina e non i mille alimenti orfani importati.
          const collator = new Intl.Collator('it', { sensitivity: 'base' });
          const filtered = tenantFoods
            .filter((f) => {
              if (!search) return true;
              return f.name.toLowerCase().includes(search)
                || (f.category ?? '').toLowerCase().includes(search);
            })
            .slice()
            .sort((a, b) => {
              const ca = recipesPerFood.get(a.id) ?? 0;
              const cb = recipesPerFood.get(b.id) ?? 0;
              // 1) chi è in ricette prima di chi non lo è
              if ((ca > 0) !== (cb > 0)) return ca > 0 ? -1 : 1;
              // 2) tra quelli in ricette, più ricette prima
              if (ca !== cb) return cb - ca;
              // 3) a parità, alfabetico
              return collator.compare(a.name, b.name);
            })
            .slice(0, 200);
          return (
            <IonModal
              isOpen={open}
              onDidDismiss={() => { setFoodPickerForLine(null); setFoodPickerSearch(''); }}
            >
              <IonHeader>
                <IonToolbar>
                  <IonTitle>Scegli alimento</IonTitle>
                  <IonButtons slot="end">
                    <IonButton onClick={() => { setFoodPickerForLine(null); setFoodPickerSearch(''); }}>Chiudi</IonButton>
                  </IonButtons>
                </IonToolbar>
                <IonToolbar>
                  <IonInput
                    placeholder="Cerca per nome o categoria…"
                    value={foodPickerSearch}
                    onIonInput={(ev) => setFoodPickerSearch(String(ev.detail.value ?? ''))}
                    autofocus
                  />
                </IonToolbar>
              </IonHeader>
              <IonContent>
                {idx != null && (
                  <div style={{ padding: '8px 16px', fontSize: 12, color: palette.textMuted, lineHeight: 1.4 }}>
                    Riga "{dryRun?.lines[idx]?.parsed.description ?? '—'}".
                    Scegli un alimento esistente per evitare di creare un
                    duplicato. La scelta verrà memorizzata per questo fornitore.
                  </div>
                )}
                <IonList>
                  {filtered.length === 0 ? (
                    <IonItem>
                      <IonLabel color="medium">
                        {tenantFoods.length === 0
                          ? 'Anagrafica vuota — usa "➕ Nuovo" o fai il First Setup dal Cassetto menu.'
                          : 'Nessun risultato per la ricerca.'}
                      </IonLabel>
                    </IonItem>
                  ) : (
                    filtered.map((f) => (
                      <IonItem
                        key={f.id}
                        button
                        onClick={() => {
                          if (idx == null) return;
                          selectExistingFoodAndRestorePackFactor(idx, {
                            ...f,
                            default_uom: f.default_uom || 'pz',
                          });
                          // T-131: scelta manuale → default "aggiorna lotto"
                          // (la regola data vale solo per il match automatico).
                          patchLine(idx, { keep_current_lot: false });
                          setManuallyMatchedLines((prev) => {
                            const next = new Set(prev);
                            next.add(idx);
                            return next;
                          });
                          setFoodPickerForLine(null);
                          setFoodPickerSearch('');
                          setToast(`Riga ${idx + 1} associata a "${f.name}"`);
                        }}
                      >
                        <IonLabel className="ion-text-wrap">
                          <h3>
                            {f.name}
                            {/* T-132: badge "in N ricette" per gli alimenti
                                effettivamente usati in cucina (ordinati in cima). */}
                            {(() => {
                              const n = recipesPerFood.get(f.id) ?? 0;
                              if (n <= 0) return null;
                              return (
                                <span
                                  style={{
                                    marginLeft: 8,
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    background: '#eff6ff',
                                    color: '#1d4ed8',
                                    border: '1px solid #bfdbfe',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  in {n} {n === 1 ? 'ricetta' : 'ricette'}
                                </span>
                              );
                            })()}
                          </h3>
                          <p style={{ fontSize: 12 }}>
                            UoM {f.default_uom} · {f.storage_type ?? '—'}
                            {f.category ? ` · ${f.category}` : ''}
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))
                  )}
                </IonList>
              </IonContent>
            </IonModal>
          );
        })()}

        {/* Modal anteprima fullscreen documento (PDF o immagine) */}
        <IonModal isOpen={previewFullscreen} onDidDismiss={() => setPreviewFullscreen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{originalFilename ?? 'Documento'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setPreviewFullscreen(false)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {pdfObjectUrl ? (
              <iframe
                src={pdfObjectUrl}
                title="PDF fullscreen"
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            ) : imageB64 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', background: '#000' }}>
                <img
                  src={`data:${imageMime};base64,${imageB64}`}
                  alt="Anteprima fullscreen"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              </div>
            ) : null}
          </IonContent>
        </IonModal>

        {/* T-407: cattura multi-foto etichetta — popup PULITO (sostituisce il
            window.confirm nativo OK/Annulla). Miniature + "Scatta un'altra foto"
            / "Ok, analizza", stile fotocamera WhatsApp. */}
        <IonModal
          isOpen={!!labelCapture}
          onDidDismiss={() => setLabelCapture(null)}
          initialBreakpoint={0.62}
          breakpoints={[0, 0.62, 0.95]}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Foto etichetta</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setLabelCapture(null)} aria-label="Chiudi"><IonIcon icon={close} /></IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
              Se lotto e scadenza sono su lati diversi, scatta più foto: le allego insieme (max 3).
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '12px 0' }}>
              {labelCapture?.photos.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img
                    src={`data:image/jpeg;base64,${p}`}
                    alt={`foto ${i + 1}`}
                    style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                  <IonButton
                    fill="clear"
                    size="small"
                    color="medium"
                    style={{ position: 'absolute', top: -12, right: -12 }}
                    onClick={() => removeLabelCapturePhoto(i)}
                    aria-label="Rimuovi foto"
                  >
                    <IonIcon icon={close} />
                  </IonButton>
                </div>
              ))}
            </div>
            {(labelCapture?.photos.length ?? 0) < 3 && (
              <IonButton expand="block" fill="outline" onClick={() => { void addLabelCapturePhoto(); }}>
                <IonIcon icon={camera} slot="start" />Scatta un'altra foto
              </IonButton>
            )}
            <IonButton
              expand="block"
              onClick={() => { void confirmLabelCapture(); }}
              style={{ marginTop: 10 }}
              disabled={(labelCapture?.photos.length ?? 0) === 0}
            >
              <IonIcon icon={checkmarkCircle} slot="start" />
              Ok, analizza{labelCapture && labelCapture.photos.length > 1 ? ` (${labelCapture.photos.length} foto)` : ''}
            </IonButton>
          </IonContent>
        </IonModal>

        {/* T-478: picker fornitore esistente (anagrafica) per la revisione import. */}
        <IonModal isOpen={supplierPickerOpen} onDidDismiss={() => setSupplierPickerOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Scegli fornitore</IonTitle>
              <IonButtons slot="end"><IonButton onClick={() => setSupplierPickerOpen(false)}>Chiudi</IonButton></IonButtons>
            </IonToolbar>
            <IonToolbar>
              <IonInput placeholder="Cerca fornitore per nome o P.IVA…" value={supplierSearch} onIonInput={(ev) => setSupplierSearch(String(ev.detail.value ?? ''))} autofocus />
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonList>
              {(() => {
                const q = supplierSearch.trim().toLowerCase();
                const list = cachedSuppliers
                  .filter((s) => !s.archived_at && (q === '' || (s.name ?? '').toLowerCase().includes(q) || (s.piva ?? '').includes(q)))
                  .slice(0, 60);
                if (list.length === 0) return (<IonItem><IonLabel color="medium">Nessun fornitore in anagrafica per questa ricerca.</IonLabel></IonItem>);
                return list.map((s) => (
                  <IonItem key={s.id} button onClick={() => void changeImportSupplier(s.id)}>
                    <IonLabel className="ion-text-wrap">
                      <h2>{s.name}</h2>
                      {s.piva && <p style={{ fontSize: 12, color: '#666' }}>P.IVA {s.piva}</p>}
                    </IonLabel>
                  </IonItem>
                ));
              })()}
            </IonList>
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2500}
          onDidDismiss={() => setToast(null)}
        />

        {/* T-144 (T-061): controllo conformità HACCP come POPUP finale, dopo
            "Conferma e carica magazzino". Niente più card inline a schermo. */}
        <IonAlert
          isOpen={confirmOpen}
          onDidDismiss={() => setConfirmOpen(false)}
          header="Controllo conformità al ricevimento"
          message="Confermi che la merce è conforme (integrità imballi, temperatura, scadenze, etichettatura)? Se ci sono non conformità, descrivile qui sotto."
          inputs={[{
            name: 'notes',
            type: 'textarea',
            placeholder: 'Note non conformità (obbligatorie se NON conforme)',
          }]}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Non conforme',
              handler: (data) => {
                const n = String((data as { notes?: string } | undefined)?.notes ?? '').trim();
                if (n.length < 5) {
                  setToast('Per "non conforme" inserisci almeno 5 caratteri di motivazione');
                  return false;
                }
                setConformity('no');
                setConformityNotes(n);
                void commitPlan('no', n);
                return true;
              },
            },
            {
              text: 'Conforme',
              handler: () => {
                setConformity('yes');
                setConformityNotes('');
                void commitPlan('yes', '');
              },
            },
          ]}
        />

        {/* T-144: "documento già caricato" come POPUP (non più banner rosso fisso). */}
        <IonAlert
          isOpen={dupAlertOpen}
          onDidDismiss={() => setDupAlertOpen(false)}
          header="Documento già caricato"
          message={dryRun?.existing_document
            ? `Sul locale attivo esiste già il n. ${dryRun.existing_document.number} del ${new Date(dryRun.existing_document.date).toLocaleDateString('it-IT')} dello stesso fornitore. Se devi correggere quello vecchio usa "Sostituisci".`
            : ''}
          buttons={[
            {
              text: 'Apri esistente',
              handler: () => {
                if (dryRun?.existing_document) {
                  history.push(`/documents/${dryRun.existing_document.source}/${dryRun.existing_document.id}`);
                }
              },
            },
            {
              text: 'Annulla import',
              role: 'cancel',
              handler: () => { resetWizard(); setImageB64(null); setOriginalFilename(null); },
            },
            {
              text: 'Sostituisci',
              handler: () => { setReplaceMode(true); },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
}
